/**
 * Points Center — main controller.
 * Loaded after points-center-support-form.js (window.initPointsCenterSupportForm).
 */
(function () {
  // ── Constants & state ──────────────────────────────────────────────
  const root = document.querySelector("[data-points-center]");
  if (!root) return;

  /** Set to null to use logged-in customer id again. */
  const POINTS_CENTER_TEST_SHOPIFY_ID = null;

  function initPointsCenterNavLayout() {
    var nav = root.querySelector(".points-center-nav");
    if (!nav) return;

    var BOTTOM_GAP = 16;
    var mq = window.matchMedia("(min-width: 769px)");
    var raf = 0;

    function measureTopOffset() {
      var headerGroup = document.getElementById("header-group");
      var bottom = 0;

      if (headerGroup) {
        Array.prototype.forEach.call(headerGroup.children, function (child) {
          if (!child || typeof child.getBoundingClientRect !== "function") return;
          var rect = child.getBoundingClientRect();
          if (rect.bottom <= 0) return;
          bottom = Math.max(bottom, rect.bottom);
        });
      }

      if (bottom <= 0) {
        var header =
          document.querySelector(".header-section") ||
          document.querySelector("#header-component");
        if (header) {
          bottom = Math.max(0, header.getBoundingClientRect().bottom);
        }
      }

      return Math.max(0, Math.round(bottom));
    }

    function apply() {
      if (!mq.matches) {
        root.style.removeProperty("--points-center-nav-top");
        root.style.removeProperty("--points-center-nav-height");
        return;
      }

      var top = measureTopOffset();
      var height = Math.max(240, window.innerHeight - top - BOTTOM_GAP);
      root.style.setProperty("--points-center-nav-top", top + "px");
      root.style.setProperty("--points-center-nav-height", height + "px");
    }

    function onChange() {
      if (raf) return;
      raf = window.requestAnimationFrame(function () {
        raf = 0;
        apply();
      });
    }

    apply();
    window.addEventListener("scroll", onChange, { passive: true });
    window.addEventListener("resize", onChange);
    if (mq.addEventListener) {
      mq.addEventListener("change", apply);
    } else if (mq.addListener) {
      mq.addListener(apply);
    }

    var headerGroup = document.getElementById("header-group");
    if (headerGroup && window.ResizeObserver) {
      new ResizeObserver(onChange).observe(headerGroup);
    }
  }

  initPointsCenterNavLayout();

  function t(path, vars) {
    var parts = String(path || "").split(".");
    var cur =
      (window.Theme &&
        Theme.translations &&
        Theme.translations.points_center) ||
      {};
    for (var i = 0; i < parts.length; i++) {
      if (cur == null) break;
      cur = cur[parts[i]];
    }
    if (typeof cur !== "string") return typeof path === "string" ? path : "";
    cur = decodeHtmlEntities(cur);
    if (!vars) return cur;
    return cur.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, key) {
      return vars[key] != null ? String(vars[key]) : "";
    });
  }

  function getOrdersStatusLabel(key) {
    var map = {
      all: "status.all",
      processing: "status.processing",
      shipped: "status.shipped",
      delivered: "status.delivered",
      cancelled: "status.cancelled",
      refunded: "status.refunded",
    };
    return map[key] ? t(map[key]) : String(key || t("common.dash"));
  }

  const POINTS_CENTER_VIEW_KEY = "miniforum:points-center:view";
  const DEFAULT_POINTS_CENTER_TAB = "account-overview";
  let userAvailablePoints = null;
  let loyaltySummaryData = null;
  let summaryFetchPromise = null;
  let cachedEarnRules = null;
  let earnPointsLoaded = false;
  let overviewEarnSwiper = null;
  let overviewRecommendedSwiper = null;
  let benefitsHeroCountUpPlayed = false;
  let minisCreditsCountUpPlayed = false;
  let availablePointsCountUpPlayed = false;
  const OVERVIEW_MOBILE_MQ =
    typeof window.matchMedia === "function"
      ? window.matchMedia("(max-width: 768px)")
      : null;

  function isOverviewMobile() {
    return !!(OVERVIEW_MOBILE_MQ && OVERVIEW_MOBILE_MQ.matches);
  }

  function isPointsCenterMobileSheet() {
    return isOverviewMobile();
  }

  function prefersReducedMotion() {
    return !!(
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  function closePointsCenterBottomSheet(modal, options) {
    options = options || {};
    if (!modal || modal.hidden) return;
    if (modal.classList.contains("is-closing")) return;

    const dialog =
      (options.dialogSelector && modal.querySelector(options.dialogSelector)) ||
      modal.querySelector("[class*='__dialog']");

    function finishClose() {
      modal.hidden = true;
      modal.classList.remove("is-closing");
      if (typeof options.onClosed === "function") {
        options.onClosed();
      }
    }

    if (!isPointsCenterMobileSheet() || prefersReducedMotion()) {
      finishClose();
      return;
    }

    modal.classList.add("is-closing");
    let fallbackTimer = null;

    function onAnimationEnd(event) {
      if (dialog && event && event.target !== dialog) return;
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
      if (dialog) dialog.removeEventListener("animationend", onAnimationEnd);
      finishClose();
    }

    if (dialog) {
      dialog.addEventListener("animationend", onAnimationEnd);
      fallbackTimer = window.setTimeout(onAnimationEnd, 450);
      return;
    }

    finishClose();
  }

  let pointStorePagesCache = {};
  let pointStoreAllItems = null;
  let pointStoreLoadPromise = null;
  let pointStoreFilter = "lt3000";
  // Match .benefits-redeem-store__grid layout:
  // >1280: 4 cols | ≤1280: 3 cols | ≤1024: 2 cols | ≤768: 2×2 grid (batch 4)
  function getBenefitsPointStorePageSize() {
    if (window.matchMedia("(max-width: 768px)").matches) return 4;
    if (window.matchMedia("(max-width: 1024px)").matches) return 2;
    if (window.matchMedia("(max-width: 1280px)").matches) return 3;
    return 4;
  }
  let benefitsPointStoreVisibleCount = getBenefitsPointStorePageSize();
  let benefitsPointStoreFilteredItems = [];
  let myCouponFullListCache = null;
  let myGiftCardFullListCache = null;
  const BENEFITS_FETCH_CHUNK = 50;
  let myCouponCurrentPage = 1;
  let myGiftCardCurrentPage = 1;
  let skipNextPointsUpdatedFetch = false;
  let loyaltyTiersCache = null;
  let tiersFetchPromise = null;
  let bonusRulesCache = null;
  let bonusRulesFetchPromise = null;
  let referReferrerPointsCache = null;
  let referReferrerPointsPromise = null;
  const referRecordsCache = {};
  const referRecordsPromises = {};
  let historyCurrentPage = 1;
  let historyRequestId = 0;
  const HISTORY_PAGE_SIZE = 10;

  const REFERRAL_REFERRER_POINTS_PATH = "/front/referral/referrer/points";
  const REFERRAL_RECORDS_PATH = "/front/referral/records";
  const REFERRAL_SHARE_LINK_PATH = "/front/referral/share-link";
  let referShareLinkLoaded = false;
  let referShareLinkPromise = null;
  const REFER_POINT_SOURCE = {
    signup: "REFER_REGISTER",
    purchase: "REFER_FIRST_ORDER",
  };
  const BENEFIT_PAGE_SIZE = 12;
  const numberFormatter = new Intl.NumberFormat("en-US");
  const COUNT_UP_DURATION_MS = 900;
  const countUpFrameIds = new WeakMap();

  function parseCountUpNumber(text) {
    if (text == null || text === "") return 0;
    const cleaned = String(text).replace(/,/g, "").replace(/[^\d.-]/g, "");
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : 0;
  }

  function cancelCountUpAnimation(el) {
    if (!el) return;
    const id = countUpFrameIds.get(el);
    if (id != null) {
      cancelAnimationFrame(id);
      countUpFrameIds.delete(el);
    }
  }

  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function animateCountUp(el, endValue, options) {
    options = options || {};
    if (!el) return;
    cancelCountUpAnimation(el);

    let end = Number(endValue);
    if (!Number.isFinite(end)) end = 0;

    const format =
      options.format ||
      function (value) {
        return numberFormatter.format(Math.round(value));
      };

    if (prefersReducedMotion()) {
      el.textContent = format(end);
      el.dataset.countupValue = String(end);
      return;
    }

    let start =
      options.start != null
        ? Number(options.start)
        : el.dataset.countupValue !== undefined
          ? Number(el.dataset.countupValue)
          : parseCountUpNumber(el.textContent);
    if (!Number.isFinite(start)) start = 0;

    if (start === end && !options.force) {
      el.textContent = format(end);
      el.dataset.countupValue = String(end);
      return;
    }

    const duration = options.duration || COUNT_UP_DURATION_MS;
    const t0 = performance.now();

    function frame(now) {
      const progress = Math.min(1, (now - t0) / duration);
      const value = start + (end - start) * easeOutCubic(progress);
      el.textContent = format(value);
      if (progress < 1) {
        countUpFrameIds.set(el, requestAnimationFrame(frame));
      } else {
        el.textContent = format(end);
        el.dataset.countupValue = String(end);
        countUpFrameIds.delete(el);
      }
    }
    countUpFrameIds.set(el, requestAnimationFrame(frame));
  }

  function animateCountUpCurrency(el, endValue, countUpOpts) {
    const opts = countUpOpts || {};
    animateCountUp(el, endValue, {
      start: opts.start,
      force: opts.force,
      duration: opts.duration,
      format: function (value) {
        return (
          "$" +
          Number(value).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        );
      },
    });
  }

  function setTierProgressLabel(el, current, max, isMaxTier) {
    if (!el) return;
    el.textContent =
      numberFormatter.format(Math.round(current)) +
      "/" +
      (isMaxTier ? "MAX" : numberFormatter.format(Math.round(max)));
  }

  const CATEGORY_SOCIAL = "Social Media & Marketing";
  const CATEGORY_LINK_SHARING = "Link Sharing";
  const PRODUCT_REVIEW_RULE_NAME = "product review";
  const DEFAULT_EARN_CTA_PATH = "/collections/all-products";
  const EARN_PRODUCT_REVIEW_URL = (
    root.getAttribute("data-earn-product-review-url") || DEFAULT_EARN_CTA_PATH
  ).trim();
  const EARN_PURCHASE_SHOP_URL = (
    root.getAttribute("data-earn-purchase-shop-url") || DEFAULT_EARN_CTA_PATH
  ).trim();

  // ── Earn rule helpers ────────────────────────────────────────────
  function getEarnRuleSearchText(item) {
    if (!item) return "";
    return [
      item.title,
      item.ruleDescription,
      item.buttonLabel,
      item.categoryName,
      item.ruleType,
    ]
      .map(function (part) {
        return String(part || "").trim().toLowerCase();
      })
      .filter(Boolean)
      .join(" ");
  }

  function normalizeEarnRuleTitle(item) {
    return String((item && item.title) || "")
      .trim()
      .toLowerCase();
  }

  function isProductReviewRule(item) {
    if (!item) return false;
    if (isBirthdayEarnRule(item)) return false;
    const ruleType = String((item && item.ruleType) || "")
      .trim()
      .toLowerCase();
    if (ruleType.indexOf("review") !== -1) return true;
    const text = getEarnRuleSearchText(item);
    if (!text) return false;
    if (
      text.indexOf(PRODUCT_REVIEW_RULE_NAME) !== -1 ||
      text.indexOf("judge.me") !== -1
    ) {
      return true;
    }
    if (text.indexOf("review") === -1) return false;
    return (
      text.indexOf("product") !== -1 ||
      text.indexOf("sharing") !== -1 ||
      text.indexOf("photo") !== -1 ||
      text.indexOf("video") !== -1 ||
      text.indexOf("leave") !== -1 ||
      text.indexOf("judge") !== -1
    );
  }

  function isBirthdayEarnRule(item) {
    if (!item) return false;
    const text = getEarnRuleSearchText(item);
    return text === "birthday" || text.indexOf("birthday") !== -1;
  }

  function isPurchaseEarnRule(item) {
    if (!item) return false;
    if (
      isBirthdayEarnRule(item) ||
      isProductReviewRule(item) ||
      item.categoryName === CATEGORY_SOCIAL ||
      item.categoryName === CATEGORY_LINK_SHARING
    ) {
      return false;
    }
    const rewardType = String((item && item.rewardType) || "")
      .trim()
      .toUpperCase();
    if (rewardType === "RATIO") return true;
    const text = getEarnRuleSearchText(item);
    if (!text) return false;
    if (text.indexOf("refer") !== -1 && text.indexOf("friend") !== -1) {
      return false;
    }
    if (text.indexOf("every $") !== -1 || text.indexOf("per $") !== -1) {
      return true;
    }
    if (text.indexOf("spent") !== -1 && text.indexOf("point") !== -1) {
      return true;
    }
    if (text.indexOf("purchase") !== -1 && text.indexOf("point") !== -1) {
      return true;
    }
    const category = String((item && item.categoryName) || "")
      .trim()
      .toLowerCase();
    return category.indexOf("purchase") !== -1;
  }

  function getPurchaseEarnLink(item) {
    const fromRule =
      item && item.socialLink ? String(item.socialLink).trim() : "";
    if (fromRule) return fromRule;
    return (
      normalizeSocialLink(EARN_PURCHASE_SHOP_URL) || DEFAULT_EARN_CTA_PATH
    );
  }

  function getPurchaseEarnButtonLabel(item) {
    const title = String((item && item.title) || "").trim();
    const description = String((item && item.ruleDescription) || "").trim();
    const facing = String((item && item.buttonLabel) || "").trim();
    if (title && title.length <= 48) return title;
    if (facing && facing !== description && facing.length <= 48) return facing;
    return t("redeem.shop_now");
  }

  let cachedProfileBirthdayIso = "";

  function parseProfileBirthdayIso(value) {
    const match = String(value || "")
      .trim()
      .match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[0] : "";
  }

  function rememberProfileBirthdayIso(iso) {
    const normalized = parseProfileBirthdayIso(iso);
    if (normalized) cachedProfileBirthdayIso = normalized;
  }

  function getProfileBirthdayIsoFromDom() {
    const birthdayInput = root.querySelector("[data-profile-birthday]");
    if (!birthdayInput) return "";
    const iso = String(
      birthdayInput.getAttribute("data-birthday-iso") || "",
    ).trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : "";
  }

  function getProfileBirthdayIso() {
    const fromDom = getProfileBirthdayIsoFromDom();
    if (fromDom) {
      rememberProfileBirthdayIso(fromDom);
      return fromDom;
    }
    return cachedProfileBirthdayIso || "";
  }

  function hasProfileBirthday() {
    return !!getProfileBirthdayIso();
  }

  function fetchProfileBirthdayForEarn() {
    if (!isLoggedIn()) return Promise.resolve("");
    const baseUrl = getLoyaltyBaseUrl();
    if (!baseUrl) return Promise.resolve("");
    return fetch(baseUrl.replace(/\/$/, "") + "/front/customer/profile", {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        if (
          json &&
          Number(json.code) === 200 &&
          json.data &&
          json.data.birthday
        ) {
          rememberProfileBirthdayIso(json.data.birthday);
        }
        return cachedProfileBirthdayIso;
      })
      .catch(function () {
        return cachedProfileBirthdayIso;
      });
  }

  function getProductReviewLink(item) {
    const fromRule =
      item && item.socialLink ? String(item.socialLink).trim() : "";
    if (fromRule) return fromRule;
    return (
      normalizeSocialLink(EARN_PRODUCT_REVIEW_URL) || DEFAULT_EARN_CTA_PATH
    );
  }

  function shouldShowEarnRuleAsCompleted(item) {
    if (!item) return false;
    if (isProductReviewRule(item)) return false;
    if (isPurchaseEarnRule(item)) return false;
    if (isBirthdayEarnRule(item)) {
      return hasProfileBirthday() || !!item.completed;
    }
    return !!item.completed;
  }

  const MEMBERSHIP_REFER_URL = (
    root.getAttribute("data-membership-refer-url") || ""
  ).trim();

  function initReferBanner() {
    const banner = root.querySelector("[data-refer-banner]");
    if (!banner) return;

    const bannerHome = banner.parentNode;
    const bannerNext = banner.nextSibling;

    function isReferBannerMobile() {
      return !!(
        window.matchMedia && window.matchMedia("(max-width: 768px)").matches
      );
    }

    function setReferBannerScrollLock(locked) {
      document.documentElement.style.overflow = locked ? "hidden" : "";
      document.body.style.overflow = locked ? "hidden" : "";
    }

    function placeReferBanner() {
      const mobileSheet = isReferBannerMobile() && !banner.hidden;
      banner.classList.toggle("is-mobile-dialog", mobileSheet);
      if (mobileSheet) {
        if (banner.parentNode !== document.body) {
          document.body.appendChild(banner);
        }
        setReferBannerScrollLock(true);
        return;
      }
      if (bannerHome && banner.parentNode !== bannerHome) {
        if (bannerNext && bannerNext.parentNode === bannerHome) {
          bannerHome.insertBefore(banner, bannerNext);
        } else {
          bannerHome.appendChild(banner);
        }
      }
      setReferBannerScrollLock(false);
    }

    function dismissBanner(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (banner.classList.contains("is-closing")) return;

      banner.setAttribute("aria-hidden", "true");

      function finishDismiss() {
        banner.hidden = true;
        banner.classList.remove("is-closing");
        banner.classList.add("is-dismissed");
        placeReferBanner();
      }

      const reduceMotion =
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (!isReferBannerMobile() || reduceMotion) {
        finishDismiss();
        return;
      }

      const sheet = banner.querySelector(".points-center-refer-banner__sheet");
      let fallbackTimer = null;

      function onCloseAnimationEnd(event) {
        if (sheet && event && event.target !== sheet) return;
        if (fallbackTimer) window.clearTimeout(fallbackTimer);
        if (sheet)
          sheet.removeEventListener("animationend", onCloseAnimationEnd);
        finishDismiss();
      }

      banner.classList.add("is-closing");

      if (sheet) {
        sheet.addEventListener("animationend", onCloseAnimationEnd);
        fallbackTimer = window.setTimeout(onCloseAnimationEnd, 450);
      } else {
        finishDismiss();
      }
    }

    banner.hidden = false;
    banner.classList.remove("is-dismissed");
    banner.removeAttribute("aria-hidden");
    placeReferBanner();

    banner
      .querySelectorAll("[data-refer-banner-later]")
      .forEach(function (btn) {
        btn.addEventListener("click", dismissBanner);
      });

    if (window.matchMedia) {
      const mediaQuery = window.matchMedia("(max-width: 768px)");
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener("change", placeReferBanner);
      } else if (mediaQuery.addListener) {
        mediaQuery.addListener(placeReferBanner);
      }
    }
  }

  initReferBanner();

  let syncOverviewViewMore = null;

  function isOverviewMobileViewport() {
    return !!(
      window.matchMedia && window.matchMedia("(max-width: 768px)").matches
    );
  }

  const OVERVIEW_MORE_SLIDE_MS = 360;

  function lockOverviewScrollDuringSlide(toggle) {
    const scrollingElement =
      document.scrollingElement || document.documentElement;
    const scrollTop = scrollingElement.scrollTop;
    const scrollLeft = scrollingElement.scrollLeft;
    const htmlAnchor = document.documentElement.style.overflowAnchor;
    const bodyAnchor = document.body.style.overflowAnchor;
    let released = false;

    if (toggle && typeof toggle.blur === "function") {
      toggle.blur();
    }

    document.documentElement.style.overflowAnchor = "none";
    document.body.style.overflowAnchor = "none";

    function hold() {
      if (released) return;
      scrollingElement.scrollTop = scrollTop;
      scrollingElement.scrollLeft = scrollLeft;
    }

    function release() {
      hold();
      requestAnimationFrame(function () {
        hold();
        released = true;
        document.documentElement.style.overflowAnchor = htmlAnchor;
        document.body.style.overflowAnchor = bodyAnchor;
      });
    }

    return { hold: hold, release: release };
  }

  function initOverviewViewMore() {
    const overview = root.querySelector(".points-center-overview");
    const toggle = root.querySelector("[data-overview-view-more]");
    const moreBody = root.querySelector("[data-overview-more-body]");
    const $ = window.jQuery;
    if (!overview || !toggle || !moreBody || !$) return;

    const $moreBody = $(moreBody);

    function refreshOverviewEarnCarousel() {
      requestAnimationFrame(function () {
        if (typeof initOverviewEarnCarousel === "function") {
          initOverviewEarnCarousel();
        }
      });
    }

    syncOverviewViewMore = function () {
      const expanded = overview.classList.contains("is-expanded");

      if (!isOverviewMobileViewport()) {
        overview.classList.remove("is-expanded");
        toggle.setAttribute("aria-expanded", "false");
        $moreBody.stop(true, true).removeAttr("style aria-hidden");
        return;
      }

      toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
    };

    toggle.addEventListener("click", function () {
      const willExpand = !overview.classList.contains("is-expanded");
      const duration = window.matchMedia("(prefers-reduced-motion: reduce)")
        .matches
        ? 0
        : OVERVIEW_MORE_SLIDE_MS;
      const scrollLock = lockOverviewScrollDuringSlide(toggle);

      if (willExpand) {
        overview.classList.add("is-expanded");
        syncOverviewViewMore();
        $moreBody
          .attr("aria-hidden", "false")
          .stop(true, true)
          .slideDown({
            duration: duration,
            progress: scrollLock.hold,
            complete: function () {
              $moreBody.css("display", "block");
              scrollLock.release();
              refreshOverviewEarnCarousel();
            },
          });
        return;
      }

      toggle.setAttribute("aria-expanded", "false");
      $moreBody.stop(true, true).slideUp({
        duration: duration,
        progress: scrollLock.hold,
        complete: function () {
          overview.classList.remove("is-expanded");
          $moreBody.attr("aria-hidden", "true").css("display", "none");
          scrollLock.release();
        },
      });
    });

    function handleViewportChange() {
      if (isOverviewMobileViewport()) {
        overview.classList.remove("is-expanded");
        toggle.setAttribute("aria-expanded", "false");
        $moreBody.stop(true, true).hide().attr("aria-hidden", "true");
      } else {
        syncOverviewViewMore();
      }
      refreshOverviewEarnCarousel();
    }

    if (window.matchMedia) {
      const mediaQuery = window.matchMedia("(max-width: 768px)");
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener("change", handleViewportChange);
      } else if (mediaQuery.addListener) {
        mediaQuery.addListener(handleViewportChange);
      }
    }

    handleViewportChange();
  }

  function expandOverviewEarnOnMobile(onComplete) {
    const overview = root.querySelector(".points-center-overview");
    const moreBody = root.querySelector("[data-overview-more-body]");
    const toggle = root.querySelector("[data-overview-view-more]");
    const $ = window.jQuery;

    if (
      !overview ||
      !moreBody ||
      !toggle ||
      !$ ||
      !isOverviewMobileViewport()
    ) {
      if (typeof onComplete === "function") onComplete();
      return false;
    }

    function finishExpand() {
      if (typeof syncOverviewViewMore === "function") {
        syncOverviewViewMore();
      }
      requestAnimationFrame(function () {
        if (typeof initOverviewEarnCarousel === "function") {
          initOverviewEarnCarousel();
        }
        if (typeof onComplete === "function") onComplete();
      });
    }

    if (overview.classList.contains("is-expanded")) {
      finishExpand();
      return false;
    }

    const $moreBody = $(moreBody);
    const duration = window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches
      ? 0
      : OVERVIEW_MORE_SLIDE_MS;

    overview.classList.add("is-expanded");
    toggle.setAttribute("aria-expanded", "true");
    const scrollLock = lockOverviewScrollDuringSlide(toggle);
    $moreBody
      .attr("aria-hidden", "false")
      .stop(true, true)
      .slideDown({
        duration: duration,
        progress: scrollLock.hold,
        complete: function () {
          $moreBody.css("display", "block");
          scrollLock.release();
          finishExpand();
        },
      });
    return true;
  }

  initOverviewViewMore();

  // ── Auth & panel accessors ─────────────────────────────────────────
  function getOverviewPanel() {
    return root.querySelector('[data-points-center-panel="account-overview"]');
  }

  function getLoyaltyBaseUrl() {
    const panel = getOverviewPanel();
    return (
      ((panel && panel.getAttribute("data-loyalty-base-url")) || "").trim() ||
      (window.Theme && Theme.loyalty && Theme.loyalty.apiBase) ||
      ""
    ).trim();
  }

  function getGiftCardShopifyId() {
    if (POINTS_CENTER_TEST_SHOPIFY_ID != null) {
      return POINTS_CENTER_TEST_SHOPIFY_ID;
    }

    const themeId =
      window.Theme && Theme.loyalty && Theme.loyalty.shopifyId != null
        ? Theme.loyalty.shopifyId
        : null;
    if (themeId) return themeId;

    const panel = getOverviewPanel();
    const raw = String((panel && panel.getAttribute("data-shopify-id")) || "").trim();
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : raw;
  }

  function isLoggedIn() {
    return !!getGiftCardShopifyId();
  }

  function getLoginReturnTo() {
    const fromRoot = (root.getAttribute("data-login-return-to") || "").trim();
    if (fromRoot) return fromRoot.split("?")[0].split("#")[0];
    const fromTheme =
      window.Theme &&
      window.Theme.loyalty &&
      window.Theme.loyalty.loginReturnTo;
    if (fromTheme && String(fromTheme).trim()) {
      return String(fromTheme).trim().split("?")[0].split("#")[0];
    }
    if (
      window.location.pathname &&
      window.location.pathname.indexOf("/pages/") === 0
    ) {
      return window.location.pathname;
    }
    return "/pages/points-center";
  }

  function getAuthUrl() {
    const returnTo = getLoginReturnTo();
    return (
      "/customer_authentication/login?return_to=" + encodeURIComponent(returnTo)
    );
  }

  function redirectToLogin() {
    const fromRoot = (root.getAttribute("data-auth-url") || "").trim();
    const url = fromRoot || getAuthUrl();
    if (url) window.location.assign(url);
  }

  function canAffordRedeem(cost) {
    return Number(userAvailablePoints) >= Number(cost);
  }

  function toSummaryPoints(value) {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  }

  function unwrapLoyaltySummaryPayload(data) {
    if (!data || typeof data !== "object") return null;
    if (
      data.availablePoints != null ||
      data.available_points != null ||
      data.totalPoints != null ||
      data.total_points != null
    ) {
      return data;
    }
    if (data.summary && typeof data.summary === "object") return data.summary;
    if (data.loyaltySummary && typeof data.loyaltySummary === "object") {
      return data.loyaltySummary;
    }
    return data;
  }

  function extractLoyaltySummaryJson(json) {
    if (!json || Number(json.code) !== 200) return null;
    if (json.data != null && typeof json.data === "object") {
      return unwrapLoyaltySummaryPayload(json.data);
    }
    return unwrapLoyaltySummaryPayload(json);
  }

  function getSummaryAvailablePoints(data) {
    if (!data) return 0;
    const raw =
      data.availablePoints != null
        ? data.availablePoints
        : data.available_points != null
          ? data.available_points
          : data.points;
    return toSummaryPoints(raw);
  }

  function getSummaryPendingPoints(data) {
    if (!data) return 0;
    return toSummaryPoints(
      data.pendingPoints != null ? data.pendingPoints : data.pending_points,
    );
  }

  function normalizeLoyaltySummaryData(data) {
    if (!data || typeof data !== "object") return data;
    return Object.assign({}, data, {
      availablePoints: getSummaryAvailablePoints(data),
      pendingPoints: getSummaryPendingPoints(data),
    });
  }

  function syncAvailablePoints(points) {
    userAvailablePoints = toSummaryPoints(points);
    root.dataset.availablePoints = userAvailablePoints;
  }

  function updateHeaderPoints(data) {
    const points = getSummaryAvailablePoints(data);
    root.querySelectorAll("[data-points-available]").forEach(function (pointsEl) {
      const shouldAnimate =
        !availablePointsCountUpPlayed && !prefersReducedMotion();
      if (shouldAnimate) {
        availablePointsCountUpPlayed = true;
        animateCountUp(pointsEl, points, { start: 0, force: true });
      } else {
        pointsEl.textContent = numberFormatter.format(points);
        pointsEl.dataset.countupValue = String(points);
      }
    });
  }

  function applyLoyaltySummaryToUi(summary) {
    if (!summary) return;
    syncAvailablePoints(getSummaryAvailablePoints(summary));
    updateHeaderPoints(summary);
  }

  // ── Loyalty overview (tiers, summary, membership bar) ──────────────
  function fetchLoyaltyTiers(force) {
    if (!force && loyaltyTiersCache) {
      return Promise.resolve(loyaltyTiersCache);
    }

    const baseUrl = getLoyaltyBaseUrl();
    if (!baseUrl) {
      loyaltyTiersCache = [];
      return Promise.resolve([]);
    }

    if (!force && tiersFetchPromise) {
      return tiersFetchPromise;
    }

    if (force) {
      loyaltyTiersCache = null;
      tiersFetchPromise = null;
    }

    tiersFetchPromise = fetch(
      baseUrl.replace(/\/$/, "") + "/front/loyalty/membership/tier/list",
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      },
    )
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        if (json && json.code === 200 && Array.isArray(json.data)) {
          loyaltyTiersCache = json.data.slice().sort(function (a, b) {
            return (
              Number(a.minPoints) - Number(b.minPoints) ||
              Number(a.id) - Number(b.id)
            );
          });
          return loyaltyTiersCache;
        }
        loyaltyTiersCache = [];
        return [];
      })
      .catch(function () {
        loyaltyTiersCache = [];
        return [];
      })
      .finally(function () {
        tiersFetchPromise = null;
      });

    return tiersFetchPromise;
  }

  function fetchBonusRulesSimple(force) {
    if (!force && bonusRulesCache) {
      return Promise.resolve(bonusRulesCache);
    }

    const baseUrl = getLoyaltyBaseUrl();
    if (!baseUrl) {
      bonusRulesCache = [];
      return Promise.resolve([]);
    }

    if (!force && bonusRulesFetchPromise) {
      return bonusRulesFetchPromise;
    }

    if (force) {
      bonusRulesCache = null;
      bonusRulesFetchPromise = null;
    }

    bonusRulesFetchPromise = fetch(
      baseUrl.replace(/\/$/, "") + "/front/loyalty/listSimple",
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      },
    )
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        if (json && json.code === 200 && Array.isArray(json.data)) {
          bonusRulesCache = json.data;
          return bonusRulesCache;
        }
        bonusRulesCache = [];
        return [];
      })
      .catch(function () {
        bonusRulesCache = [];
        return [];
      })
      .finally(function () {
        bonusRulesFetchPromise = null;
      });

    return bonusRulesFetchPromise;
  }

  function buildBonusRulesMap(rules) {
    const map = {};
    if (!Array.isArray(rules)) return map;
    rules.forEach(function (rule) {
      const id = Number(rule.id);
      if (id) map[id] = rule;
    });
    return map;
  }

  function formatMembershipPointRate(rate) {
    const value = Number(rate);
    if (!Number.isFinite(value)) return "—";
    return Number.isInteger(value) ? String(value) : String(value);
  }

  var LEVEL_UP_PERKS_ICON_SVG =
    '<svg width="28" height="28" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<g clip-path="url(#points-perks-clip)">' +
    '<path d="M9 18C6.597 18 4.338 17.064 2.637 15.363C0.936 13.662 0 11.403 0 9C0 6.597 0.936 4.338 2.637 2.637C4.338 0.936 6.597 0 9 0C11.403 0 13.662 0.936 15.363 2.637C17.064 4.338 18 6.597 18 9C18 11.403 17.064 13.662 15.363 15.363C13.662 17.064 11.403 18 9 18ZM9 1.125C6.897 1.125 4.92 1.944 3.432 3.432C1.944 4.92 1.125 6.897 1.125 9C1.125 11.103 1.944 13.08 3.432 14.568C4.92 16.056 6.9 16.875 9 16.875C11.1 16.875 13.08 16.056 14.568 14.568C16.056 13.08 16.875 11.1 16.875 9C16.875 6.9 16.056 4.92 14.568 3.432C13.08 1.944 11.103 1.125 9 1.125Z" fill="#133EE3"/>' +
    '<path d="M11.5983 13.41C11.4633 13.41 11.3313 13.377 11.2053 13.311L8.97635 12.141L6.74735 13.311C6.46235 13.461 6.12035 13.437 5.85935 13.248C5.59835 13.059 5.46935 12.741 5.52335 12.426L5.94935 9.94502L4.14935 8.18702C3.91835 7.96202 3.83435 7.63202 3.93635 7.32302C4.03835 7.01402 4.29635 6.79502 4.61735 6.75002L7.10735 6.39002L8.22035 4.13402C8.36435 3.84302 8.65235 3.66602 8.97635 3.66602C9.30035 3.66602 9.58835 3.84602 9.73235 4.13402L10.8453 6.39002L13.3353 6.75002C13.6533 6.79802 13.9173 7.01402 14.0163 7.32602C14.1183 7.63202 14.0343 7.96501 13.8033 8.19001L12.0033 9.94801L12.4293 12.426C12.4833 12.744 12.3573 13.059 12.0933 13.248C11.9493 13.356 11.7753 13.41 11.6013 13.41H11.5983ZM5.34035 7.77902L6.84035 9.24001C7.03835 9.43501 7.12835 9.71102 7.08335 9.98702L6.72935 12.051L8.58335 11.079C8.82935 10.95 9.12335 10.95 9.36635 11.079L11.2203 12.051L10.8663 9.98702C10.8183 9.71402 10.9113 9.43501 11.1093 9.24001L12.6093 7.77902L10.5363 7.47902C10.2633 7.44002 10.0263 7.26902 9.90035 7.02002L8.97935 5.14202L8.05235 7.02002C7.92935 7.26902 7.69235 7.44302 7.41635 7.48202L5.34335 7.77902H5.34035Z" fill="#133EE3"/>' +
    '</g><defs><clipPath id="points-perks-clip"><rect width="18" height="18" fill="white"/></clipPath></defs></svg>';

  function buildCurrentTierBenefitItems(tier, rulesMap) {
    const items = [];
    const pointRate = Number(tier.pointRate);
    if (Number.isFinite(pointRate)) {
      items.push({
        name: t("rewards.points_per_dollar_short", {
          count: formatMembershipPointRate(pointRate),
        }),
        iconHtml: LEVEL_UP_PERKS_ICON_SVG,
        description: "",
      });
    }

    const ruleIds = Array.isArray(tier.bonusRuleIds) ? tier.bonusRuleIds : [];
    ruleIds.forEach(function (ruleId) {
      const rule = rulesMap[Number(ruleId)];
      if (!rule) return;
      items.push({
        name: rule.ruleName || t("redeem.benefit"),
        icon: rule.ruleIcon || "",
        description: String(rule.description || "").trim(),
      });
    });

    return items;
  }

  function getCurrentMyBenefitsItems() {
    const summary = loyaltySummaryData;
    if (!summary || summary.currentTierId == null) return [];
    const tierBenefits = loyaltyTiersCache || [];
    const currentTier = tierBenefits.find(function (tier) {
      return Number(tier.id) === Number(summary.currentTierId);
    });
    if (!currentTier) return [];
    const rulesMap = buildBonusRulesMap(bonusRulesCache || []);
    return buildCurrentTierBenefitItems(currentTier, rulesMap);
  }

  function renderBenefitsHelpModalList(listEl) {
    if (!listEl) return;
    const items = getCurrentMyBenefitsItems();
    listEl.classList.toggle("is-single", items.length === 1);
    if (!items.length) {
      listEl.classList.remove("is-single");
      listEl.innerHTML =
        '<p class="benefits-help-modal__item-desc">' +
        escapeHtml(t("benefits.no_benefits")) +
        "</p>";
      return;
    }

    listEl.innerHTML = items
      .map(function (item) {
        const iconSrc = escapeHttpUrl(item.icon);
        const iconHtml = item.iconHtml
          ? item.iconHtml
          : iconSrc
            ? '<img class="benefits-help-modal__icon-svg" src="' +
              escapeHtml(iconSrc) +
              '" alt="" width="26" height="26" loading="lazy" decoding="async">'
            : "";
        const desc = String(item.description || "").trim();
        return (
          '<div class="benefits-help-modal__item' +
          (desc ? "" : " is-no-desc") +
          '">' +
          '<div class="benefits-help-modal__icon" aria-hidden="true">' +
          iconHtml +
          "</div>" +
          '<div class="benefits-help-modal__text">' +
          '<p class="benefits-help-modal__item-title">' +
          escapeHtml(item.name || t("redeem.benefit")) +
          "</p>" +
          (desc
            ? '<p class="benefits-help-modal__item-desc">' +
              escapeHtml(desc) +
              "</p>"
            : "") +
          "</div></div>"
        );
      })
      .join("");
  }

  function getTierMinPoints(tier) {
    if (!tier || tier.minPoints == null) return 0;
    const value = Number(tier.minPoints);
    return Number.isFinite(value) ? value : 0;
  }

  const PROFILE_BG_SLOT_COUNT = 4;

  function getTierProfileBgConfig() {
    const configEl = root.querySelector("[data-points-center-tier-profile-bg]");
    const fallback = {
      default: "",
      byTierIndex: [],
      defaultMobile: "",
      byTierIndexMobile: [],
    };
    if (!configEl) return fallback;
    try {
      const parsed = JSON.parse(configEl.textContent);
      return {
        default: parsed && parsed.default ? String(parsed.default) : "",
        byTierIndex:
          parsed && Array.isArray(parsed.byTierIndex) ? parsed.byTierIndex : [],
        defaultMobile:
          parsed && parsed.defaultMobile ? String(parsed.defaultMobile) : "",
        byTierIndexMobile:
          parsed && Array.isArray(parsed.byTierIndexMobile)
            ? parsed.byTierIndexMobile
            : [],
      };
    } catch (e) {
      return fallback;
    }
  }

  function resolveProfileBgSlotIndex(tiers, tierIndex) {
    if (tierIndex < 0) return -1;
    if (!Array.isArray(tiers) || !tiers.length) {
      return Math.min(PROFILE_BG_SLOT_COUNT - 1, tierIndex);
    }

    const tier = tiers[tierIndex];
    const name = String((tier && tier.tierName) || "")
      .trim()
      .toLowerCase();

    if (name.includes("diamond") || name.includes("钻石")) return 3;
    if (name.includes("gold") || name.includes("黄金")) return 2;
    if (
      name.includes("platinum") ||
      name.includes("铂金") ||
      name.includes("白金")
    ) {
      return 2;
    }
    if (name.includes("silver") || name.includes("白银")) return 1;
    if (name.includes("bronze") || name.includes("青铜")) return 0;

    if (tierIndex === tiers.length - 1 && tiers.length > PROFILE_BG_SLOT_COUNT) {
      return PROFILE_BG_SLOT_COUNT - 1;
    }

    return Math.min(PROFILE_BG_SLOT_COUNT - 1, tierIndex);
  }

  let appliedProfileBgKey = "";
  let profileBgApplySeq = 0;

  function preloadProfileImageUrl(url) {
    return new Promise(function (resolve) {
      const raw = String(url || "").trim();
      if (!raw) {
        resolve();
        return;
      }
      const img = new Image();
      img.onload = function () {
        resolve();
      };
      img.onerror = function () {
        resolve();
      };
      img.src = raw;
    });
  }

  function setProfileBgLoading(active) {
    root.querySelectorAll("[data-overview-profile-card]").forEach(function (el) {
      el.classList.toggle("is-profile-bg-loading", !!active);
      el.setAttribute("aria-busy", active ? "true" : "false");
    });
  }

  function setProfileBgPending(active) {
    root.querySelectorAll("[data-overview-profile-card]").forEach(function (el) {
      el.classList.toggle("is-profile-bg-pending", !!active);
    });
  }

  function clearProfileBgPendingState() {
    setProfileBgPending(false);
    setProfileBgLoading(false);
  }

  function formatProfileBgCssValue(url) {
    const raw = String(url || "").trim();
    if (!raw) return "";
    return "url(" + JSON.stringify(raw) + ")";
  }

  function getProfileBgUrlForSlot(config, slotIndex, useMobile) {
    const urls = useMobile
      ? config.byTierIndexMobile || []
      : config.byTierIndex || [];
    const fallbackDefault = useMobile
      ? config.defaultMobile || config.default || ""
      : config.default || "";
    if (
      slotIndex >= 0 &&
      slotIndex < urls.length &&
      String(urls[slotIndex] || "").trim()
    ) {
      return String(urls[slotIndex]).trim();
    }
    if (!useMobile) return fallbackDefault;
    return getProfileBgUrlForSlot(config, slotIndex, false) || fallbackDefault;
  }

  function applyTierProfileBackgroundBySlot(slotIndex) {
    const config = getTierProfileBgConfig();
    const safeSlot = Math.min(
      PROFILE_BG_SLOT_COUNT - 1,
      Math.max(0, Number(slotIndex) || 0),
    );
    const bgUrl = getProfileBgUrlForSlot(config, safeSlot, false);
    const bgUrlMobile = getProfileBgUrlForSlot(config, safeSlot, true);
    if (!bgUrl && !bgUrlMobile) {
      clearProfileBgPendingState();
      return;
    }

    const bgValue =
      formatProfileBgCssValue(bgUrl) || formatProfileBgCssValue(bgUrlMobile);
    const bgValueMobile =
      formatProfileBgCssValue(bgUrlMobile) || formatProfileBgCssValue(bgUrl);
    if (!bgValue && !bgValueMobile) {
      clearProfileBgPendingState();
      return;
    }

    const bgKey = safeSlot + "|" + bgUrl + "|" + bgUrlMobile;
    if (bgKey === appliedProfileBgKey) {
      setProfileBgPending(false);
      setProfileBgLoading(false);
      return;
    }

    const applySeq = ++profileBgApplySeq;
    setProfileBgLoading(true);
    Promise.all([
      preloadProfileImageUrl(bgUrl),
      preloadProfileImageUrl(bgUrlMobile),
    ]).then(function () {
      if (applySeq !== profileBgApplySeq) return;
      appliedProfileBgKey = bgKey;

      if (bgValue) {
        root.style.setProperty("--points-tier-profile-bg", bgValue);
      }
      if (bgValueMobile) {
        root.style.setProperty("--points-tier-profile-bg-mobile", bgValueMobile);
        root.style.setProperty("--points-mobile-hero-bg", bgValueMobile);
      }

      root
        .querySelectorAll("[data-overview-profile-card]")
        .forEach(function (el) {
          const isBenefitsHero = el.classList.contains("points-benefits-hero");
          const cssVar = isBenefitsHero
            ? "--points-benefits-hero-bg"
            : "--points-profile-bg";
          const tierClassPrefix = isBenefitsHero
            ? "points-benefits-hero--tier-"
            : "points-center-overview-profile--tier-";

          if (bgValue) {
            el.style.setProperty(cssVar, bgValue);
          }
          if (bgValueMobile) {
            el.style.setProperty("--points-profile-bg-mobile", bgValueMobile);
            if (isBenefitsHero) {
              el.style.setProperty(
                "--points-benefits-hero-bg-mobile",
                bgValueMobile,
              );
            }
          }

          for (let i = 0; i < PROFILE_BG_SLOT_COUNT; i += 1) {
            el.classList.remove(tierClassPrefix + i);
          }
          el.setAttribute("data-profile-tier-index", String(safeSlot));
          el.classList.add(tierClassPrefix + safeSlot);
        });

      if (applySeq === profileBgApplySeq) {
        setProfileBgPending(false);
        setProfileBgLoading(false);
      }
    });
  }

  function applyTierProfileBackground(summary, tiers) {
    if (
      !summary ||
      summary.currentTierId == null ||
      !Array.isArray(tiers) ||
      !tiers.length
    ) {
      if (root.getAttribute("data-design-mode") === "true") {
        applyTierProfileBackgroundBySlot(0);
      } else {
        setProfileBgPending(true);
        setProfileBgLoading(false);
      }
      return;
    }

    const tierIndex = tiers.findIndex(function (tier) {
      return Number(tier.id) === Number(summary.currentTierId);
    });
    if (tierIndex < 0) {
      setProfileBgPending(true);
      setProfileBgLoading(false);
      return;
    }

    const slotIndex = resolveProfileBgSlotIndex(tiers, tierIndex);
    applyTierProfileBackgroundBySlot(slotIndex);
  }

  function bootstrapTierProfileBackgrounds() {
    if (root.getAttribute("data-design-mode") !== "true") {
      return;
    }
    let slot = 0;
    const previewTier = Number(
      root.getAttribute("data-profile-bg-preview-tier"),
    );
    if (Number.isFinite(previewTier)) {
      slot = Math.min(PROFILE_BG_SLOT_COUNT - 1, Math.max(0, previewTier));
    }
    setProfileBgPending(false);
    applyTierProfileBackgroundBySlot(slot);
  }

  function getTierProgress(summary, tiers) {
    const invalid = {
      invalid: true,
      currentTierName: "",
      nextTierName: "",
      progressCurrent: 0,
      progressMax: 0,
      percent: 0,
      pointsNeeded: 0,
      isMaxTier: false,
    };
    const sorted = Array.isArray(tiers) ? tiers : [];
    if (
      !summary ||
      summary.totalPoints == null ||
      summary.currentTierId == null
    ) {
      return invalid;
    }

    const totalPoints = Number(summary.totalPoints) || 0;
    const tierId = Number(summary.currentTierId);
    const currentIdx = sorted.findIndex(function (tier) {
      return Number(tier.id) === tierId;
    });
    if (currentIdx < 0) return invalid;

    const currentTier = sorted[currentIdx] || { tierName: "", minPoints: 0 };
    const nextTier = sorted[currentIdx + 1];
    const currentTierMin = getTierMinPoints(currentTier);

    if (!nextTier) {
      return {
        invalid: false,
        currentTierName: currentTier.tierName || "",
        nextTierName: "",
        progressCurrent: totalPoints,
        progressMax: currentTierMin,
        percent: 100,
        pointsNeeded: 0,
        isMaxTier: true,
      };
    }

    const nextTierMin = getTierMinPoints(nextTier);
    const percent =
      nextTierMin <= 0
        ? totalPoints > 0
          ? 100
          : 0
        : Math.min(100, Math.round((totalPoints / nextTierMin) * 100));
    const pointsNeeded = Math.max(0, nextTierMin - totalPoints);

    return {
      invalid: false,
      currentTierName: currentTier.tierName || "",
      nextTierName: nextTier.tierName || "",
      progressCurrent: totalPoints,
      progressMax: nextTierMin,
      percent: percent,
      pointsNeeded: pointsNeeded,
      isMaxTier: false,
    };
  }

  function renderOverviewTierIcons(tiers, summary) {
    const host = root.querySelector("[data-overview-tier-icons]");
    if (!host) return;

    const sorted = Array.isArray(tiers) ? tiers : [];
    const currentTierId =
      summary && summary.currentTierId != null
        ? Number(summary.currentTierId)
        : null;

    function finishTierIconsLoading() {
      host.classList.remove("is-loading");
      host.removeAttribute("aria-busy");
      host.setAttribute("aria-hidden", "true");
      host.removeAttribute("aria-label");
    }

    if (
      !sorted.length ||
      currentTierId == null ||
      !Number.isFinite(currentTierId)
    ) {
      host.innerHTML = "";
      finishTierIconsLoading();
      return;
    }

    const currentIdx = sorted.findIndex(function (tier) {
      return Number(tier.id) === currentTierId;
    });
    if (currentIdx < 0) {
      host.innerHTML = "";
      finishTierIconsLoading();
      return;
    }

    const currentTier = sorted[currentIdx];
    let fallbacks = [];
    try {
      fallbacks = JSON.parse(
        host.getAttribute("data-fallback-tier-icons") || "[]",
      );
    } catch (e) {
      fallbacks = [];
    }
    if (!Array.isArray(fallbacks)) fallbacks = [];

    // Only current tier badge (same as membership-level-card__badge-image), repeated 3 times
    const tierIcon = String(currentTier.tierIcon || "").trim();
    const badgeSrc =
      (tierIcon ? escapeHttpUrl(tierIcon) : "") ||
      String(fallbacks[currentIdx] || "").trim() ||
      String(fallbacks[fallbacks.length - 1] || "").trim() ||
      "";
    if (!badgeSrc) {
      host.innerHTML = "";
      finishTierIconsLoading();
      return;
    }

    const tierName = escapeHtml(
      currentTier.tierName || t("overview.member_tier"),
    );
    const ICON_REPEAT = 3;
    host.classList.add("is-loading");
    host.setAttribute("aria-busy", "true");
    preloadProfileImageUrl(badgeSrc).then(function () {
      let html = "";
      for (let i = 0; i < ICON_REPEAT; i += 1) {
        html +=
          '<img class="points-center-overview-profile__tier-icon is-current" src="' +
          escapeHtml(badgeSrc) +
          '" alt="' +
          tierName +
          '" title="' +
          tierName +
          '" width="24" height="24" decoding="async">';
      }
      host.innerHTML = html;
      finishTierIconsLoading();
    });
  }

  function setOverviewLevelVisible(visible) {
    const section = root.querySelector("[data-overview-level]");
    if (section) section.hidden = !visible;
  }

  function getMemberSinceDays(createdAt) {
    if (createdAt == null || createdAt === "") return null;

    let createdMs = null;
    if (typeof createdAt === "number" && Number.isFinite(createdAt)) {
      // seconds vs milliseconds
      createdMs = createdAt < 1e12 ? createdAt * 1000 : createdAt;
    } else {
      const parsed = new Date(createdAt);
      if (!Number.isNaN(parsed.getTime())) createdMs = parsed.getTime();
    }

    if (createdMs == null) return null;
    const elapsedDays = Math.floor((Date.now() - createdMs) / 86400000);
    // Joining today is day 1 (floor alone would show 0 before 24h elapse).
    return Math.max(1, elapsedDays);
  }

  function renderOverviewMemberSince(summary) {
    const el = root.querySelector("[data-overview-member-since]");
    if (!el) return;

    const days = getMemberSinceDays(
      summary &&
        (summary.createdAt != null ? summary.createdAt : summary.created_at),
    );

    if (days == null) {
      el.hidden = true;
      el.textContent = "";
      return;
    }

    const label = days === 1 ? t("overview.day") : t("overview.days");
    el.textContent = t("overview.member_since", { count: days, unit: label });
    el.hidden = false;
  }

  function renderOverviewLevel(summary, tiers) {
    const section = root.querySelector("[data-overview-level]");
    const body = root.querySelector("[data-overview-level-body]");
    const loading = root.querySelector("[data-overview-level-loading]");
    if (!section || !body) return;

    const shopifyId = getGiftCardShopifyId();
    if (!shopifyId || !summary) {
      if (loading) loading.hidden = true;
      body.hidden = true;
      section.hidden = true;
      renderOverviewTierIcons([], null);
      renderOverviewMemberSince(null);
      if (root.getAttribute("data-design-mode") === "true") {
        bootstrapTierProfileBackgrounds();
      } else {
        setProfileBgPending(true);
      }
      return;
    }

    renderOverviewMemberSince(summary);
    renderOverviewTierIcons(tiers, summary);
    applyTierProfileBackground(summary, tiers);

    const progress = getTierProgress(summary, tiers);
    const subtitleEl = body.querySelector("[data-overview-tier-subtitle]");
    const progressFillEl = body.querySelector(
      "[data-overview-tier-progress-fill]",
    );
    const progressBarEl = body.querySelector(
      "[data-overview-tier-progressbar]",
    );
    const ratioEl = body.querySelector("[data-overview-tier-ratio]");
    const earnHintEl = root.querySelector("[data-overview-earn-hint]");

    if (!progress.invalid) {
      if (ratioEl) {
        setTierProgressLabel(
          ratioEl,
          progress.progressCurrent,
          progress.progressMax,
          progress.isMaxTier,
        );
      }

      if (subtitleEl) {
        if (progress.isMaxTier) {
          subtitleEl.hidden = true;
          subtitleEl.textContent = "";
        } else {
          subtitleEl.hidden = false;
          subtitleEl.textContent = t("overview.credits_to_next", {
            count: numberFormatter.format(progress.pointsNeeded),
          });
        }
      }

      if (earnHintEl) {
        if (progress.isMaxTier) {
          earnHintEl.textContent = "";
        } else {
          earnHintEl.textContent = t("overview.credits_away", {
            count: numberFormatter.format(progress.pointsNeeded),
            tier: progress.nextTierName || t("overview.next_tier"),
          });
        }
      }

      if (progressFillEl) {
        progressFillEl.style.width = progress.percent + "%";
      }

      if (progressBarEl) {
        progressBarEl.setAttribute("aria-valuenow", String(progress.percent));
        progressBarEl.setAttribute(
          "aria-label",
          progress.isMaxTier
            ? t("overview.progress_complete")
            : t("overview.progress_away", {
                count: numberFormatter.format(progress.pointsNeeded),
                tier: progress.nextTierName || t("overview.next_tier"),
              }),
        );
      }
    } else {
      if (ratioEl) ratioEl.textContent = "";
      if (subtitleEl) {
        subtitleEl.hidden = true;
        subtitleEl.textContent = "";
      }
      if (earnHintEl) earnHintEl.textContent = "";
      if (progressFillEl) progressFillEl.style.width = "0%";
      if (progressBarEl) {
        progressBarEl.setAttribute("aria-valuenow", "0");
        progressBarEl.removeAttribute("aria-label");
      }
    }

    if (loading) loading.hidden = true;
    body.hidden = false;
    section.hidden = false;
  }
  function getBenefitsHeroCountUpOptions(replay) {
    return replay ? { start: 0, force: true } : {};
  }

  function renderBenefitsCenterHero(summary, tiers, couponCount, options) {
    options = options || {};
    const hero = root.querySelector("[data-benefits-hero]");
    if (!hero) return;

    const shopifyId = getGiftCardShopifyId();
    if (!shopifyId) {
      hero.hidden = true;
      return;
    }

    const availablePoints = getSummaryAvailablePoints(summary);
    const progress = getTierProgress(summary, tiers);
    const coupons = couponCount != null ? Number(couponCount) : 0;

    applyTierProfileBackground(summary, tiers);

    const pointsEl = hero.querySelector("[data-benefits-hero-points]");
    const worthEl = hero.querySelector("[data-benefits-hero-worth]");
    const couponsEl = hero.querySelector("[data-benefits-hero-coupons]");
    const progressLabelEl = hero.querySelector(
      "[data-benefits-hero-progress-label]",
    );
    const progressFillEl = hero.querySelector(
      "[data-benefits-hero-progress-fill]",
    );
    const progressBarEl = hero.querySelector(
      "[data-benefits-hero-progressbar]",
    );
    const tierGoalEl = hero.querySelector("[data-benefits-hero-tier-goal]");

    const countUpOpts = getBenefitsHeroCountUpOptions(options.replayCountUp);
    if (pointsEl) {
      if (countUpOpts) {
        animateCountUp(pointsEl, availablePoints, countUpOpts);
      } else {
        pointsEl.textContent = numberFormatter.format(availablePoints);
        pointsEl.dataset.countupValue = String(availablePoints);
      }
    }
    if (worthEl) {
      const worthAmount = availablePoints / 100;
      worthEl.textContent =
        "$" +
        Number(worthAmount).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
    }
    if (couponsEl) {
      if (countUpOpts) {
        animateCountUp(couponsEl, coupons, countUpOpts);
      } else {
        couponsEl.textContent = numberFormatter.format(coupons);
        couponsEl.dataset.countupValue = String(coupons);
      }
    }

    if (!progress.invalid) {
      if (progressLabelEl) {
        setTierProgressLabel(
          progressLabelEl,
          progress.progressCurrent,
          progress.progressMax,
          progress.isMaxTier,
        );
      }

      if (progressFillEl) {
        progressFillEl.style.width = progress.percent + "%";
      }

      if (progressBarEl) {
        progressBarEl.setAttribute("aria-valuenow", String(progress.percent));
      }

      if (tierGoalEl) {
        if (progress.isMaxTier) {
          tierGoalEl.hidden = true;
          tierGoalEl.textContent = "";
        } else {
          tierGoalEl.hidden = false;
          tierGoalEl.textContent = t("overview.credits_to_next", {
            count: numberFormatter.format(progress.pointsNeeded),
          });
        }
      }
    } else {
      if (progressLabelEl) progressLabelEl.textContent = "0/0";
      if (progressFillEl) progressFillEl.style.width = "0%";
      if (progressBarEl) progressBarEl.setAttribute("aria-valuenow", "0");
      if (tierGoalEl) {
        tierGoalEl.hidden = true;
        tierGoalEl.textContent = "";
      }
    }

    hero.hidden = false;
  }

  function renderMyBenefits(summary) {
    const section = root.querySelector("[data-my-benefits]");
    const grid = root.querySelector("[data-my-benefits-grid]");
    if (!section || !grid) return;

    const shopifyId = getGiftCardShopifyId();
    if (!shopifyId) {
      section.hidden = true;
      grid.innerHTML = "";
      return;
    }

    const tierBenefits = loyaltyTiersCache || [];
    const currentTier =
      summary && summary.currentTierId != null
        ? tierBenefits.find(function (tier) {
            return Number(tier.id) === Number(summary.currentTierId);
          })
        : null;

    const rulesMap = buildBonusRulesMap(bonusRulesCache || []);
    const items = currentTier
      ? buildCurrentTierBenefitItems(currentTier, rulesMap)
      : [];

    if (!items.length) {
      grid.innerHTML = "";
      section.hidden = false;
      return;
    }

    let html = "";
    items.forEach(function (item) {
      const iconSrc = escapeHttpUrl(item.icon);
      const iconHtml = item.iconHtml
        ? item.iconHtml
        : iconSrc
          ? '<img src="' +
            escapeHtml(iconSrc) +
            '" alt="" width="36" height="36" loading="lazy" decoding="async">'
          : "";

      html +=
        '<div class="points-my-benefits__item" role="listitem">' +
        '<div class="points-my-benefits__icon">' +
        iconHtml +
        "</div>" +
        '<p class="points-my-benefits__label">' +
        escapeHtml(item.name) +
        "</p>" +
        "</div>";
    });

    grid.innerHTML = html;
    section.hidden = false;
  }

  function renderBenefitsCenter(summary, tiers, couponCount, options) {
    renderBenefitsCenterHero(summary, tiers, couponCount, options);
    renderMyBenefits(summary);
  }

  function refreshLoyaltyOverview(force) {
    const shopifyId = getGiftCardShopifyId();
    if (!shopifyId) {
      if (root.getAttribute("data-design-mode") === "true") {
        bootstrapTierProfileBackgrounds();
      } else {
        setProfileBgPending(true);
        setProfileBgLoading(false);
      }
      setOverviewLevelVisible(false);
      renderOverviewMemberSince(null);
      return Promise.resolve();
    }

    setProfileBgPending(true);
    setProfileBgLoading(true);

    return Promise.all([
      fetchLoyaltySummary(force),
      fetchLoyaltyTiers(force),
      fetchBonusRulesSimple(force),
      updateAssetCounts(force),
      fetchOrderTabCounts(force),
    ]).then(function (results) {
      const summary = results[0];
      const tiers = results[1] || loyaltyTiersCache || [];
      const couponEl = root.querySelector("[data-asset-coupon-count]");
      const couponCount = couponEl ? parseCountUpNumber(couponEl.textContent) : 0;
      if (summary) {
        applyLoyaltySummaryToUi(summary);
        renderOverviewLevel(summary, tiers);
        renderBenefitsCenter(summary, tiers, couponCount);
      } else {
        const loading = root.querySelector("[data-overview-level-loading]");
        const body = root.querySelector("[data-overview-level-body]");
        if (loading) {
          loading.hidden = false;
          loading.textContent = t("overview.unable_load_membership");
        }
        if (body) body.hidden = true;
        renderOverviewMemberSince(null);
        renderBenefitsCenter(null, tiers, couponCount);
        setProfileBgPending(true);
        setProfileBgLoading(false);
      }
      loadEarnPoints(force);
    }).catch(function () {
      setProfileBgPending(true);
      setProfileBgLoading(false);
    });
  }

  let orderTabCountsCache = null;
  let orderTabCountsPromise = null;
  let orderTabCountsRequestId = 0;
  const ORDERS_TAB_KEYS = [
    "all",
    "processing",
    "shipped",
    "delivered",
    "cancelled",
    "refunded",
  ];
  let ordersActiveSubtab = "all";
  let ordersCursorStack = [null];
  let ordersPageIndex = 0;
  let ordersHasNextPage = false;
  let ordersNextCursor = null;
  let ordersTotal = 0;
  let ordersTotalReady = false;
  let ordersPageSize = 10;
  let ordersLoading = false;
  let ordersRequestId = 0;
  let ordersAbortController = null;
  let ordersCurrentItemCount = 0;
  let ordersCurrentItems = [];
  let activateOrdersSubtab = null;
  let ordersDetailLoading = false;

  function isValidOrdersSubtab(tabId) {
    return ORDERS_TAB_KEYS.indexOf(tabId) !== -1;
  }

  function normalizeOrderStatusKey(status) {
    const raw = String(status || "")
      .trim()
      .toLowerCase();
    if (!raw) return "";
    if (raw.indexOf("process") !== -1) return "processing";
    if (raw.indexOf("ship") !== -1) return "shipped";
    if (raw.indexOf("deliver") !== -1) return "delivered";
    if (raw.indexOf("cancel") !== -1) return "cancelled";
    if (raw.indexOf("refund") !== -1) return "refunded";
    return raw;
  }

  function formatOrderDate(value) {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }

  function formatOrderDateTime(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    const day = date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    const time = date
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();
    return day + " at " + time;
  }

  function formatOrderDisplayValue(value) {
    if (value == null || value === "") return "—";
    const text = String(value).trim();
    return text || "—";
  }

  function formatOrderAddressBlock(addr) {
    if (!addr) return "—";
    if (typeof addr === "string") {
      const text = addr.trim();
      return text || "—";
    }
    const name = String(addr.name || addr.customerName || "").trim();
    const lines = [
      addr.address1 || addr.address || "",
      addr.address2 || "",
      [
        addr.city,
        addr.province || addr.provinceCode,
        addr.zip || addr.postalCode,
      ]
        .filter(Boolean)
        .join(", "),
      addr.country || addr.countryCode || "",
    ]
      .map(function (line) {
        return String(line || "").trim();
      })
      .filter(Boolean);
    const parts = [];
    if (name) parts.push(name);
    if (lines.length) parts.push(lines.join(" "));
    return parts.length ? parts.join("\n") : "—";
  }

  function pickOrderField(obj, keys) {
    if (!obj) return "";
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (obj[key] != null && obj[key] !== "") return obj[key];
    }
    return "";
  }

  function isLoyaltyApiOk(json) {
    if (!json) return false;
    const code = json.code;
    return code === 200 || code === "200";
  }

  function normalizeOrderDetailPayload(data) {
    if (!data || typeof data !== "object" || Array.isArray(data)) return null;
    if (data.order && typeof data.order === "object") return data.order;
    if (data.orderDetail && typeof data.orderDetail === "object") {
      return data.orderDetail;
    }
    if (data.detail && typeof data.detail === "object") return data.detail;
    return data;
  }

  function mergeOrderDetailSources(listItem, detail) {
    const normalized = normalizeOrderDetailPayload(detail);
    const merged = Object.assign({}, listItem || {}, normalized || {});
    if (!normalized) return merged;

    const detailLines = getOrderLineItems(normalized);
    const listLines = getOrderLineItems(listItem || {});
    if (!detailLines.length && listLines.length) {
      merged.lineItems = listLines;
    }
    return merged;
  }

  function getOrderDetailRequestId(listItem) {
    const direct = pickOrderField(listItem, [
      "orderId",
      "id",
      "shopifyOrderId",
      "order_id",
      "legacyResourceId",
    ]);
    if (direct) return String(direct);
    const name = formatOrderName(listItem);
    return name && name !== "—" ? name : "";
  }

  function getOrderLineItems(detail) {
    if (!detail) return [];
    const raw =
      detail.lineItems ||
      detail.items ||
      detail.products ||
      detail.line_items ||
      detail.orderLineItems ||
      detail.productList ||
      [];
    return Array.isArray(raw) ? raw : [];
  }

  /** List page uses `amount`; detail uses `totalAmount`. */
  function getOrderItemTotalAmount(item) {
    return pickOrderField(item, ["totalAmount", "amount", "total"]);
  }

  /** Sum gift card amounts actually applied on this order. */
  function getOrderGiftCardUsedTotal(giftPayments, giftCardPaid) {
    let sum = 0;
    let hasLine = false;
    if (Array.isArray(giftPayments)) {
      giftPayments.forEach(function (item) {
        const amount = pickOrderField(item, ["amount"]);
        if (!hasOrderAdjustmentAmount(amount)) return;
        hasLine = true;
        const num = Number(amount);
        if (!Number.isNaN(num)) sum += Math.abs(num);
      });
    }
    if (hasLine) return sum;
    if (giftCardPaid == null || giftCardPaid === "") return 0;
    const paid = Number(giftCardPaid);
    return Number.isNaN(paid) ? 0 : Math.abs(paid);
  }

  /** Order total after discounts, including shipping and tax (before gift card). */
  function computeOrderGrossTotalFromParts(data) {
    const subtotal = pickOrderField(data, ["subtotalAmount", "subtotal"]);
    if (subtotal === "") return NaN;
    let sum = Number(subtotal);
    if (Number.isNaN(sum)) return NaN;
    const discount = pickOrderField(data, ["discountAmount"]);
    if (discount !== "") {
      const d = Number(discount);
      if (!Number.isNaN(d)) sum -= Math.abs(d);
    }
    const shipping = pickOrderField(data, [
      "shippingAmount",
      "shippingFee",
      "shippingPrice",
    ]);
    if (shipping !== "") {
      const s = Number(shipping);
      if (!Number.isNaN(s)) sum += s;
    }
    const tax = pickOrderField(data, ["taxAmount", "totalTax", "tax"]);
    if (tax !== "") {
      const t = Number(tax);
      if (!Number.isNaN(t)) sum += t;
    }
    return sum;
  }

  /**
   * Remaining payable = order gross total − gift card used (when API has no payable field).
   */
  function getOrderDetailPayableAmount(data, giftPayments, giftCardPaid) {
    const explicit = pickOrderField(data, [
      "payableAmount",
      "amountDue",
      "remainingPayable",
      "remainingAmount",
      "netPaymentAmount",
    ]);
    if (explicit !== "") return explicit;

    const giftUsed = getOrderGiftCardUsedTotal(giftPayments, giftCardPaid);
    const grossRaw = getOrderItemTotalAmount(data);
    if (giftUsed <= 0) return grossRaw;

    let gross = Number(grossRaw);
    if (Number.isNaN(gross)) {
      gross = computeOrderGrossTotalFromParts(data);
    }
    if (Number.isNaN(gross)) return grossRaw;

    const net = Math.max(0, gross - giftUsed);
    const apiTotal = Number(grossRaw);
    if (!Number.isNaN(apiTotal)) {
      if (Math.abs(apiTotal - gross) < 0.02) return net;
      return apiTotal;
    }
    return net;
  }

  /** Order total before gift card payment (subtotal − discounts + shipping + tax). */
  function getOrderDetailOrderTotalAmount(data, giftPayments, giftCardPaid) {
    const payable = getOrderDetailPayableAmount(
      data,
      giftPayments,
      giftCardPaid,
    );
    const giftUsed = getOrderGiftCardUsedTotal(giftPayments, giftCardPaid);
    const payNum = Number(payable);
    if (giftUsed > 0 && !Number.isNaN(payNum)) {
      return payNum + giftUsed;
    }
    const computed = computeOrderGrossTotalFromParts(data);
    if (!Number.isNaN(computed)) return computed;
    return getOrderItemTotalAmount(data);
  }

  function formatOrderAmount(value) {
    if (value == null || value === "") return "—";
    const num = Number(value);
    if (Number.isNaN(num)) {
      const raw = String(value).trim();
      return raw.charAt(0) === "$" ? raw : "$" + raw;
    }
    return (
      "$" +
      (num % 1 === 0
        ? numberFormatter.format(num)
        : num.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }))
    );
  }

  function formatOrderMoney(value) {
    if (value == null || value === "") return "—";
    const num = Number(value);
    if (Number.isNaN(num)) return formatOrderAmount(value);
    return (
      "$" +
      num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }

  function formatOrderMoneyDeduction(value) {
    if (value == null || value === "") return "—";
    const num = Number(value);
    if (Number.isNaN(num)) return formatOrderAmount(value);
    if (num === 0) return formatOrderMoney(0);
    return (
      "-$" +
      Math.abs(num).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }

  function formatOrderName(item) {
    const name = String((item && item.orderName) || "").trim();
    if (name) return name.replace(/^#/, "");
    const id = String((item && item.orderId) || "").trim();
    if (!id) return "—";
    const parts = id.split("/");
    return parts[parts.length - 1] || id;
  }

  function formatOrderDisplayName(item) {
    const name = formatOrderName(item);
    if (name === "—") return name;
    return name.charAt(0) === "#" ? name : "#" + name;
  }

  function getOrdersTbody() {
    return root.querySelector("[data-orders-tbody]");
  }

  function getOrdersListEl() {
    return root.querySelector("[data-orders-list]");
  }

  function setOrdersDisplay(tableHtml, listHtml) {
    const tbody = getOrdersTbody();
    const list = getOrdersListEl();
    if (tbody && tableHtml != null) tbody.innerHTML = tableHtml;
    if (list && listHtml != null) list.innerHTML = listHtml;
  }

  function renderOrdersListMessageHtml(message, isError) {
    if (!isError && message === t("orders.no_orders")) {
      return getOrdersEmptyHtml();
    }
    return (
      '<p class="orders-list__msg' +
      (isError ? " orders-list__msg--error" : "") +
      '">' +
      escapeHtml(message) +
      "</p>"
    );
  }

  function getOrderListImageUrl(item) {
    const raw = pickOrderField(item, [
      "imageUrl",
      "image",
      "src",
      "featuredImage",
    ]);
    if (!raw) return "";
    if (typeof raw === "string") return raw;
    if (raw && typeof raw === "object") {
      return raw.src || raw.url || raw.imageUrl || "";
    }
    return "";
  }

  function buildOrdersListThumbHtml(item, options) {
    options = options || {};
    const size = options.size || 40;
    const className = options.className || "orders-table__order-thumb";
    const src = getOrderListImageUrl(item);
    return (
      '<span class="' +
      className +
      '" aria-hidden="true">' +
      (src
        ? '<img src="' +
          escapeHtml(src) +
          '" alt="" width="' +
          size +
          '" height="' +
          size +
          '">'
        : "") +
      "</span>"
    );
  }

  function buildOrdersCardHtml(item, index) {
    return (
      '<article class="orders-card">' +
      '<div class="orders-card__top">' +
      '<div class="orders-card__info">' +
      buildOrdersListThumbHtml(item, {
        size: 48,
        className: "orders-card__thumb",
      }) +
      '<div class="orders-card__meta">' +
      '<p class="orders-card__id">' +
      escapeHtml(formatOrderDisplayName(item)) +
      "</p>" +
      '<p class="orders-card__date">' +
      escapeHtml(formatOrderDate(item.date)) +
      "</p>" +
      "</div></div>" +
      '<div class="orders-card__status">' +
      getOrderStatusBadgeHtml(item.status) +
      "</div></div>" +
      '<div class="orders-card__divider" aria-hidden="true"></div>' +
      '<div class="orders-card__foot">' +
      '<p class="orders-card__total">' +
      '<span class="orders-card__total-label">' +
      escapeHtml(t("orders.total")) +
      "</span> " +
      '<span class="orders-card__total-amount">' +
      escapeHtml(formatOrderMoney(getOrderItemTotalAmount(item))) +
      "</span></p>" +
      '<button type="button" class="orders-card__details" data-orders-open-detail data-order-index="' +
      escapeHtml(String(index)) +
      '">' +
      escapeHtml(t("orders.view_details")) +
      "</button></div></article>"
    );
  }

  function getOrderStatusBadgeHtml(status) {
    const key = normalizeOrderStatusKey(status) || "processing";
    const label =
      getOrdersStatusLabel(key) || String(status || t("common.dash"));
    const mod =
      key === "delivered" ||
      key === "processing" ||
      key === "shipped" ||
      key === "refunded" ||
      key === "cancelled"
        ? key
        : "processing";
    return (
      '<span class="orders-status orders-status--' +
      mod +
      '">' +
      '<span class="orders-status__dot" aria-hidden="true"></span>' +
      '<span class="orders-status__label">' +
      escapeHtml(label) +
      "</span></span>"
    );
  }

  function renderOverviewOrderCounts(data) {
    const keys = ["all", "processing", "shipped"];
    keys.forEach(function (key) {
      const el = root.querySelector(
        '[data-overview-order-count="' + key + '"]',
      );
      if (!el) return;
      const value = data && data[key] != null ? Number(data[key]) || 0 : 0;
      animateCountUp(el, value);
    });
  }

  function renderOrdersTabCounts(data) {
    ORDERS_TAB_KEYS.forEach(function (key) {
      const el = root.querySelector('[data-orders-tab-count="' + key + '"]');
      if (!el) return;
      const value = data && data[key] != null ? Number(data[key]) || 0 : 0;
      el.textContent = "(" + numberFormatter.format(value) + ")";
    });
  }

  function getCachedOrdersTabCount(statusKey) {
    if (!orderTabCountsCache || !isValidOrdersSubtab(statusKey)) return null;
    if (
      orderTabCountsCache[statusKey] == null ||
      orderTabCountsCache[statusKey] === ""
    ) {
      return null;
    }
    const value = Number(orderTabCountsCache[statusKey]);
    return Number.isFinite(value) ? Math.max(0, value) : null;
  }

  function applyOrdersTotalFromTabCount(statusKey) {
    const tabCount = getCachedOrdersTabCount(statusKey || ordersActiveSubtab);
    if (tabCount == null) return false;
    ordersTotal = tabCount;
    ordersTotalReady = true;
    if (
      ordersPageSize > 0 &&
      ordersTotal <= ordersPageSize &&
      ordersPageIndex === 0
    ) {
      ordersHasNextPage = false;
    }
    updateOrdersPaginationUi();
    return true;
  }

  function reconcileOrdersListTotal(statusKey, pageTotal) {
    const tabCount = getCachedOrdersTabCount(statusKey);
    if (tabCount != null) {
      // Tab counts above are the list quantity for each status.
      ordersTotal = tabCount;
      ordersTotalReady = true;
    } else if (pageTotal != null) {
      ordersTotal = Math.max(0, Number(pageTotal) || 0);
      ordersTotalReady = true;
    }
    if (
      ordersTotalReady &&
      ordersPageSize > 0 &&
      ordersTotal <= ordersPageSize &&
      ordersPageIndex === 0
    ) {
      ordersHasNextPage = false;
    }
  }

  function getOrdersTotalPages() {
    if (ordersTotal > 0 && ordersPageSize > 0) {
      return Math.max(1, Math.ceil(ordersTotal / ordersPageSize));
    }
    return Math.max(ordersCursorStack.length + (ordersHasNextPage ? 1 : 0), 1);
  }

  function buildOrdersApiUrl(path, params) {
    const baseUrl = getLoyaltyBaseUrl().replace(/\/$/, "");
    const qs = [];
    Object.keys(params || {}).forEach(function (key) {
      const value = params[key];
      if (value == null || value === "") return;
      qs.push(encodeURIComponent(key) + "=" + encodeURIComponent(value));
    });
    return baseUrl + path + (qs.length ? "?" + qs.join("&") : "");
  }

  function fetchOrderTabCounts(force) {
    if (!force && orderTabCountsCache) {
      renderOverviewOrderCounts(orderTabCountsCache);
      renderOrdersTabCounts(orderTabCountsCache);
      applyOrdersTotalFromTabCount(ordersActiveSubtab);
      return Promise.resolve(orderTabCountsCache);
    }

    const baseUrl = getLoyaltyBaseUrl();
    const shopifyId = getGiftCardShopifyId();
    if (!baseUrl || !shopifyId) {
      return Promise.resolve(null);
    }

    if (!force && orderTabCountsPromise) {
      return orderTabCountsPromise;
    }

    if (force) {
      orderTabCountsCache = null;
      orderTabCountsPromise = null;
    }

    const requestId = ++orderTabCountsRequestId;
    orderTabCountsPromise = fetch(
      buildOrdersApiUrl("/front/customer/orders/tab-counts", {
        shopifyId: shopifyId,
      }),
      {
        method: "GET",
        headers: { Accept: "application/json" },
      },
    )
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        if (json && json.code === 200 && json.data) {
          return json;
        }
        // Fallback: older POST contract used by overview
        return fetch(
          getLoyaltyBaseUrl().replace(/\/$/, "") +
            "/front/customer/orders/tab-counts",
          {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ shopifyId: shopifyId }),
          },
        ).then(function (res) {
          return res.json();
        });
      })
      .then(function (json) {
        if (requestId !== orderTabCountsRequestId) {
          return orderTabCountsCache;
        }
        if (json && json.code === 200 && json.data) {
          orderTabCountsCache = json.data;
          renderOverviewOrderCounts(orderTabCountsCache);
          renderOrdersTabCounts(orderTabCountsCache);
          applyOrdersTotalFromTabCount(ordersActiveSubtab);
          return orderTabCountsCache;
        }
        return null;
      })
      .catch(function () {
        return null;
      })
      .finally(function () {
        if (requestId === orderTabCountsRequestId) {
          orderTabCountsPromise = null;
        }
      });

    return orderTabCountsPromise;
  }

  function updateOrdersPaginationUi() {
    const wrap = root.querySelector("[data-orders-pagination]");
    const dots = root.querySelector("[data-orders-page-dots]");
    const prevBtn = root.querySelector("[data-orders-prev]");
    const nextBtn = root.querySelector("[data-orders-next]");
    if (!wrap || !dots || !prevBtn || !nextBtn) return;

    const hasRows = ordersCurrentItemCount > 0;
    const pageCount = getOrdersTotalPages();
    const firstPageNotFull =
      ordersPageIndex === 0 &&
      ordersPageSize > 0 &&
      ordersCurrentItemCount > 0 &&
      ordersCurrentItemCount < ordersPageSize;
    const totalFitsOnePage =
      ordersTotalReady &&
      ordersPageSize > 0 &&
      ordersTotal <= ordersPageSize &&
      ordersPageIndex === 0;
    const showPager =
      hasRows &&
      !firstPageNotFull &&
      !totalFitsOnePage &&
      (pageCount > 1 || ordersHasNextPage || ordersPageIndex > 0);
    wrap.hidden = !showPager;
    if (!showPager) {
      dots.innerHTML = "";
      prevBtn.disabled = true;
      nextBtn.disabled = true;
      return;
    }

    let html = "";
    const maxDots = Math.min(pageCount, 5);
    // Keep active dot in visible window when there are more than 5 pages
    let start = 0;
    if (pageCount > maxDots) {
      start = Math.min(
        Math.max(0, ordersPageIndex - Math.floor(maxDots / 2)),
        pageCount - maxDots,
      );
    }
    for (let i = 0; i < maxDots; i++) {
      const pageIdx = start + i;
      const isActive = pageIdx === ordersPageIndex;
      html +=
        '<span class="orders-pagination__dot' +
        (isActive ? " is-active" : "") +
        '" data-orders-page-index="' +
        pageIdx +
        '"></span>';
    }
    dots.innerHTML = html;

    prevBtn.disabled = ordersLoading || ordersPageIndex <= 0;
    nextBtn.disabled =
      ordersLoading ||
      !(
        ordersHasNextPage ||
        ordersPageIndex + 1 < pageCount ||
        !!ordersCursorStack[ordersPageIndex + 1]
      );
  }

  function getOrdersShopUrl() {
    const panel = root.querySelector('[data-points-center-panel="my-orders"]');
    return panel
      ? String(panel.getAttribute("data-orders-shop-url") || "").trim()
      : "";
  }

  const ORDERS_EMPTY_ICON_SVG =
    '<svg class="orders-table__empty-icon-svg" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M34 28H74L88 42V92C88 94.2091 86.2091 96 84 96H34C31.7909 96 30 94.2091 30 92V32C30 29.7909 31.7909 28 34 28Z" fill="#EDF5FE" stroke="#B9D9FA" stroke-width="1.5"/>' +
    '<path d="M74 28V42H88" stroke="#B9D9FA" stroke-width="1.5" stroke-linejoin="round"/>' +
    '<path d="M40 54H78" stroke="#A7D0F9" stroke-width="2" stroke-linecap="round"/>' +
    '<path d="M40 64H70" stroke="#A7D0F9" stroke-width="2" stroke-linecap="round"/>' +
    '<path d="M40 74H62" stroke="#A7D0F9" stroke-width="2" stroke-linecap="round"/>' +
    '<circle cx="78" cy="78" r="16" fill="#DCEDFD" stroke="#B9D9FA" stroke-width="1.5"/>' +
    '<path d="M73 78H83" stroke="#A7D0F9" stroke-width="2" stroke-linecap="round"/>' +
    '<path d="M78 73V83" stroke="#A7D0F9" stroke-width="2" stroke-linecap="round"/>' +
    '<path opacity="0.35" d="M18 86C18 86 24 78 34 78C44 78 48 88 58 88C68 88 72 80 82 80C92 80 98 88 98 88" stroke="#A7D0F9" stroke-width="2" stroke-linecap="round"/>' +
    "</svg>";

  function getOrdersEmptyHtml() {
    const shopUrl = getOrdersShopUrl();
    return (
      '<div class="orders-table__empty-state">' +
      '<div class="orders-table__empty-copy">' +
      '<div class="orders-table__empty-icon" aria-hidden="true">' +
      ORDERS_EMPTY_ICON_SVG +
      "</div>" +
      '<p class="orders-table__empty-text">' +
      escapeHtml(t("orders.no_orders")) +
      "</p>" +
      "</div>" +
      (shopUrl
        ? '<a class="orders-table__empty-btn" href="' +
          escapeHtml(shopUrl) +
          '">' +
          escapeHtml(t("orders.shop_now")) +
          "</a>"
        : "") +
      "</div>"
    );
  }

  function renderOrdersEmptyRow(message, isError) {
    if (!isError && message === t("orders.no_orders")) {
      return (
        '<tr class="orders-table__empty-row"><td colspan="4" class="orders-table__empty">' +
        getOrdersEmptyHtml() +
        "</td></tr>"
      );
    }
    return (
      '<tr><td colspan="4" class="orders-table__empty' +
      (isError ? " orders-table__empty--error" : "") +
      '"><span class="orders-table__empty-msg">' +
      escapeHtml(message) +
      "</span></td></tr>"
    );
  }

  function renderOrdersStatusContent(message, isError) {
    setOrdersDisplay(
      renderOrdersEmptyRow(message, isError),
      renderOrdersListMessageHtml(message, isError),
    );
  }

  function renderOrdersRows(items) {
    const tbody = getOrdersTbody();
    if (!tbody) return;

    const list = Array.isArray(items) ? items : [];
    ordersCurrentItemCount = list.length;
    ordersCurrentItems = list.slice();

    if (!list.length) {
      renderOrdersStatusContent(t("orders.no_orders"));
      return;
    }

    const tableHtml = list
      .map(function (item, index) {
        return (
          "<tr>" +
          '<td class="orders-table__content">' +
          '<button type="button" class="orders-table__order-name" data-orders-open-detail data-order-index="' +
          escapeHtml(String(index)) +
          '">' +
          buildOrdersListThumbHtml(item) +
          '<span class="orders-table__order-id">' +
          escapeHtml(formatOrderName(item)) +
          "</span></button></td>" +
          '<td class="orders-table__date">' +
          escapeHtml(formatOrderDate(item.date)) +
          "</td>" +
          '<td class="orders-table__status">' +
          getOrderStatusBadgeHtml(item.status) +
          "</td>" +
          '<td class="orders-table__amount">' +
          escapeHtml(formatOrderMoney(getOrderItemTotalAmount(item))) +
          "</td></tr>"
        );
      })
      .join("");

    const listHtml = list
      .map(function (item, index) {
        return buildOrdersCardHtml(item, index);
      })
      .join("");

    setOrdersDisplay(tableHtml, listHtml);
  }

  function buildOrdersDetailRow(label, valueHtml, options) {
    options = options || {};
    const labelHtml = options.htmlLabel ? label : escapeHtml(label);
    return (
      '<div class="orders-detail-modal__row' +
      (options.multi ? " orders-detail-modal__row--multi" : "") +
      (options.emphasis ? " orders-detail-modal__row--emphasis" : "") +
      '">' +
      '<div class="orders-detail-modal__label">' +
      labelHtml +
      "</div>" +
      '<div class="orders-detail-modal__value">' +
      valueHtml +
      "</div></div>"
    );
  }

  function buildOrdersDetailFeeGroupRow(label, valueHtml, breakdownHtml) {
    if (!breakdownHtml) {
      return buildOrdersDetailRow(label, valueHtml);
    }
    return (
      '<div class="orders-detail-modal__row orders-detail-modal__row--fee-group">' +
      '<div class="orders-detail-modal__label">' +
      escapeHtml(label) +
      "</div>" +
      '<div class="orders-detail-modal__value">' +
      valueHtml +
      "</div>" +
      '<div class="orders-detail-modal__fee-breakdown">' +
      breakdownHtml +
      "</div></div>"
    );
  }

  function buildOrdersDetailValueWithList(primaryHtml, listHtml) {
    if (!listHtml) return primaryHtml;
    return (
      '<div class="orders-detail-modal__value-stack">' +
      '<div class="orders-detail-modal__value-primary">' +
      primaryHtml +
      "</div>" +
      listHtml +
      "</div>"
    );
  }

  const CHECKOUT_PILL_DISCOUNT_ICON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" fill="none" aria-hidden="true">' +
    '<g stroke="currentColor" stroke-width="1" stroke-linejoin="round">' +
    '<path stroke-linecap="round" d="M10.5 1.5H7.93a2.5 2.5 0 0 0-1.8.766L1.52 7.052a1.5 1.5 0 0 0 .02 2.101l3.48 3.48a1.25 1.25 0 0 0 1.75.016l5.116-4.926a2 2 0 0 0 .613-1.441V3.5a2 2 0 0 0-2-2"/>' +
    '<circle cx="9.5" cy="4.5" r=".563" stroke-linecap="round"/></g></svg>';

  const CHECKOUT_PILL_GIFT_CARD_ICON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" fill="none" aria-hidden="true">' +
    '<g stroke="currentColor" stroke-width="1">' +
    '<path stroke-linecap="round" stroke-linejoin="round" d="M7 8v4.5"/>' +
    '<path stroke-linejoin="round" d="M1.5 5.755c0-.693.562-1.255 1.255-1.255h8.49c.693 0 1.255.562 1.255 1.255v.99C12.5 7.438 11.938 8 11.245 8h-8.49A1.255 1.255 0 0 1 1.5 6.745zM2.5 8h9v2.75a1.75 1.75 0 0 1-1.75 1.75h-5.5a1.75 1.75 0 0 1-1.75-1.75z"/>' +
    '<path stroke-linejoin="round" d="M4.524 4.017c.566.58 2.41.499 2.41.499s.08-1.888-.486-2.468c-.566-.579-1.455-.607-1.986-.064-.531.544-.503 1.454.062 2.033m4.952 0c-.566.58-2.41.499-2.41.499s-.08-1.888.486-2.468c.566-.579 1.455-.607 1.986-.064.531.544.503 1.454-.062 2.033"/></g></svg>';

  function isOrdersDetailGiftCodeLabel(label) {
    const text = String(label || "");
    return (
      text.indexOf("•") >= 0 ||
      text.indexOf("····") >= 0 ||
      text.indexOf("****") >= 0
    );
  }

  function buildOrdersDetailFeePillIconHtml(kind, label) {
    const isGift = kind === "gift" || isOrdersDetailGiftCodeLabel(label);
    return isGift
      ? CHECKOUT_PILL_GIFT_CARD_ICON_SVG
      : CHECKOUT_PILL_DISCOUNT_ICON_SVG;
  }

  function buildOrdersDetailFeePillHtml(kind, codeText) {
    const text = codeText != null ? String(codeText).trim() : "";
    const isGift = kind === "gift";
    const display =
      text ||
      (isGift ? t("orders.gift_card_item") : t("orders.discount_item"));
    return (
      '<span class="orders-detail-modal__fee-pill' +
      (isGift ? " orders-detail-modal__fee-pill--gift" : "") +
      '">' +
      '<span class="orders-detail-modal__fee-pill-icon" aria-hidden="true">' +
      buildOrdersDetailFeePillIconHtml(kind, text) +
      "</span>" +
      '<span class="orders-detail-modal__fee-pill-code">' +
      escapeHtml(display) +
      "</span></span>"
    );
  }

  function formatOrderAdjustmentCodeSuffix(code, kind) {
    const raw = String(code || "").trim();
    if (!raw) return "";
    if (kind === "gift") {
      if (isOrdersDetailGiftCodeLabel(raw)) return raw;
      if (raw.length <= 4) return "****" + raw;
      return "****" + raw.slice(-4);
    }
    return raw;
  }

  function buildOrdersDetailAdjustmentLabel(kind, code) {
    const suffix = formatOrderAdjustmentCodeSuffix(code, kind);
    if (suffix) return suffix;
    return kind === "gift"
      ? t("orders.gift_card_item")
      : t("orders.discount");
  }

  function hasOrderAdjustmentAmount(amount) {
    if (amount == null || amount === "") return false;
    const num = Number(amount);
    if (!Number.isNaN(num)) return num !== 0;
    const text = String(amount).trim();
    return text !== "" && text !== "—" && text !== "-";
  }

  function buildOrdersDetailAdjustmentRow(kind, code, amount) {
    const icon =
      kind === "gift"
        ? CHECKOUT_PILL_GIFT_CARD_ICON_SVG
        : CHECKOUT_PILL_DISCOUNT_ICON_SVG;
    return (
      '<div class="orders-detail-modal__adjustment-row">' +
      '<span class="orders-detail-modal__adjustment-label">' +
      '<span class="orders-detail-modal__adjustment-icon" aria-hidden="true">' +
      icon +
      "</span>" +
      '<span class="orders-detail-modal__adjustment-text">' +
      escapeHtml(buildOrdersDetailAdjustmentLabel(kind, code)) +
      "</span></span>" +
      '<span class="orders-detail-modal__adjustment-amount">' +
      escapeHtml(formatOrderMoneyDeduction(amount)) +
      "</span></div>"
    );
  }

  function buildOrdersDetailSubtotalAdjustmentsHtml(
    discounts,
    giftPayments,
    discountAmount,
    giftCardPaid,
  ) {
    const parts = [];
    discounts.forEach(function (item) {
      const code = pickOrderField(item, ["code", "title", "name"]);
      const amount = pickOrderField(item, ["allocatedAmount", "amount"]);
      if (!hasOrderAdjustmentAmount(amount)) return;
      parts.push(buildOrdersDetailAdjustmentRow("discount", code, amount));
    });
    giftPayments.forEach(function (item) {
      const code = pickOrderField(item, ["code"]);
      const amount = pickOrderField(item, ["amount"]);
      if (!hasOrderAdjustmentAmount(amount)) return;
      parts.push(buildOrdersDetailAdjustmentRow("gift", code, amount));
    });
    const giftPaidNum =
      giftCardPaid != null && giftCardPaid !== ""
        ? Number(giftCardPaid)
        : NaN;
    if (
      !giftPayments.length &&
      !Number.isNaN(giftPaidNum) &&
      giftPaidNum !== 0
    ) {
      parts.push(
        buildOrdersDetailAdjustmentRow("gift", "", giftCardPaid),
      );
    }
    const discountNum =
      discountAmount !== "" ? Number(discountAmount) : NaN;
    if (
      !discounts.length &&
      discountAmount !== "" &&
      !Number.isNaN(discountNum) &&
      discountNum !== 0
    ) {
      parts.push(
        buildOrdersDetailAdjustmentRow("discount", "", discountAmount),
      );
    }
    if (!parts.length) return "";
    return (
      '<div class="orders-detail-modal__fee-adjustments">' +
      parts.join("") +
      "</div>"
    );
  }

  function buildOrdersDetailFeeBreakdownListHtml(
    items,
    labelKeys,
    amountKeys,
    formatAmount,
    defaultLabelKey,
    listOptions,
  ) {
    if (!items || !items.length) return "";
    listOptions = listOptions || {};
    const pillKind = listOptions.pillKind || "";
    const usePills = pillKind === "discount" || pillKind === "gift";
    return (
      '<ul class="orders-detail-modal__fee-list orders-detail-modal__fee-list--sub' +
      (usePills ? " orders-detail-modal__fee-list--pills" : "") +
      '">' +
      items
        .map(function (item) {
          const label =
            pickOrderField(item, labelKeys) ||
            (defaultLabelKey ? t(defaultLabelKey) : t("common.dash"));
          const amount = pickOrderField(item, amountKeys);
          const labelText = String(label);
          const isGiftPill =
            pillKind === "gift" || isOrdersDetailGiftCodeLabel(labelText);
          if (usePills) {
            return (
              '<li class="orders-detail-modal__fee-list-item">' +
              '<span class="orders-detail-modal__fee-pill' +
              (isGiftPill ? " orders-detail-modal__fee-pill--gift" : "") +
              '">' +
              '<span class="orders-detail-modal__fee-pill-icon" aria-hidden="true">' +
              buildOrdersDetailFeePillIconHtml(pillKind, labelText) +
              "</span>" +
              '<span class="orders-detail-modal__fee-pill-code">' +
              escapeHtml(labelText) +
              "</span>" +
              '<span class="orders-detail-modal__fee-pill-amount">' +
              escapeHtml(formatAmount(amount)) +
              "</span></span></li>"
            );
          }
          return (
            '<li class="orders-detail-modal__fee-list-item">' +
            '<span class="orders-detail-modal__fee-list-label">' +
            escapeHtml(labelText) +
            "</span>" +
            '<span class="orders-detail-modal__fee-list-amount">' +
            escapeHtml(formatAmount(amount)) +
            "</span></li>"
          );
        })
        .join("") +
      "</ul>"
    );
  }

  function buildOrdersDetailFeeSubRow(labelHtml, valueHtml) {
    return (
      '<div class="orders-detail-modal__row orders-detail-modal__row--fee-sub">' +
      '<div class="orders-detail-modal__label">' +
      labelHtml +
      "</div>" +
      '<div class="orders-detail-modal__value">' +
      valueHtml +
      "</div></div>"
    );
  }

  function buildOrdersDetailDiscountBlockHtml(discounts, discountAmount) {
    const lines = [];
    if (Array.isArray(discounts)) {
      discounts.forEach(function (item) {
        const code = pickOrderField(item, ["code", "title", "name"]);
        const amount = pickOrderField(item, ["allocatedAmount", "amount"]);
        if (!hasOrderAdjustmentAmount(amount)) return;
        lines.push(
          buildOrdersDetailFeeSubRow(
            buildOrdersDetailFeePillHtml("discount", code ? String(code) : ""),
            escapeHtml(formatOrderMoneyDeduction(amount)),
          ),
        );
      });
    }
    const discountNum =
      discountAmount !== "" ? Number(discountAmount) : NaN;
    if (
      !lines.length &&
      discountAmount !== "" &&
      !Number.isNaN(discountNum) &&
      discountNum !== 0
    ) {
      lines.push(
        buildOrdersDetailFeeSubRow(
          buildOrdersDetailFeePillHtml("discount", ""),
          escapeHtml(formatOrderMoneyDeduction(discountAmount)),
        ),
      );
    }
    if (!lines.length) return "";
    return (
      '<div class="orders-detail-modal__discount-group">' +
      '<div class="orders-detail-modal__row orders-detail-modal__row--discount-header">' +
      '<div class="orders-detail-modal__label">' +
      escapeHtml(t("orders.discounts")) +
      "</div>" +
      '<div class="orders-detail-modal__value" aria-hidden="true"></div></div>' +
      lines.join("") +
      "</div>"
    );
  }

  function buildOrdersDetailGiftPaymentLabelHtml(code) {
    const suffix = formatOrderAdjustmentCodeSuffix(code, "gift");
    return (
      '<span class="orders-detail-modal__fee-payment-label">' +
      '<span class="orders-detail-modal__fee-payment-prefix">' +
      escapeHtml(t("orders.paid_by_gift_card")) +
      "</span>" +
      buildOrdersDetailFeePillHtml("gift", suffix) +
      "</span>"
    );
  }

  function buildOrdersDetailGiftPaymentRowsHtml(giftPayments, giftCardPaid) {
    const rows = [];
    if (Array.isArray(giftPayments)) {
      giftPayments.forEach(function (item) {
        const code = pickOrderField(item, ["code"]);
        const amount = pickOrderField(item, ["amount"]);
        if (!hasOrderAdjustmentAmount(amount)) return;
        rows.push(
          buildOrdersDetailRow(
            buildOrdersDetailGiftPaymentLabelHtml(code),
            escapeHtml(formatOrderMoneyDeduction(amount)),
            { htmlLabel: true },
          ),
        );
      });
    }
    if (rows.length) return rows.join("");
    const giftPaidNum =
      giftCardPaid != null && giftCardPaid !== ""
        ? Number(giftCardPaid)
        : NaN;
    if (!Number.isNaN(giftPaidNum) && giftPaidNum !== 0) {
      rows.push(
        buildOrdersDetailRow(
          buildOrdersDetailGiftPaymentLabelHtml(""),
          escapeHtml(formatOrderMoneyDeduction(giftCardPaid)),
          { htmlLabel: true },
        ),
      );
    }
    return rows.join("");
  }

  function buildOrdersDetailFeesHtml(data) {
    const subtotal = pickOrderField(data, ["subtotalAmount", "subtotal"]);
    const discountAmount = pickOrderField(data, ["discountAmount"]);
    const discounts = Array.isArray(data.discounts) ? data.discounts : [];
    const shippingAmount = pickOrderField(data, [
      "shippingAmount",
      "shippingFee",
      "shippingPrice",
    ]);
    const taxAmount = pickOrderField(data, ["taxAmount", "totalTax", "tax"]);
    const giftCardPaid = data.giftCardPaidAmount;
    const giftPayments = Array.isArray(data.giftCardPayments)
      ? data.giftCardPayments
      : [];
    const orderTotal = getOrderDetailOrderTotalAmount(
      data,
      giftPayments,
      giftCardPaid,
    );
    const payableAmount = getOrderDetailPayableAmount(
      data,
      giftPayments,
      giftCardPaid,
    );
    const giftUsed = getOrderGiftCardUsedTotal(giftPayments, giftCardPaid);

    let html =
      '<div class="orders-detail-modal__section orders-detail-modal__fees">';

    if (subtotal !== "") {
      html += buildOrdersDetailRow(
        t("orders.subtotal"),
        escapeHtml(formatOrderMoney(subtotal)),
      );
    }

    const discountBlockHtml = buildOrdersDetailDiscountBlockHtml(
      discounts,
      discountAmount,
    );
    if (discountBlockHtml) {
      html += discountBlockHtml;
    }

    html += buildOrdersDetailRow(
      t("orders.shipping_fee"),
      escapeHtml(formatOrderMoney(shippingAmount)),
    );
    html += buildOrdersDetailRow(
      t("orders.tax"),
      escapeHtml(formatOrderMoney(taxAmount)),
    );

    html +=
      '<div class="orders-detail-modal__fees-divider" aria-hidden="true"></div>';

    html += buildOrdersDetailRow(
      t("orders.payment_amount"),
      '<span class="orders-detail-modal__total-amount">' +
        escapeHtml(formatOrderMoney(orderTotal)) +
        "</span>",
      { emphasis: true },
    );

    if (giftUsed > 0) {
      html += buildOrdersDetailGiftPaymentRowsHtml(
        giftPayments,
        giftCardPaid,
      );
      html += buildOrdersDetailRow(
        t("orders.paid_by_credit_card"),
        escapeHtml(formatOrderMoney(payableAmount)),
      );
    }

    html += "</div>";
    return html;
  }

  function buildOrdersDetailProductHtml(line, options) {
    options = options || {};
    const title =
      pickOrderField(line, ["title", "name", "productTitle", "productName"]) ||
      "—";
    const spec = pickOrderField(line, [
      "variantTitle",
      "sku",
      "spec",
      "dimensions",
      "subtitle",
    ]);
    const image = pickOrderField(line, [
      "image",
      "imageUrl",
      "src",
      "featuredImage",
    ]);
    const qtyRaw = pickOrderField(line, ["quantity", "qty"]);
    const qtyNum = qtyRaw === "" ? "" : Number(qtyRaw);
    const qtyLabel =
      qtyRaw === ""
        ? "—"
        : Number.isNaN(qtyNum)
          ? String(qtyRaw) + "x"
          : numberFormatter.format(qtyNum) + "x";
    const price = formatOrderAmount(
      pickOrderField(line, ["price", "amount", "linePrice", "originalPrice"]),
    );

    return (
      '<div class="orders-detail-modal__product' +
      (options.expandable ? " orders-detail-modal__product--expandable" : "") +
      '">' +
      '<div class="orders-detail-modal__product-media">' +
      (image
        ? '<img class="orders-detail-modal__product-img" src="' +
          escapeHtml(String(image)) +
          '" alt="" width="128" height="128" loading="lazy">'
        : '<div class="orders-detail-modal__product-placeholder" aria-hidden="true"></div>') +
      "</div>" +
      '<div class="orders-detail-modal__product-main">' +
      '<p class="orders-detail-modal__product-title">' +
      escapeHtml(title) +
      "</p>" +
      (spec
        ? '<p class="orders-detail-modal__product-spec">' +
          escapeHtml(String(spec)) +
          "</p>"
        : "") +
      "</div>" +
      '<div class="orders-detail-modal__product-divider" aria-hidden="true"></div>' +
      '<div class="orders-detail-modal__product-meta">' +
      '<p class="orders-detail-modal__product-price">' +
      escapeHtml(price) +
      "</p>" +
      '<p class="orders-detail-modal__product-qty">' +
      escapeHtml(qtyLabel) +
      "</p>" +
      "</div>" +
      (options.viewMore
        ? '<button type="button" class="orders-detail-modal__view-toggle" data-orders-detail-view-more>' +
          escapeHtml(t("common.view_more")) +
          "</button>"
        : "") +
      "</div>"
    );
  }

  function buildOrdersDetailProductsHtml(lineItems) {
    if (!lineItems.length) {
      return (
        '<div class="orders-detail-modal__products-empty">' +
        escapeHtml(t("orders.no_products")) +
        "</div>"
      );
    }

    const hasMore = lineItems.length > 1;
    let html =
      '<div class="orders-detail-modal__product-preview">' +
      buildOrdersDetailProductHtml(lineItems[0], {
        expandable: hasMore,
        viewMore: hasMore,
      }) +
      "</div>";

    if (hasMore) {
      html +=
        '<div class="orders-detail-modal__products-more" hidden data-orders-detail-products-extra aria-hidden="true">' +
        lineItems.slice(1).map(buildOrdersDetailProductHtml).join("") +
        "</div>" +
        '<button type="button" class="orders-detail-modal__view-toggle orders-detail-modal__view-toggle--collapse" hidden data-orders-detail-view-less>' +
        escapeHtml(t("common.view_less")) +
        "</button>";
    }

    return html;
  }

  function setOrdersDetailProductsExpanded(productsRoot, expanded) {
    if (!productsRoot) return;
    const extra = productsRoot.querySelector(
      "[data-orders-detail-products-extra]",
    );
    const viewMore = productsRoot.querySelector(
      "[data-orders-detail-view-more]",
    );
    const viewLess = productsRoot.querySelector(
      "[data-orders-detail-view-less]",
    );
    productsRoot.classList.toggle("is-expanded", expanded);
    if (extra) {
      extra.hidden = !expanded;
      extra.setAttribute("aria-hidden", expanded ? "false" : "true");
    }
    if (viewMore) viewMore.hidden = expanded;
    if (viewLess) viewLess.hidden = !expanded;
  }

  function renderOrdersDetailModalContent(detail, listItem) {
    const body = root.querySelector("[data-orders-detail-body]");
    if (!body) return;

    try {
      renderOrdersDetailModalContentInner(detail, listItem, body);
    } catch (error) {
      console.error("[points-center] order detail render failed", error);
      body.innerHTML =
        '<div class="orders-detail-modal__loading orders-detail-modal__loading--error" role="alert">' +
        escapeHtml(t("orders.failed_load")) +
        "</div>";
    }
  }

  function renderOrdersDetailModalContentInner(detail, listItem, body) {
    const data = mergeOrderDetailSources(listItem, detail);
    const orderName = formatOrderName(data);
    const status = pickOrderField(data, ["status", "fulfillmentStatus"]) || "";
    const createdRaw = pickOrderField(data, [
      "createdAt",
      "date",
      "processedAt",
      "orderDate",
    ]);
    const source = pickOrderField(data, ["source", "sourceName", "channel"]);
    const subtitleParts = [];
    const dt = formatOrderDateTime(createdRaw);
    if (dt) subtitleParts.push(dt);
    if (source) subtitleParts.push(t("orders.from_source", { source: source }));
    else if (dt) subtitleParts.push(t("orders.from_create"));

    const paymentStatus = pickOrderField(data, [
      "paymentStatus",
      "financialStatus",
      "payment",
    ]);
    const paymentMethod = pickOrderField(data, [
      "paymentMethod",
      "gateway",
      "paymentGateway",
    ]);
    const shippingStatus = pickOrderField(data, [
      "shippingStatus",
      "fulfillmentStatus",
      "deliveryStatus",
    ]);
    const estimatedShip = pickOrderField(data, [
      "estimatedShippingDate",
      "estimatedDeliveryDate",
      "eta",
    ]);
    const tracking = pickOrderField(data, [
      "trackingNumber",
      "trackingNo",
      "tracking",
    ]);
    let customerName = pickOrderField(data, ["customerName"]);
    if (!customerName && data.customer && typeof data.customer === "object") {
      customerName = pickOrderField(data.customer, [
        "displayName",
        "name",
        "firstName",
      ]);
    }
    const email =
      pickOrderField(data, ["email", "customerEmail"]) ||
      (data.customer && typeof data.customer === "object"
        ? pickOrderField(data.customer, ["email"])
        : "");
    const phone =
      pickOrderField(data, ["phone", "customerPhone"]) ||
      (data.customer && typeof data.customer === "object"
        ? pickOrderField(data.customer, ["phone"])
        : "");
    const fromAddr =
      data.from ||
      data.shippingFrom ||
      data.originAddress ||
      data.fromAddress ||
      null;
    const toAddr =
      data.to ||
      data.shippingAddress ||
      data.destinationAddress ||
      data.toAddress ||
      null;

    const lineItems = getOrderLineItems(data);
    const productsHtml = buildOrdersDetailProductsHtml(lineItems);
    const feesHtml = buildOrdersDetailFeesHtml(data);

    const paymentValueHtml = paymentStatus
      ? '<span class="orders-detail-modal__pill orders-detail-modal__pill--paid">' +
        escapeHtml(String(paymentStatus)) +
        "</span>"
      : "—";

    const emailHtml = email
      ? '<a class="orders-detail-modal__email" href="mailto:' +
        escapeHtml(String(email)) +
        '">' +
        escapeHtml(String(email)) +
        "</a>"
      : "—";

    body.innerHTML =
      '<div class="orders-detail-modal__header">' +
      '<div class="orders-detail-modal__title-row">' +
      '<h3 id="orders-detail-modal-title" class="orders-detail-modal__title">' +
      escapeHtml(t("orders.order_content", { name: orderName })) +
      "</h3>" +
      "</div>" +
      (subtitleParts.length || status
        ? '<div class="orders-detail-modal__meta-row">' +
          (subtitleParts.length
            ? '<p class="orders-detail-modal__subtitle">' +
              escapeHtml(subtitleParts.join(" ")) +
              "</p>"
            : '<p class="orders-detail-modal__subtitle orders-detail-modal__subtitle--empty" aria-hidden="true"></p>') +
          (status ? getOrderStatusBadgeHtml(status) : "") +
          "</div>"
        : "") +
      "</div>" +
      '<div class="orders-detail-modal__products" data-orders-detail-products>' +
      productsHtml +
      "</div>" +
      feesHtml +
      '<div class="orders-detail-modal__section">' +
      buildOrdersDetailRow(
        t("orders.created_at"),
        escapeHtml(formatOrderDate(createdRaw)),
      ) +
      buildOrdersDetailRow(t("orders.payment"), paymentValueHtml) +
      buildOrdersDetailRow(
        t("orders.payment_method"),
        escapeHtml(formatOrderDisplayValue(paymentMethod)),
      ) +
      buildOrdersDetailRow(
        t("orders.shipping_status"),
        escapeHtml(formatOrderDisplayValue(shippingStatus)),
      ) +
      buildOrdersDetailRow(
        t("orders.estimated_shipping"),
        escapeHtml(
          estimatedShip ? formatOrderDate(estimatedShip) : t("common.dash"),
        ),
      ) +
      buildOrdersDetailRow(
        t("orders.tracking_number"),
        escapeHtml(formatOrderDisplayValue(tracking)),
      ) +
      "</div>" +
      '<div class="orders-detail-modal__divider" aria-hidden="true"></div>' +
      '<div class="orders-detail-modal__section">' +
      buildOrdersDetailRow(
        t("orders.customer_name"),
        escapeHtml(formatOrderDisplayValue(customerName)),
      ) +
      buildOrdersDetailRow(t("orders.email"), emailHtml) +
      buildOrdersDetailRow(
        t("orders.phone"),
        escapeHtml(formatOrderDisplayValue(phone)),
      ) +
      buildOrdersDetailRow(
        t("orders.from"),
        '<span class="orders-detail-modal__address">' +
          escapeHtml(formatOrderAddressBlock(fromAddr)).replace(/\n/g, "<br>") +
          "</span>",
        { multi: true },
      ) +
      buildOrdersDetailRow(
        t("orders.to"),
        '<span class="orders-detail-modal__address">' +
          escapeHtml(formatOrderAddressBlock(toAddr)).replace(/\n/g, "<br>") +
          "</span>",
        { multi: true },
      ) +
      "</div>" +
      '<div class="orders-detail-modal__divider" aria-hidden="true"></div>' +
      '<div class="orders-detail-modal__actions">' +
      '<button type="button" class="orders-detail-modal__support" data-orders-detail-support>' +
      escapeHtml(t("orders.contact_support")) +
      "</button>" +
      "</div>";
  }

  function closeOrdersDetailModal() {
    const modal = root.querySelector("[data-orders-detail-modal]");
    closePointsCenterBottomSheet(modal, {
      onClosed: function () {
        document.documentElement.style.overflow = "";
      },
    });
  }

  let ordersDetailRequestId = 0;

  function showOrdersDetailLoading() {
    const body = root.querySelector("[data-orders-detail-body]");
    if (!body) return;
    body.innerHTML =
      '<div class="orders-detail-modal__loading" role="status" aria-live="polite">' +
      '<span class="orders-detail-modal__loading-spinner" aria-hidden="true"></span>' +
      '<span class="orders-detail-modal__loading-text">' +
      escapeHtml(t("common.loading")) +
      "</span>" +
      "</div>";
  }

  function openOrdersDetailModal(listItem) {
    const modal = root.querySelector("[data-orders-detail-modal]");
    const body = root.querySelector("[data-orders-detail-body]");
    if (!modal || !body || !listItem) return;

    modal.hidden = false;
    document.documentElement.style.overflow = "hidden";
    showOrdersDetailLoading();

    const baseUrl = getLoyaltyBaseUrl();
    const shopifyId = getGiftCardShopifyId();
    const orderId = getOrderDetailRequestId(listItem);
    const requestId = ++ordersDetailRequestId;

    if (!baseUrl || !shopifyId || !orderId) {
      renderOrdersDetailModalContent(null, listItem);
      return;
    }

    ordersDetailLoading = true;
    fetch(
      buildOrdersApiUrl("/front/customer/orders/detail", {
        shopifyId: shopifyId,
        orderId: orderId,
      }),
      {
        method: "GET",
        headers: { Accept: "application/json" },
      },
    )
      .then(function (res) {
        return res.json().then(function (json) {
          return { ok: res.ok, json: json };
        });
      })
      .then(function (result) {
        if (requestId !== ordersDetailRequestId) return;
        const json = result && result.json;
        if (isLoyaltyApiOk(json) && json.data) {
          renderOrdersDetailModalContent(json.data, listItem);
          return;
        }
        if (typeof console !== "undefined" && console.warn) {
          console.warn(
            "[points-center] order detail API fallback to list item",
            json,
          );
        }
        renderOrdersDetailModalContent(null, listItem);
      })
      .catch(function () {
        if (requestId !== ordersDetailRequestId) return;
        renderOrdersDetailModalContent(null, listItem);
      })
      .finally(function () {
        if (requestId === ordersDetailRequestId) {
          ordersDetailLoading = false;
        }
      });
  }

  function initOrdersDetailModal() {
    const modal = root.querySelector("[data-orders-detail-modal]");
    if (!modal) return;

    modal.querySelectorAll("[data-orders-detail-close]").forEach(function (el) {
      el.addEventListener("click", closeOrdersDetailModal);
    });

    modal.addEventListener("click", function (e) {
      const productsRoot = e.target.closest("[data-orders-detail-products]");
      const viewMoreBtn = e.target.closest("[data-orders-detail-view-more]");
      if (viewMoreBtn && productsRoot && modal.contains(viewMoreBtn)) {
        e.preventDefault();
        setOrdersDetailProductsExpanded(productsRoot, true);
        return;
      }

      const viewLessBtn = e.target.closest("[data-orders-detail-view-less]");
      if (viewLessBtn && productsRoot && modal.contains(viewLessBtn)) {
        e.preventDefault();
        setOrdersDetailProductsExpanded(productsRoot, false);
        return;
      }

      const supportBtn = e.target.closest("[data-orders-detail-support]");
      if (!supportBtn || !modal.contains(supportBtn)) return;
      e.preventDefault();
      closeOrdersDetailModal();
      activateTab("my-support");
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && !modal.hidden) {
        closeOrdersDetailModal();
      }
    });

    const ordersPanel = root.querySelector("[data-orders-panel]");
    if (ordersPanel) {
      ordersPanel.addEventListener("click", function (e) {
        const btn = e.target.closest("[data-orders-open-detail]");
        if (!btn || !ordersPanel.contains(btn)) return;
        const index = Number(btn.getAttribute("data-order-index"));
        const item =
          Number.isFinite(index) && ordersCurrentItems[index]
            ? ordersCurrentItems[index]
            : null;
        if (!item) return;
        openOrdersDetailModal(item);
      });
    }
  }

  function loadOrdersPage(options) {
    options = options || {};
    const tbody = getOrdersTbody();
    if (!tbody) return Promise.resolve();

    const requestId = ++ordersRequestId;
    if (ordersAbortController) {
      try {
        ordersAbortController.abort();
      } catch (e) {
        /* ignore */
      }
    }
    const abortController =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    ordersAbortController = abortController;

    const baseUrl = getLoyaltyBaseUrl();
    const shopifyId = getGiftCardShopifyId();
    if (!baseUrl) {
      ordersCurrentItemCount = 0;
      ordersCurrentItems = [];
      ordersHasNextPage = false;
      ordersNextCursor = null;
      ordersTotal = 0;
      ordersTotalReady = false;
      ordersLoading = false;
      renderOrdersStatusContent(t("common.no_api"));
      updateOrdersPaginationUi();
      return Promise.resolve();
    }
    if (!shopifyId) {
      ordersCurrentItemCount = 0;
      ordersCurrentItems = [];
      ordersHasNextPage = false;
      ordersNextCursor = null;
      ordersTotal = 0;
      ordersTotalReady = false;
      ordersLoading = false;
      renderOrdersStatusContent(t("orders.please_login"));
      updateOrdersPaginationUi();
      return Promise.resolve();
    }

    const pageIndex =
      options.pageIndex != null ? options.pageIndex : ordersPageIndex;
    const cursor =
      options.cursor !== undefined
        ? options.cursor
        : ordersCursorStack[pageIndex] || null;
    const statusKey = isValidOrdersSubtab(ordersActiveSubtab)
      ? ordersActiveSubtab
      : "all";
    const params = {
      shopifyId: shopifyId,
      // Docs: all / processing / shipped / delivered / cancelled / refunded
      status: statusKey,
    };
    if (cursor) params.cursor = cursor;

    ordersLoading = true;
    updateOrdersPaginationUi();
    if (!options.append) {
      renderOrdersStatusContent(t("common.loading"));
    }

    const fetchOpts = {
      method: "GET",
      headers: { Accept: "application/json" },
    };
    if (abortController) fetchOpts.signal = abortController.signal;

    return fetch(
      buildOrdersApiUrl("/front/customer/orders/page", params),
      fetchOpts,
    )
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        if (requestId !== ordersRequestId) return null;
        if (!json || json.code !== 200 || !json.data) {
          ordersCurrentItemCount = 0;
          ordersHasNextPage = false;
          ordersNextCursor = null;
          ordersTotal = 0;
          ordersTotalReady = false;
          renderOrdersStatusContent(t("orders.failed_load"), true);
          updateOrdersPaginationUi();
          return null;
        }

        const data = json.data;
        const items = Array.isArray(data.items) ? data.items : [];

        ordersPageIndex = pageIndex;
        ordersHasNextPage = !!data.hasNextPage;
        ordersNextCursor = data.nextCursor || null;

        const apiPageSize = Number(
          data.pageSize != null
            ? data.pageSize
            : data.size != null
              ? data.size
              : data.limit,
        );
        if (Number.isFinite(apiPageSize) && apiPageSize > 0) {
          ordersPageSize = apiPageSize;
        } else if (pageIndex === 0 && items.length > 0) {
          ordersPageSize = items.length;
        }

        let pageTotal = null;
        if (data.total != null && data.total !== "") {
          pageTotal = Math.max(0, Number(data.total) || 0);
        } else if (data.totalCount != null && data.totalCount !== "") {
          pageTotal = Math.max(0, Number(data.totalCount) || 0);
        } else {
          // Fallback when API has no total yet
          pageTotal = Math.max(
            ordersTotal,
            pageIndex * ordersPageSize +
              items.length +
              (ordersHasNextPage ? 1 : 0),
          );
        }
        reconcileOrdersListTotal(statusKey, pageTotal);

        if (ordersCursorStack[pageIndex] === undefined) {
          ordersCursorStack[pageIndex] = cursor;
        }
        if (ordersHasNextPage && ordersNextCursor) {
          ordersCursorStack[pageIndex + 1] = ordersNextCursor;
        }

        renderOrdersRows(items);
        updateOrdersPaginationUi();
        return data;
      })
      .catch(function (err) {
        if (requestId !== ordersRequestId) return null;
        if (err && err.name === "AbortError") return null;
        ordersCurrentItemCount = 0;
        ordersHasNextPage = false;
        ordersNextCursor = null;
        ordersTotal = 0;
        ordersTotalReady = false;
        renderOrdersStatusContent(t("common.network_error"), true);
        updateOrdersPaginationUi();
        return null;
      })
      .finally(function () {
        if (requestId !== ordersRequestId) return;
        ordersLoading = false;
        updateOrdersPaginationUi();
      });
  }

  function resetAndLoadOrders(subtab) {
    if (isValidOrdersSubtab(subtab)) ordersActiveSubtab = subtab;
    ordersCursorStack = [null];
    ordersPageIndex = 0;
    ordersHasNextPage = false;
    ordersNextCursor = null;
    ordersTotal = 0;
    ordersTotalReady = false;
    ordersCurrentItemCount = 0;
    fetchOrderTabCounts(true);
    return loadOrdersPage({ pageIndex: 0, cursor: null });
  }

  function ensureOrdersData(options) {
    options = options || {};
    const subtab = isValidOrdersSubtab(options.ordersTab)
      ? options.ordersTab
      : ordersActiveSubtab;
    if (typeof activateOrdersSubtab === "function") {
      activateOrdersSubtab(subtab, {
        skipSave: true,
        forceReload: true,
        skipTabScroll: true,
      });
      return;
    }
    resetAndLoadOrders(subtab);
  }

  function fetchLoyaltySummary(force) {
    if (!force && loyaltySummaryData) {
      applyLoyaltySummaryToUi(loyaltySummaryData);
      return Promise.resolve(loyaltySummaryData);
    }

    const baseUrl = getLoyaltyBaseUrl();
    const shopifyId = getGiftCardShopifyId();
    if (!baseUrl || !shopifyId) {
      syncAvailablePoints(0);
      loyaltySummaryData = null;
      return Promise.resolve(null);
    }

    if (!force && summaryFetchPromise) {
      return summaryFetchPromise;
    }

    if (force) {
      loyaltySummaryData = null;
      userAvailablePoints = null;
      summaryFetchPromise = null;
    }

    summaryFetchPromise = fetch(
      baseUrl.replace(/\/$/, "") + "/front/loyalty/summary",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shopifyId: shopifyId }),
      },
    )
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        const summaryPayload = extractLoyaltySummaryJson(json);
        if (summaryPayload) {
          loyaltySummaryData = normalizeLoyaltySummaryData(summaryPayload);
          applyLoyaltySummaryToUi(loyaltySummaryData);
          Promise.all([
            fetchLoyaltyTiers(false),
            fetchBonusRulesSimple(false),
          ]).then(function (results) {
            const tiers = results[0];
            const couponEl = root.querySelector("[data-asset-coupon-count]");
            const couponCount = couponEl
              ? parseCountUpNumber(couponEl.textContent)
              : 0;
            renderOverviewLevel(loyaltySummaryData, tiers);
            renderBenefitsCenter(loyaltySummaryData, tiers, couponCount);
          });
          return loyaltySummaryData;
        }
        loyaltySummaryData = null;
        syncAvailablePoints(0);
        return null;
      })
      .catch(function () {
        loyaltySummaryData = null;
        syncAvailablePoints(0);
        return null;
      })
      .finally(function () {
        summaryFetchPromise = null;
      });

    return summaryFetchPromise;
  }

  function ensurePointsLoaded() {
    if (userAvailablePoints != null) {
      return Promise.resolve(userAvailablePoints);
    }
    return fetchLoyaltySummary().then(function () {
      return userAvailablePoints;
    });
  }

  function invalidatePointStoreCache() {
    pointStorePagesCache = {};
    pointStoreAllItems = null;
    pointStoreLoadPromise = null;
  }

  function invalidateBenefitCaches() {
    myCouponFullListCache = null;
    myGiftCardFullListCache = null;
  }

  // ── Benefits & coupons ─────────────────────────────────────────────
  function getBenefitRuleType(item) {
    const snap = getStoreItemSnap(item);
    return String((snap && snap.ruleType) || "").trim();
  }

  function isGiftCardBenefitItem(item) {
    if (getBenefitRuleType(item) === "Gift Card") return true;
    const category = String(
      (item && item.benefitCategory) || "",
    ).toUpperCase();
    return category === "GIFT_CARD";
  }

  function getGiftCardEnabledFlag(item) {
    if (item == null) return undefined;
    if (item.giftCardEnabled != null) return item.giftCardEnabled;
    const snap = item.snapshot ? item.snapshot : {};
    if (snap.giftCardEnabled != null) return snap.giftCardEnabled;
    return undefined;
  }

  function isGiftCardDeactivated(item) {
    if (!isGiftCardBenefitItem(item)) return false;
    const raw = getGiftCardEnabledFlag(item);
    if (raw == null) return false;
    if (typeof raw === "boolean") return !raw;
    const normalized = String(raw).trim().toLowerCase();
    if (normalized === "false" || normalized === "0") return true;
    if (normalized === "true" || normalized === "1") return false;
    return false;
  }

  function getGiftCardSnapshotDiscountAmount(item) {
    const snap = getStoreItemSnap(item);
    if (snap.discountAmount == null || snap.discountAmount === "") return null;
    const amount = Number(snap.discountAmount);
    return Number.isFinite(amount) ? amount : null;
  }

  /** null / empty giftCardExpiresOn = no expiry (permanent). */
  function getGiftCardExpiresOn(item) {
    if (!item) return null;
    const snap = getStoreItemSnap(item);
    const raw =
      item.giftCardExpiresOn != null && item.giftCardExpiresOn !== ""
        ? item.giftCardExpiresOn
        : snap.giftCardExpiresOn;
    if (raw == null || raw === "") return null;
    if (typeof raw === "string" && !String(raw).trim()) return null;
    return raw;
  }

  function isGiftCardRuleItem(item) {
    return (
      isGiftCardBenefitItem(item) ||
      normalizeRuleType(item).toLowerCase() === "gift card"
    );
  }

  function formatGiftCardExpiresOnDate(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const year = date.getFullYear();
    return day + " " + month + " " + year;
  }

  function getGiftCardExpiryDisplayText(item) {
    const raw = getGiftCardExpiresOn(item);
    if (raw == null) return t("coupons.never_expires");
    const formatted = formatGiftCardExpiresOnDate(raw);
    return formatted || t("coupons.never_expires");
  }

  function formatGiftCardExpiryHtml(item, className) {
    if (!isGiftCardRuleItem(item)) return "";
    const text = getGiftCardExpiryDisplayText(item);
    if (!text) return "";
    return (
      '<p class="' +
      escapeHtml(className) +
      '">' +
      escapeHtml(text) +
      "</p>"
    );
  }

  function isGiftCardExpiredByDate(item) {
    if (!isGiftCardBenefitItem(item)) return false;
    const raw = getGiftCardExpiresOn(item);
    if (raw == null) return false;
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return false;
    return date.getTime() < Date.now();
  }

  function isGiftCardBenefitAvailable(item) {
    if (!isGiftCardBenefitItem(item)) return false;
    if (isGiftCardDeactivated(item)) return false;
    if (isBenefitItemExpired(item)) return false;
    const status = item && item.status ? String(item.status).toUpperCase() : "";
    if (status === "USED") return false;
    const amount = getGiftCardSnapshotDiscountAmount(item);
    return amount != null && amount > 0;
  }

  function isGiftCardBalanceDepleted(item) {
    if (!isGiftCardBenefitItem(item)) return false;
    const amount = getGiftCardSnapshotDiscountAmount(item);
    if (amount != null) return amount <= 0;
    if (item == null || item.giftCardBalance == null) return false;
    const balance = Number(item.giftCardBalance);
    return Number.isFinite(balance) && balance <= 0;
  }

  function isBenefitItemUsed(item) {
    if (isGiftCardBenefitItem(item)) {
      if (isGiftCardDeactivated(item)) return true;
      if (isGiftCardBalanceDepleted(item)) return true;
      return getBenefitItemStatusRaw(item) === "USED";
    }
    if (isGiftCardBalanceDepleted(item)) return true;
    return getBenefitItemStatusRaw(item) === "USED";
  }

  function getBenefitItemStatusLabel(item) {
    const raw =
      item && item.status != null && String(item.status).trim() !== ""
        ? String(item.status).trim()
        : "";
    if (!raw) return t("common.dash");
    return getBenefitStatusLabel(raw);
  }

  function filterBenefitsForCouponTab(items, couponTabId) {
    if (!items || !items.length) return [];
    if (couponTabId === "gift-card") {
      return items.filter(isGiftCardBenefitItem);
    }
    return items.filter(function (item) {
      return !isGiftCardBenefitItem(item);
    });
  }

  function getBenefitPanelConfig(couponTabId) {
    if (couponTabId === "gift-card") {
      return {
        couponTabId: "gift-card",
        benefitCategory: "GIFT_CARD",
        gridSelector: "[data-my-gift-card-grid]",
        paginationSelector: "[data-my-gift-card-pagination]",
        getFullListCache: function () {
          return myGiftCardFullListCache;
        },
        setFullListCache: function (value) {
          myGiftCardFullListCache = value;
        },
        getCurrentPage: function () {
          return myGiftCardCurrentPage;
        },
        setCurrentPage: function (page) {
          myGiftCardCurrentPage = page;
        },
      };
    }
    return {
      couponTabId: "discount",
      benefitCategory: "COUPON",
      gridSelector: "[data-my-coupon-grid]",
      paginationSelector: "[data-my-coupon-pagination]",
      getFullListCache: function () {
        return myCouponFullListCache;
      },
      setFullListCache: function (value) {
        myCouponFullListCache = value;
      },
      getCurrentPage: function () {
        return myCouponCurrentPage;
      },
      setCurrentPage: function (page) {
        myCouponCurrentPage = page;
      },
    };
  }

  function decodeHtmlEntities(value) {
    const str = String(value || "");
    if (!str.includes("&")) return str;
    if (typeof document === "undefined") {
      return str
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&#x27;/gi, "'");
    }
    const el = document.createElement("textarea");
    el.innerHTML = str;
    return el.value;
  }

  function escapeHtml(value) {
    return decodeHtmlEntities(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function escapeHttpUrl(url) {
    const raw =
      typeof resolveMembershipApiImageUrl === "function"
        ? resolveMembershipApiImageUrl(url)
        : String(url || "").trim();
    if (!raw) return "";
    try {
      if (/^https?:\/\//i.test(raw)) {
        const parsed = new URL(raw);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:")
          return "";
        return parsed.href;
      }
      const parsed = new URL(raw, window.location.origin);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:")
        return "";
      return parsed.href;
    } catch (e) {
      return "";
    }
  }

  function formatBenefitLabel(snapshot) {
    const snap = snapshot || {};
    const points = snap.costToRedeem != null ? snap.costToRedeem : "";
    const amount = snap.discountAmount != null ? snap.discountAmount : "";
    const label =
      snap.customerFacingLabel || snap.ruleName || t("redeem.reward");
    return String(label)
      .replace(/\{\{\s*points\s*\}\}/gi, String(points))
      .replace(/\{\{\s*amount\s*\}\}/gi, String(amount))
      .replace(/\{\{\s*discount\s*\}\}/gi, String(amount));
  }

  function formatBenefitDiscount(snapshot) {
    const snap = snapshot || {};
    if (snap.discountAmount == null || snap.discountAmount === "") return "";
    const type = String(snap.discountType || "").toLowerCase();
    if (type === "percentage" || type === "percent") {
      return t("redeem.percent_off", { amount: snap.discountAmount });
    }
    return t("redeem.fixed_off", {
      amount: Number(snap.discountAmount).toFixed(2),
    });
  }

  function isPercentageDiscountType(discountType) {
    const type = String(discountType || "").toLowerCase();
    return type === "percentage" || type === "percent";
  }

  function formatMoneyAmountDisplay(raw) {
    const num = Number(raw);
    if (Number.isNaN(num)) return String(raw == null ? "" : raw);
    return num % 1 === 0 ? String(Math.round(num)) : num.toFixed(2);
  }

  function getFreeProductImageRaw(snap, item) {
    const sources = [];
    if (snap) sources.push(snap);
    if (item) {
      sources.push(item);
      sources.push(getStoreItemSnap(item));
      const nested = parseRedeemSnapshot(item);
      if (nested && Object.keys(nested).length) sources.push(nested);
    }

    for (let i = 0; i < sources.length; i++) {
      const source = sources[i];
      if (!source || typeof source !== "object") continue;
      const candidates = [
        source.freeProductImage,
        source.free_product_image,
        source.redeemRule && source.redeemRule.freeProductImage,
      ];
      for (let j = 0; j < candidates.length; j++) {
        const value = candidates[j];
        if (value != null && String(value).trim() !== "") {
          return String(value).trim();
        }
      }
    }
    return "";
  }

  function normalizeFreeProductImageUrl(url) {
    const raw = String(url || "").trim();
    if (!raw) return "";

    if (raw.indexOf("//") === 0) {
      return normalizeFreeProductImageUrl("https:" + raw);
    }

    if (/^https?:\/\//i.test(raw)) {
      try {
        const parsed = new URL(raw);
        if (parsed.protocol === "http:" || parsed.protocol === "https:") {
          return parsed.href;
        }
      } catch (e) {}
      return "";
    }

    return escapeHttpUrl(raw);
  }

  function getFreeProductImageUrl(snap, item) {
    return normalizeFreeProductImageUrl(getFreeProductImageRaw(snap, item));
  }

  function isFreeProductSnap(snap, item) {
    const ruleType = String(
      (snap && snap.ruleType) || (item ? normalizeRuleType(item) : "") || "",
    )
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, " ");
    return ruleType === "free product";
  }

  function formatFreeProductImageHtml(snap, classPrefix, item) {
    const prefix = classPrefix || "benefits-redeem-ticket";
    const url = getFreeProductImageUrl(snap, item);
    const labelSnap = snap || (item ? getStoreItemSnap(item) : {});
    if (!url) {
      return (
        '<span class="' +
        prefix +
        "__value " +
        prefix +
        '__value--label">' +
        escapeHtml(formatBenefitLabel(labelSnap) || t("common.free_product")) +
        "</span>"
      );
    }
    const alt = escapeHtml(
      formatBenefitLabel(labelSnap) || t("common.free_product"),
    );
    return (
      '<div class="' +
      prefix +
      '__product-media">' +
      '<img class="' +
      prefix +
      '__product-image" src="' +
      escapeHtml(url) +
      '" alt="' +
      alt +
      '" width="120" height="120" loading="lazy" decoding="async">' +
      "</div>"
    );
  }

  /**
   * Ticket hero value for Redeem EcoCredits / Coupons / History / modal.
   * Free Product → "Free Product"
   * Free Shipping → label (not shippingThreshold as $ amount)
   * Others → discountAmount (+ discountType Percentage / Fixed)
   */
  function formatCouponTicketDisplay(snap) {
    const s = snap || {};
    const ruleType = String(s.ruleType || "")
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, " ");

    if (ruleType === "free product") {
      return {
        currency: "",
        amount: t("common.free_product"),
        isLabel: true,
        isPercent: false,
        label: t("common.free_product"),
      };
    }

    if (ruleType === "free shipping") {
      const freeShippingLabel = t("common.free_shipping");
      return {
        currency: "",
        amount: freeShippingLabel,
        isLabel: true,
        isPercent: false,
        label: freeShippingLabel,
      };
    }

    if (s.discountAmount != null && s.discountAmount !== "") {
      if (isPercentageDiscountType(s.discountType)) {
        return {
          currency: "",
          amount: String(s.discountAmount) + "%",
          isLabel: false,
          isPercent: true,
          label: formatBenefitLabel(s) || "",
        };
      }
      return {
        currency: "$",
        amount: formatMoneyAmountDisplay(s.discountAmount),
        isLabel: false,
        isPercent: false,
        label: formatBenefitLabel(s) || "",
      };
    }

    const label = formatBenefitLabel(s) || t("common.dash");
    return {
      currency: "",
      amount: label,
      isLabel: true,
      isPercent: false,
      label: label,
    };
  }

  function formatTicketValueHtmlByPrefix(snap, classPrefix, item) {
    const resolved = snap || (item ? getStoreItemSnap(item) : {});
    if (isFreeProductSnap(resolved, item)) {
      return formatFreeProductImageHtml(resolved, classPrefix, item);
    }

    const display = formatCouponTicketDisplay(resolved);
    const prefix = classPrefix || "benefits-redeem-ticket";
    if (display.isLabel) {
      return (
        '<span class="' +
        prefix +
        "__value " +
        prefix +
        '__value--label">' +
        escapeHtml(display.label || display.amount || t("common.dash")) +
        "</span>"
      );
    }
    function faceHtml(mod, text) {
      const safe = escapeHtml(text);
      return (
        '<span class="' +
        prefix +
        "__" +
        mod +
        '-wrap">' +
        '<span class="' +
        prefix +
        "__" +
        mod +
        '-ghost" aria-hidden="true">' +
        safe +
        "</span>" +
        '<span class="' +
        prefix +
        "__" +
        mod +
        '">' +
        safe +
        "</span></span>"
      );
    }
    return (
      '<div class="' +
      prefix +
      '__value">' +
      (display.currency ? faceHtml("currency", display.currency) : "") +
      faceHtml("amount", display.amount) +
      "</div>"
    );
  }

  function getBenefitItemStatusRaw(item) {
    if (!item || item.status == null) return "";
    const raw = String(item.status).trim();
    return raw ? raw.toUpperCase() : "";
  }

  function isBenefitStatusDeactivated(item) {
    const status = getBenefitItemStatusRaw(item);
    return status === "DEACTIVATED" || status === "DISABLED";
  }

  function isBenefitItemScheduled(item) {
    return getBenefitItemStatusRaw(item) === "SCHEDULED";
  }

  function getBenefitStatusLabel(status) {
    const normalized = String(status || "").toUpperCase();
    if (normalized === "ACTIVE") return t("status.available");
    if (normalized === "USED") return t("status.used");
    if (normalized === "DEACTIVATED" || normalized === "DISABLED")
      return t("status.deactivated");
    if (normalized === "SCHEDULED") return t("status.scheduled");
    if (normalized === "EXPIRED" || normalized === "INACTIVE")
      return t("status.expired");
    if (normalized === "PENDING") return t("status.pending");
    return status || t("common.dash");
  }

  function getBenefitStatusMod(item) {
    const status = getBenefitItemStatusRaw(item);
    if (status === "EXPIRED" || status === "INACTIVE") return "expired";
    if (status === "DEACTIVATED" || status === "DISABLED") return "deactivated";
    if (status === "SCHEDULED") return "scheduled";
    if (status === "USED") return "used";
    if (status === "ACTIVE") return "available";
    return "available";
  }

  function isBenefitCouponActionDisabled(item) {
    const status = getBenefitItemStatusRaw(item);
    return status !== "ACTIVE";
  }

  function isBenefitItemExpired(item) {
    const status = getBenefitItemStatusRaw(item);
    return status === "EXPIRED" || status === "INACTIVE";
  }

  /** Point store + owned benefits: block redeem / primary actions when expired or deactivated. */
  function isStoreItemRedeemBlocked(item) {
    if (isBenefitItemExpired(item)) return true;
    if (isBenefitStatusDeactivated(item)) return true;
    if (isBenefitItemScheduled(item)) return true;
    return false;
  }

  function getStoreItemRedeemBlockedReason(item) {
    if (isBenefitItemExpired(item)) return t("status.expired");
    if (isBenefitStatusDeactivated(item)) return t("status.deactivated");
    if (isBenefitItemScheduled(item)) return t("status.scheduled");
    return "";
  }

  let copiedTooltipEl = null;
  let copiedTooltipTimer = null;

  function hideCopiedTooltip() {
    if (copiedTooltipTimer) {
      clearTimeout(copiedTooltipTimer);
      copiedTooltipTimer = null;
    }
    if (copiedTooltipEl) {
      copiedTooltipEl.remove();
      copiedTooltipEl = null;
    }
  }

  function showCopiedTooltip(anchorEl, message) {
    hideCopiedTooltip();
    if (!anchorEl || !(anchorEl instanceof Element)) return;

    const tip = document.createElement("div");
    tip.setAttribute("role", "status");
    tip.textContent = message || t("common.copied") || "Copied";

    if (anchorEl.closest("[data-points-center-refer-share]")) {
      tip.className = "points-copy-tooltip is-visible";
      anchorEl.appendChild(tip);
      copiedTooltipEl = tip;
      copiedTooltipTimer = setTimeout(hideCopiedTooltip, 1600);
      return;
    }

    tip.className = "points-copy-tooltip points-copy-tooltip--fixed is-visible";

    // Mount inside the open overlay so the tip shares its stacking context and
    // stays above the dialog (body-level tips can still lose to modal layers).
    const host =
      anchorEl.closest(".benefits-history-modal") ||
      anchorEl.closest(".points-redeem-modal") ||
      anchorEl.closest("[data-redeem-modal]") ||
      document.body;

    tip.style.position = "fixed";
    tip.style.zIndex = "2147483646";
    tip.style.opacity = "1";
    tip.style.pointerEvents = "none";
    tip.style.transform = "translateX(-50%)";
    tip.style.bottom = "auto";
    host.appendChild(tip);
    copiedTooltipEl = tip;

    const rect = anchorEl.getBoundingClientRect();
    // Force layout so we get a real tip size even before paint.
    const tipHeight = tip.offsetHeight || 28;
    const tipWidth = tip.offsetWidth || 64;
    const gap = 8;
    let top = rect.top - tipHeight - gap;
    const left = rect.left + rect.width / 2;

    if (top < 8) {
      top = rect.bottom + gap;
      tip.classList.add("is-below");
    } else {
      tip.classList.remove("is-below");
    }

    // Keep tip on-screen horizontally.
    const minLeft = tipWidth / 2 + 8;
    const maxLeft = window.innerWidth - tipWidth / 2 - 8;
    const safeLeft = Math.min(Math.max(left, minLeft), maxLeft);

    tip.style.top = Math.round(top) + "px";
    tip.style.left = Math.round(safeLeft) + "px";

    copiedTooltipTimer = setTimeout(hideCopiedTooltip, 1600);
  }

  function copyTextWithTip(value, tipMessage, anchorEl) {
    return copyTextSilent(value).then(function (ok) {
      if (ok) showCopiedTooltip(anchorEl, tipMessage || t("common.copied"));
      return ok;
    });
  }

  let myCouponSvgUid = 0;

  function uniquifySvgRootIds(svgRoot, uid) {
    const prefix = "ct" + uid + "_";
    const idMap = {};
    if (svgRoot.id) {
      idMap[svgRoot.id] = prefix + svgRoot.id;
      svgRoot.id = prefix + svgRoot.id;
    }
    svgRoot.querySelectorAll("[id]").forEach(function (node) {
      const oldId = node.id;
      if (!oldId || idMap[oldId]) return;
      idMap[oldId] = prefix + oldId;
      node.id = prefix + oldId;
    });
    const urlAttrNames = [
      "fill",
      "stroke",
      "mask",
      "clip-path",
      "filter",
      "href",
      "xlink:href",
    ];
    const nodes = [svgRoot].concat(
      Array.prototype.slice.call(svgRoot.querySelectorAll("*")),
    );
    nodes.forEach(function (el) {
      urlAttrNames.forEach(function (attr) {
        const val = el.getAttribute(attr);
        if (!val || val.indexOf("url(#") === -1) return;
        el.setAttribute(
          attr,
          val.replace(/url\(#([^)]+)\)/g, function (_, id) {
            return "url(#" + (idMap[id] || prefix + id) + ")";
          }),
        );
      });
    });
  }

  function mountCouponTicketBgToShell(shell, disabled) {
    if (!shell) return false;
    const templateId = disabled
      ? "my-coupon-ticket-bg-disabled"
      : "my-coupon-ticket-bg-active";
    const tpl = document.getElementById(templateId);
    if (!tpl || !tpl.content) return false;
    const svg = tpl.content.querySelector("svg");
    if (!svg) return false;
    const clone = svg.cloneNode(true);
    clone.classList.add("my-coupon-card__bg-svg");
    clone.removeAttribute("width");
    clone.removeAttribute("height");
    clone.removeAttribute("style");
    uniquifySvgRootIds(clone, String(++myCouponSvgUid));
    const wrap = document.createElement("div");
    wrap.className = "my-coupon-card__bg";
    wrap.setAttribute("data-ticket-variant", disabled ? "disabled" : "active");
    wrap.setAttribute("aria-hidden", "true");
    wrap.appendChild(clone);
    const oldBg = shell.querySelector(".my-coupon-card__bg");
    if (oldBg) oldBg.replaceWith(wrap);
    else shell.insertBefore(wrap, shell.firstChild);
    return true;
  }

  function getCouponTemplateHtml(templateId, className, uniquify) {
    const tpl = document.getElementById(templateId);
    if (!tpl || !tpl.content) return "";
    const wrap = document.createElement("div");
    wrap.appendChild(tpl.content.cloneNode(true));
    const svg = wrap.querySelector("svg");
    if (svg) {
      if (className) svg.classList.add(className);
      svg.removeAttribute("width");
      svg.removeAttribute("height");
      svg.removeAttribute("style");
      if (uniquify) {
        uniquifySvgRootIds(svg, String(++myCouponSvgUid));
      }
    }
    return wrap.innerHTML;
  }

  function getCouponTicketBgHtml(disabled) {
    const shell = document.createElement("div");
    shell.className = "my-coupon-card__shell";
    if (!mountCouponTicketBgToShell(shell, disabled)) return "";
    const bg = shell.querySelector(".my-coupon-card__bg");
    return bg ? bg.outerHTML : "";
  }

  function isCouponCardTicketDisabled(card) {
    return (
      card.classList.contains("my-coupon-card--used") ||
      card.classList.contains("my-coupon-card--expired") ||
      card.classList.contains("my-coupon-card--deactivated") ||
      card.classList.contains("my-coupon-card--scheduled") ||
      card.getAttribute("data-benefit-disabled") === "true"
    );
  }

  function refreshCouponCardBackgrounds(container) {
    const scope = container || root;
    scope
      .querySelectorAll(".my-coupon-card:not(.my-coupon-card--gift-card)")
      .forEach(function (card) {
        const shell = card.querySelector(".my-coupon-card__shell");
        if (!shell) return;
        const isDisabled = isCouponCardTicketDisabled(card);
        const variant = isDisabled ? "disabled" : "active";
        const oldBg = shell.querySelector(".my-coupon-card__bg");
        if (oldBg && oldBg.getAttribute("data-ticket-variant") === variant) {
          return;
        }
        mountCouponTicketBgToShell(shell, isDisabled);
      });
  }

  function warmCouponSvgTemplates() {
    [
      "my-coupon-ticket-bg-active",
      "my-coupon-ticket-bg-disabled",
      "my-giftcard-ribbon-active",
      "my-giftcard-ribbon-disabled",
      "my-coupons-empty-icon",
    ].forEach(function (templateId) {
      getCouponTemplateHtml(templateId, null, false);
    });
  }

  warmCouponSvgTemplates();

  function syncCouponCardScales(container) {
    const scope = container || root;
    const isDesktop = window.matchMedia("(min-width: 769px)").matches;

    scope
      .querySelectorAll(".my-coupon-card--discount")
      .forEach(function (card) {
        const shell = card.querySelector(".my-coupon-card__shell");
        if (!shell) return;
        if (isDesktop) {
          shell.style.transform = "";
          return;
        }
        const width = card.getBoundingClientRect().width;
        if (!width) return;
        shell.style.transform = "scale(" + width / 258 + ")";
      });

    scope
      .querySelectorAll(".my-coupon-card--gift-card")
      .forEach(function (card) {
        const shell = card.querySelector(".my-giftcard-card__shell");
        if (!shell) return;
        if (isDesktop) {
          shell.style.transform = "";
          return;
        }
        const width = card.getBoundingClientRect().width;
        if (!width) return;
        shell.style.transform = "scale(" + width / 270 + ")";
      });
  }

  function scheduleCouponCardBackgroundRefresh(container) {
    // Apply ticket SVG synchronously so the first paint is not active-blue then grey.
    refreshCouponCardBackgrounds(container);
    syncCouponCardScales(container);
    requestAnimationFrame(function () {
      syncCouponCardScales(container);
      const scope = container || root;
      let needsBgReflow = false;
      scope
        .querySelectorAll(".my-coupon-card--discount")
        .forEach(function (card) {
          if (!card.getBoundingClientRect().width) needsBgReflow = true;
        });
      if (needsBgReflow) {
        refreshCouponCardBackgrounds(container);
      }
    });
  }

  function getGiftcardRibbonHtml(disabled) {
    const html = getCouponTemplateHtml(
      disabled ? "my-giftcard-ribbon-disabled" : "my-giftcard-ribbon-active",
      "my-giftcard-card__ribbon-svg",
      false,
    );
    if (!html) return "";
    return (
      '<div class="my-giftcard-card__ribbon" aria-hidden="true">' +
      html +
      "</div>"
    );
  }

  function getCouponsEmptyHtml() {
    const iconHtml = getCouponTemplateHtml(
      "my-coupons-empty-icon",
      "my-coupons__empty-icon-svg",
      true,
    );
    return (
      '<div class="my-coupons__empty-state">' +
      '<div class="my-coupons__empty-copy">' +
      (iconHtml
        ? '<div class="my-coupons__empty-icon" aria-hidden="true">' +
          iconHtml +
          "</div>"
        : "") +
      '<p class="my-coupons__empty-text">' +
      escapeHtml(t("coupons.empty")) +
      "</p>" +
      "</div>" +
      '<button type="button" class="my-coupons__empty-btn" data-points-center-tab="benefits-center">' +
      escapeHtml(t("coupons.go_get")) +
      "</button>" +
      "</div>"
    );
  }

  function formatGiftCardDisplayCode(item, code) {
    const raw = String(
      code || (item && (item.giftCardCodeMasked || item.codeMasked)) || "",
    ).trim();
    if (!raw) return "****••••";
    if (raw.indexOf("*") !== -1) return raw;
    if (raw.length > 4) return "****" + raw.slice(-4);
    return raw;
  }

  function formatGiftCardSpendHint(snap, item, display) {
    // Reuse same minPurchaseAmount / minItemQuantity resolution as Redeem EcoCredits
    const minHtml = formatStoreMinPurchase(snap, item);
    if (!minHtml) return "";
    const amountMatch = minHtml.match(/\(\$([^)]+)\)/);
    if (!amountMatch) return "";
    const offPart = display.isPercent
      ? display.amount
      : (display.currency || "$") + display.amount;
    return t("coupons.spend_for", { amount: amountMatch[1], off: offPart });
  }

  const COUPON_COPY_ICON_SVG =
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M13.0208 11.9996H11.5848C11.2453 11.9996 10.9701 11.7246 10.9701 11.3849C10.9701 11.0452 11.2454 10.7702 11.5848 10.7702H13.0208C13.1586 10.7702 13.2704 10.658 13.2704 10.5205V3.02833C13.2704 2.89083 13.1584 2.77864 13.0208 2.77864H5.52873C5.39092 2.77864 5.27904 2.89083 5.27904 3.02833V4.44801C5.27904 4.78786 5.00373 5.0627 4.66435 5.0627C4.32498 5.0627 4.04966 4.7877 4.04966 4.44801V3.02833C4.04966 2.21254 4.71326 1.54911 5.52888 1.54911H13.0209C13.8364 1.54911 14.5001 2.21239 14.5001 3.02833V10.5204C14.5 11.3361 13.8362 11.9996 13.0208 11.9996Z" fill="currentColor"/>' +
    '<path d="M10.5619 14.4583H3.0699C2.25443 14.4583 1.59068 13.795 1.59068 12.9791V5.48703C1.59068 4.67125 2.25427 4.00781 3.0699 4.00781H10.5619C11.3774 4.00781 12.0411 4.67109 12.0411 5.48703V12.9791C12.041 13.795 11.3774 14.4583 10.5619 14.4583ZM3.0699 5.23734C2.93209 5.23734 2.82021 5.34953 2.82021 5.48703V12.9791C2.82021 13.1166 2.93224 13.2288 3.0699 13.2288H10.5619C10.6997 13.2288 10.8116 13.1166 10.8116 12.9791V5.48703C10.8116 5.34953 10.6996 5.23734 10.5619 5.23734H3.0699Z" fill="currentColor"/>' +
    "</svg>";

  function createMyBenefitCard(item, config) {
    // Same snap / field helpers as Redeem EcoCredits tickets & History
    const snap = getStoreItemSnap(item);
    const display = formatCouponTicketDisplay(snap);
    const minPurchase = formatStoreMinPurchase(snap, item);
    const typeLabel = getStoreBenefitTypeLabel(normalizeRuleType(snap));
    const code = item && item.code ? item.code : "";
    const displayCode = code || "••••••";
    const isDisabled = isBenefitCouponActionDisabled(item);
    const statusLabel = getBenefitItemStatusLabel(item);
    const statusMod = getBenefitStatusMod(item);
    const isGiftCard = isGiftCardBenefitItem(item);
    const historyTicket = !!(config && config.benefitsHistoryTicket);
    const useVerticalTicket = !isGiftCard || historyTicket;

    const card = document.createElement("article");
    card.className =
      "my-coupon-card" +
      (useVerticalTicket
        ? " my-coupon-card--discount"
        : " my-coupon-card--gift-card") +
      " my-coupon-card--" +
      statusMod;
    if (isDisabled) {
      card.setAttribute("data-benefit-disabled", "true");
    }
    if (item && item.id != null) {
      card.setAttribute("data-benefit-redeem-id", String(item.id));
    }

    if (isGiftCard && historyTicket) {
      let giftDisplay = display;
      if (
        giftDisplay.isLabel ||
        snap.discountAmount == null ||
        snap.discountAmount === ""
      ) {
        const balRaw =
          item && item.giftCardBalance != null && item.giftCardBalance !== ""
            ? item.giftCardBalance
            : snap.giftCardInitialValue != null &&
                snap.giftCardInitialValue !== ""
              ? snap.giftCardInitialValue
              : null;
        if (balRaw != null) {
          const balNum = Number(balRaw);
          giftDisplay = {
            currency: "$",
            amount: Number.isNaN(balNum)
              ? String(balRaw)
              : balNum % 1 === 0
                ? String(Math.round(balNum))
                : balNum.toFixed(2),
            isLabel: false,
            isPercent: false,
          };
        }
      }
      const ticketSnap = Object.assign({}, snap);
      if (!giftDisplay.isLabel && giftDisplay.amount != null) {
        ticketSnap.discountAmount = giftDisplay.amount;
        ticketSnap.discountType = "FIXED";
      }
      const valueHtml = formatTicketValueHtmlByPrefix(
        ticketSnap,
        "my-coupon-card",
        item,
      );
      const giftExpiryHtml = formatGiftCardExpiryHtml(
        item,
        "my-coupon-card__expiry",
      );
      const giftDisplayCode = formatGiftCardDisplayCode(item, code) || "••••••";
      const redeemId = item && item.id != null ? Number(item.id) : 0;

      card.innerHTML =
        '<div class="my-coupon-card__shell">' +
        '<div class="my-coupon-card__body">' +
        '<span class="my-coupon-card__status my-coupon-card__status--' +
        statusMod +
        '">' +
        escapeHtml(statusLabel) +
        "</span>" +
        '<div class="my-coupon-card__top">' +
        valueHtml +
        (minPurchase
          ? '<p class="my-coupon-card__min">' + minPurchase + "</p>"
          : "") +
        giftExpiryHtml +
        "</div>" +
        '<div class="my-coupon-card__foot">' +
        '<p class="my-coupon-card__type">' +
        escapeHtml(typeLabel) +
        "</p>" +
        '<div class="my-coupon-card__code-row">' +
        '<code class="my-coupon-card__code">' +
        escapeHtml(giftDisplayCode) +
        "</code>" +
        '<button type="button" class="my-coupon-card__copy" data-redeem-id="' +
        escapeHtml(String(redeemId)) +
        '" aria-label="' +
        escapeHtml(t("common.copy_code")) +
        '"' +
        (redeemId && !isDisabled
          ? ""
          : ' data-copy-default-disabled="true" disabled') +
        ">" +
        COUPON_COPY_ICON_SVG +
        "</button></div>" +
        '<button type="button" class="my-coupon-card__rules" data-coupon-rules>' +
        escapeHtml(t("coupons.rules")) +
        " >></button>" +
        "</div></div></div>";
      const historyShell = card.querySelector(".my-coupon-card__shell");
      if (historyShell) {
        mountCouponTicketBgToShell(historyShell, isDisabled);
      }
    } else if (isGiftCard) {
      let giftDisplay = display;
      if (
        giftDisplay.isLabel ||
        snap.discountAmount == null ||
        snap.discountAmount === ""
      ) {
        const balRaw =
          item && item.giftCardBalance != null && item.giftCardBalance !== ""
            ? item.giftCardBalance
            : snap.giftCardInitialValue != null &&
                snap.giftCardInitialValue !== ""
              ? snap.giftCardInitialValue
              : null;
        if (balRaw != null) {
          const balNum = Number(balRaw);
          giftDisplay = {
            currency: "$",
            amount: Number.isNaN(balNum)
              ? String(balRaw)
              : balNum % 1 === 0
                ? String(Math.round(balNum))
                : balNum.toFixed(2),
            isLabel: false,
            isPercent: false,
          };
        }
      }
      const spendHint = formatGiftCardSpendHint(snap, item, giftDisplay);
      const giftExpiryHtml = formatGiftCardExpiryHtml(
        item,
        "my-giftcard-card__expiry",
      );
      const redeemId = item && item.id != null ? Number(item.id) : 0;
      const giftDisplayCode = formatGiftCardDisplayCode(item, code);
      const valueHtml = giftDisplay.isLabel
        ? '<p class="my-giftcard-card__value my-giftcard-card__value--label">' +
          escapeHtml(giftDisplay.label || giftDisplay.amount) +
          "</p>"
        : '<div class="my-giftcard-card__value">' +
          (giftDisplay.currency
            ? '<span class="my-giftcard-card__currency-wrap">' +
              '<span class="my-giftcard-card__currency-ghost" aria-hidden="true">' +
              escapeHtml(giftDisplay.currency) +
              "</span>" +
              '<span class="my-giftcard-card__currency">' +
              escapeHtml(giftDisplay.currency) +
              "</span></span>"
            : "") +
          '<span class="my-giftcard-card__amount-wrap">' +
          '<span class="my-giftcard-card__amount-ghost" aria-hidden="true">' +
          escapeHtml(giftDisplay.amount) +
          "</span>" +
          '<span class="my-giftcard-card__amount">' +
          escapeHtml(giftDisplay.amount) +
          "</span></span></div>";

      card.innerHTML =
        '<div class="my-giftcard-card__shell">' +
        getGiftcardRibbonHtml(isDisabled) +
        '<span class="my-coupon-card__status my-coupon-card__status--' +
        statusMod +
        '">' +
        escapeHtml(statusLabel) +
        "</span>" +
        '<p class="my-giftcard-card__title">' +
        escapeHtml(t("common.gift_card")) +
        "</p>" +
        valueHtml +
        (spendHint
          ? '<p class="my-giftcard-card__hint">' +
            escapeHtml(spendHint) +
            "</p>"
          : "") +
        giftExpiryHtml +
        '<div class="my-giftcard-card__code-row">' +
        '<code class="my-giftcard-card__code">' +
        escapeHtml(giftDisplayCode) +
        "</code>" +
        '<button type="button" class="my-coupon-card__copy" data-redeem-id="' +
        escapeHtml(String(redeemId)) +
        '" aria-label="' +
        escapeHtml(t("common.copy_code")) +
        '"' +
        (redeemId && !isDisabled
          ? ""
          : ' data-copy-default-disabled="true" disabled') +
        ">" +
        COUPON_COPY_ICON_SVG +
        "</button></div>" +
        "</div>";
    } else {
      // Same amount / % / Free Product display as Redeem EcoCredits
      const valueHtml = formatTicketValueHtmlByPrefix(
        snap,
        "my-coupon-card",
        item,
      );

      card.innerHTML =
        '<div class="my-coupon-card__shell">' +
        '<div class="my-coupon-card__body">' +
        '<span class="my-coupon-card__status my-coupon-card__status--' +
        statusMod +
        '">' +
        escapeHtml(statusLabel) +
        "</span>" +
        '<div class="my-coupon-card__top">' +
        valueHtml +
        (minPurchase
          ? '<p class="my-coupon-card__min">' + minPurchase + "</p>"
          : "") +
        "</div>" +
        '<div class="my-coupon-card__foot">' +
        '<p class="my-coupon-card__type">' +
        escapeHtml(typeLabel) +
        "</p>" +
        '<div class="my-coupon-card__code-row">' +
        '<code class="my-coupon-card__code">' +
        escapeHtml(displayCode) +
        "</code>" +
        '<button type="button" class="my-coupon-card__copy" data-coupon-code="' +
        escapeHtml(code) +
        '" aria-label="' +
        escapeHtml(t("common.copy_code")) +
        '"' +
        (code && !isDisabled
          ? ""
          : ' data-copy-default-disabled="true" disabled') +
        ">" +
        COUPON_COPY_ICON_SVG +
        "</button></div>" +
        '<button type="button" class="my-coupon-card__rules" data-coupon-rules>' +
        escapeHtml(t("coupons.rules")) +
        " >></button>" +
        "</div></div></div>";
      const discountShell = card.querySelector(".my-coupon-card__shell");
      if (discountShell) {
        mountCouponTicketBgToShell(discountShell, isDisabled);
      }
    }

    const copyBtn = card.querySelector(".my-coupon-card__copy");
    if (copyBtn) {
      copyBtn.addEventListener("click", function () {
        if (copyBtn.disabled) return;
        const redeemId = copyBtn.getAttribute("data-redeem-id");
        if (redeemId) {
          copyGiftCardRedeemCode(copyBtn, redeemId);
          return;
        }
        const value = copyBtn.getAttribute("data-coupon-code") || "";
        if (!value) return;
        copyTextWithTip(value, t("common.copied"), copyBtn).then(function (ok) {
          if (!ok) return;
          copyBtn.classList.add("is-copied");
          copyBtn.setAttribute("aria-label", t("common.copied"));
          setTimeout(function () {
            copyBtn.classList.remove("is-copied");
            copyBtn.setAttribute("aria-label", t("common.copy_code"));
          }, 1500);
        });
      });
    }

    const rulesBtn = card.querySelector("[data-coupon-rules]");
    if (rulesBtn) {
      rulesBtn.addEventListener("click", function () {
        openBenefitsRedeemRulesModal(item);
      });
    }

    return card;
  }

  function parseBenefitsSelectPageResponse(json) {
    if (!json || Number(json.code) !== 200 || !json.data) return null;
    const data = json.data;
    const list = Array.isArray(data.list)
      ? data.list
      : Array.isArray(data.records)
        ? data.records
        : [];
    const total = Math.max(0, Number(data.total) || list.length);
    return { list: list, total: total };
  }

  function fetchBenefitsSelectPage(
    baseUrl,
    shopifyId,
    benefitCategory,
    pageNum,
    pageSize,
  ) {
    return fetch(
      baseUrl.replace(/\/$/, "") + "/front/redeem/redeem/selectPage",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          shopifyId: shopifyId,
          benefitCategory: benefitCategory,
          pageNum: pageNum,
          pageSize: pageSize,
        }),
      },
    )
      .then(function (res) {
        return res.json();
      })
      .then(parseBenefitsSelectPageResponse);
  }

  function fetchBenefitsFullList(baseUrl, shopifyId, benefitCategory) {
    return fetchBenefitsSelectPage(
      baseUrl,
      shopifyId,
      benefitCategory,
      1,
      1,
    ).then(function (probe) {
      if (!probe) return { list: [], total: 0 };
      const total = probe.total;
      if (total === 0) return { list: [], total: 0 };

      const chunkSize = Math.min(BENEFITS_FETCH_CHUNK, total);
      return fetchBenefitsSelectPage(
        baseUrl,
        shopifyId,
        benefitCategory,
        1,
        chunkSize,
      ).then(function (firstPage) {
        if (!firstPage) return { list: [], total: 0 };
        let all = firstPage.list.slice();
        if (all.length >= total) {
          return { list: all.slice(0, total), total: total };
        }

        const pageCount = Math.ceil(total / chunkSize);
        let chain = Promise.resolve();
        for (let page = 2; page <= pageCount; page++) {
          chain = chain.then(function () {
            return fetchBenefitsSelectPage(
              baseUrl,
              shopifyId,
              benefitCategory,
              page,
              chunkSize,
            ).then(function (pageData) {
              if (pageData && pageData.list.length) {
                all = all.concat(pageData.list);
              }
            });
          });
        }
        return chain.then(function () {
          return { list: all.slice(0, total), total: total };
        });
      });
    });
  }

  function buildBenefitsClientPageView(fullList, total, pageNum) {
    const safeTotal = Math.max(0, Number(total) || 0);
    const all = Array.isArray(fullList) ? fullList : [];
    const effectiveTotal = safeTotal > 0 ? safeTotal : all.length;
    const pages = Math.max(
      1,
      Math.ceil(effectiveTotal / BENEFIT_PAGE_SIZE) || 1,
    );
    let current = Math.max(1, Number(pageNum) || 1);
    if (current > pages) current = pages;
    const start = (current - 1) * BENEFIT_PAGE_SIZE;
    const list = all.slice(start, start + BENEFIT_PAGE_SIZE);
    return {
      list: list,
      total: effectiveTotal,
      pageNum: current,
      pageSize: BENEFIT_PAGE_SIZE,
      pages: pages,
    };
  }

  function resolveBenefitsListPagination(data, fallbackPageNum, pageSize) {
    const size =
      Number(data && data.pageSize) > 0
        ? Number(data.pageSize)
        : Number(pageSize) || BENEFIT_PAGE_SIZE;
    const current = Number(data && data.pageNum) || Number(fallbackPageNum) || 1;
    let totalPages = Number(data && data.pages);
    if (!Number.isFinite(totalPages) || totalPages < 1) {
      const total = Number(data && data.total);
      totalPages =
        Number.isFinite(total) && total > 0 ? Math.ceil(total / size) : 1;
    }
    return { current: current, totalPages: totalPages, pageSize: size };
  }

  function renderMyBenefitsPagination(
    container,
    data,
    loadFn,
    fallbackPageNum,
    pageSize,
  ) {
    container.innerHTML = "";
    const paging = resolveBenefitsListPagination(
      data,
      fallbackPageNum,
      pageSize,
    );
    const totalPages = paging.totalPages;
    const current = paging.current;

    if (totalPages <= 1) {
      return;
    }

    const prevBtn = document.createElement("button");
    prevBtn.className = "point-store__page-btn";
    prevBtn.textContent = t("common.previous");
    prevBtn.disabled = current <= 1;
    prevBtn.addEventListener("click", function () {
      if (current > 1) {
        scrollPointsCenterToTop();
        loadFn(current - 1);
      }
    });
    container.appendChild(prevBtn);

    const start = Math.max(1, current - 2);
    const end = Math.min(totalPages, current + 2);
    for (let i = start; i <= end; i++) {
      const pageBtn = document.createElement("button");
      pageBtn.className =
        "point-store__page-btn" + (i === current ? " is-active" : "");
      pageBtn.textContent = i;
      pageBtn.addEventListener("click", function () {
        if (i !== current) {
          scrollPointsCenterToTop();
          loadFn(i);
        }
      });
      container.appendChild(pageBtn);
    }

    const nextBtn = document.createElement("button");
    nextBtn.className = "point-store__page-btn";
    nextBtn.textContent = t("common.next");
    nextBtn.disabled = current >= totalPages;
    nextBtn.addEventListener("click", function () {
      if (current < totalPages) {
        scrollPointsCenterToTop();
        loadFn(current + 1);
      }
    });
    container.appendChild(nextBtn);
  }

  function renderMyBenefitsPage(
    grid,
    pagination,
    data,
    pageNum,
    config,
    loadFn,
  ) {
    const pagingMeta = resolveBenefitsListPagination(
      data,
      pageNum,
      BENEFIT_PAGE_SIZE,
    );
    config.setCurrentPage(pagingMeta.current);
    grid.innerHTML = "";

    // Server already filters by benefitCategory; client filter would break page sizes.
    const list = Array.isArray(data.list) ? data.list : [];
    if (!list.length) {
      grid.innerHTML = getCouponsEmptyHtml();
      pagination.innerHTML = "";
      return;
    }

    list.forEach(function (item) {
      grid.appendChild(createMyBenefitCard(item, config));
    });
    renderMyBenefitsPagination(
      pagination,
      data,
      loadFn,
      pageNum,
      BENEFIT_PAGE_SIZE,
    );
    scheduleCouponCardBackgroundRefresh(grid);
  }

  let myBenefitsRequestSeq = 0;
  // TEMP: force Coupons Discount / Gift Card empty state — set false when done
  const PREVIEW_COUPONS_EMPTY = false;

  function loadMyBenefits(couponTabId, pageNum, force) {
    const baseUrl = getLoyaltyBaseUrl();
    const config = getBenefitPanelConfig(couponTabId);
    const grid = root.querySelector(config.gridSelector);
    const pagination = root.querySelector(config.paginationSelector);
    if (!grid || !pagination) return Promise.resolve();

    if (PREVIEW_COUPONS_EMPTY) {
      grid.innerHTML = getCouponsEmptyHtml();
      pagination.innerHTML = "";
      return Promise.resolve();
    }

    if (!baseUrl) {
      grid.innerHTML =
        '<p class="my-coupons__empty">' +
        escapeHtml(t("common.no_api")) +
        "</p>";
      pagination.innerHTML = "";
      return Promise.resolve();
    }

    const shopifyId = getGiftCardShopifyId();
    if (!shopifyId) {
      grid.innerHTML =
        '<p class="my-coupons__empty">' +
        escapeHtml(t("coupons.please_login")) +
        "</p>";
      pagination.innerHTML = "";
      return Promise.resolve();
    }

    const requestPage = Math.max(1, Number(pageNum) || 1);
    const loadFn = function (p) {
      loadMyBenefits(couponTabId, p, false);
    };

    function renderFromFullList(fullList, total) {
      config.setFullListCache({ list: fullList, total: total });
      const pageView = buildBenefitsClientPageView(
        fullList,
        total,
        requestPage,
      );
      renderMyBenefitsPage(
        grid,
        pagination,
        pageView,
        pageView.pageNum,
        config,
        loadFn,
      );
      return pageView;
    }

    const cachedFull = !force ? config.getFullListCache() : null;
    if (cachedFull && Array.isArray(cachedFull.list)) {
      return Promise.resolve(
        renderFromFullList(cachedFull.list, cachedFull.total),
      );
    }

    const requestSeq = ++myBenefitsRequestSeq;
    const requestTabId = couponTabId;

    grid.innerHTML =
      '<p class="my-coupons__empty">' +
      escapeHtml(t("common.loading")) +
      "</p>";
    pagination.innerHTML = "";

    return fetchBenefitsFullList(
      baseUrl,
      shopifyId,
      config.benefitCategory,
    )
      .then(function (payload) {
        if (requestSeq !== myBenefitsRequestSeq) return null;
        if (requestTabId !== getActiveCouponTabId()) return null;
        if (!payload) {
          grid.innerHTML =
            '<p class="my-coupons__empty my-coupons__empty--error">' +
            escapeHtml(t("coupons.failed_load")) +
            "</p>";
          pagination.innerHTML = "";
          return null;
        }
        return renderFromFullList(payload.list, payload.total);
      })
      .catch(function () {
        if (requestSeq !== myBenefitsRequestSeq) return null;
        if (requestTabId !== getActiveCouponTabId()) return null;
        grid.innerHTML =
          '<p class="my-coupons__empty my-coupons__empty--error">' +
          escapeHtml(t("common.network_error")) +
          "</p>";
        pagination.innerHTML = "";
        return null;
      });
  }

  function updateAssetCounts(force) {
    const baseUrl = getLoyaltyBaseUrl();
    const shopifyId = getGiftCardShopifyId();
    const couponEl = root.querySelector("[data-asset-coupon-count]");
    const giftCardEl = root.querySelector("[data-asset-gift-card-count]");
    if (!baseUrl || !shopifyId || (!couponEl && !giftCardEl))
      return Promise.resolve();

    function fetchTotal(benefitCategory) {
      return fetch(
        baseUrl.replace(/\/$/, "") + "/front/redeem/redeem/selectPage",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            shopifyId: shopifyId,
            benefitCategory: benefitCategory,
            pageNum: 1,
            pageSize: 1,
          }),
        },
      )
        .then(function (res) {
          return res.json();
        })
        .then(function (json) {
          if (!json || json.code !== 200 || !json.data) return 0;
          return Number(json.data.total) || 0;
        })
        .catch(function () {
          return 0;
        });
    }

    return Promise.all([fetchTotal("COUPON"), fetchTotal("GIFT_CARD")]).then(
      function (totals) {
        if (couponEl) animateCountUp(couponEl, totals[0]);
        if (giftCardEl) animateCountUp(giftCardEl, totals[1]);
        if (loyaltySummaryData && loyaltyTiersCache) {
          renderOverviewLevel(loyaltySummaryData, loyaltyTiersCache);
          renderBenefitsCenter(
            loyaltySummaryData,
            loyaltyTiersCache,
            totals[0],
          );
        }
        return totals;
      },
    );
  }

  function getActiveCouponTabId() {
    const couponTabsEl = root.querySelector("[data-coupon-tabs]");
    if (!couponTabsEl) return "discount";
    const active = couponTabsEl.querySelector(
      "[data-my-coupon-subtab].is-active",
    );
    return active ? active.getAttribute("data-my-coupon-subtab") : "discount";
  }

  function refreshBenefitPanelsIfVisible() {
    const couponPanel = root.querySelector(
      '[data-points-center-panel="my-coupon"]',
    );
    if (couponPanel && !couponPanel.hidden) {
      const couponTab = getActiveCouponTabId();
      const config = getBenefitPanelConfig(couponTab);
      loadMyBenefits(couponTab, config.getCurrentPage(), true);
    }
    const benefitsPanel = root.querySelector(
      '[data-points-center-panel="benefits-center"]',
    );
    if (benefitsPanel && !benefitsPanel.hidden) {
      loadPointStore(true);
    }
    updateAssetCounts(true);
  }

  function applyPointsUpdate(availablePointsAfter) {
    if (availablePointsAfter != null) {
      syncAvailablePoints(availablePointsAfter);
      if (!loyaltySummaryData) {
        loyaltySummaryData = {
          availablePoints: availablePointsAfter,
          pendingPoints: 0,
          spentAmount: 0,
        };
      } else {
        loyaltySummaryData.availablePoints = availablePointsAfter;
      }
      updateHeaderPoints(loyaltySummaryData);
      const grid = root.querySelector("[data-points-summary-grid]");
      if (grid) renderMyPointSummary(grid, loyaltySummaryData);
      if (
        loyaltySummaryData.totalPoints != null &&
        loyaltySummaryData.currentTierId != null
      ) {
        const couponEl = root.querySelector("[data-asset-coupon-count]");
        const couponCount = couponEl ? parseCountUpNumber(couponEl.textContent) : 0;
        renderOverviewLevel(loyaltySummaryData, loyaltyTiersCache || []);
        renderBenefitsCenter(
          loyaltySummaryData,
          loyaltyTiersCache || [],
          couponCount,
        );
      }
      invalidatePointStoreCache();
      invalidateBenefitCaches();
      if (isMyRewardsPanelActive()) {
        loadPointsHistory(1);
      }
      refreshBenefitPanelsIfVisible();
      return;
    }
    fetchLoyaltySummary(true).then(function (data) {
      const grid = root.querySelector("[data-points-summary-grid]");
      if (grid && data) renderMyPointSummary(grid, data);
      Promise.all([fetchLoyaltyTiers(true), fetchBonusRulesSimple(true)]).then(
        function (results) {
          const tiers = results[0];
          updateAssetCounts(true).then(function (totals) {
            if (data) {
              renderOverviewLevel(data, tiers);
              renderBenefitsCenter(data, tiers, totals[0]);
            }
          });
        },
      );
      loadEarnPoints(true);
      invalidatePointStoreCache();
      invalidateBenefitCaches();
      refreshBenefitPanelsIfVisible();
    });
  }

  function isMyRewardsPanelActive() {
    const panel = root.querySelector('[data-points-center-panel="my-rewards"]');
    return !!(panel && panel.classList.contains("is-active") && !panel.hidden);
  }

  function ensureMyRewardsData(options) {
    loadMyPointSummary(true, options);
    loadPointsHistory(1);
  }

  function setPointSummaryValue(el, stat) {
    if (!el) return;
    if (stat.kind === "currency") {
      el.textContent =
        "$" +
        Number(stat.value || 0).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
    } else {
      el.textContent = numberFormatter.format(Math.round(stat.value || 0));
    }
    el.dataset.countupValue = String(stat.value || 0);
  }

  // ── Refer-a-friend ─────────────────────────────────────────────────
  function getReferPanel() {
    return root.querySelector('[data-points-center-panel="refer-a-friend"]');
  }

  function getReferPointsApiPath() {
    const panel = getReferPanel();
    const customPath = panel && panel.getAttribute("data-refer-points-api");
    const path = (customPath || REFERRAL_REFERRER_POINTS_PATH).trim();
    return path.charAt(0) === "/" ? path : "/" + path;
  }

  function getReferRecordsApiPath() {
    const panel = getReferPanel();
    const customPath = panel && panel.getAttribute("data-refer-records-api");
    const path = (customPath || REFERRAL_RECORDS_PATH).trim();
    return path.charAt(0) === "/" ? path : "/" + path;
  }

  function getActiveReferReferrerTabId() {
    const panel = getReferPanel();
    if (!panel) return "signup";
    const active = panel.querySelector(
      ".refer-referrer [data-refer-referrer-tab].is-active",
    );
    return (
      (active && active.getAttribute("data-refer-referrer-tab")) || "signup"
    );
  }

  function formatReferRecordDate(value) {
    if (!value) return "-";
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  function getReferRecordStatusBadgeHtml(status) {
    if (!status) {
      return getPointsHistoryStatusBadgeHtml("default", t("common.dash"));
    }
    return getPointsHistoryStatusBadgeHtml(
      getPointsHistoryStatusMod(status),
      getPointsHistoryStatusLabel(status),
    );
  }

  function parseReferRecordsResponse(json) {
    if (!json || json.code !== 200 || json.data == null) return null;
    const data = json.data;
    if (Array.isArray(data)) {
      return { records: data, totalPoints: null };
    }
    return {
      records: Array.isArray(data.records) ? data.records : [],
      totalPoints: data.totalPoints != null ? Number(data.totalPoints) : null,
    };
  }

  function getReferRecordsElements(tabId) {
    const isSignup = tabId === "signup";
    return {
      tbody: root.querySelector(
        isSignup ? "[data-refer-signup-tbody]" : "[data-refer-purchase-tbody]",
      ),
      listEl: root.querySelector(
        isSignup ? "[data-refer-signup-list]" : "[data-refer-purchase-list]",
      ),
    };
  }

  function setReferRecordsMessage(tbody, listEl, message) {
    if (tbody) {
      tbody.innerHTML =
        '<tr class="refer-referrer__empty-row"><td colspan="4">' +
        escapeHtml(message) +
        "</td></tr>";
    }
    if (listEl) {
      listEl.innerHTML =
        '<p class="refer-referrer__list-msg">' + escapeHtml(message) + "</p>";
    }
  }

  function renderReferRecordsTable(tbody, listEl, records, emptyMessage) {
    const list = Array.isArray(records) ? records : [];
    if (!list.length) {
      setReferRecordsMessage(tbody, listEl, emptyMessage);
      return;
    }

    if (tbody) {
      tbody.innerHTML = list
        .map(function (row) {
          const points =
            row && row.pointsAmount != null
              ? numberFormatter.format(Number(row.pointsAmount))
              : "";
          return (
            "<tr>" +
            "<td>" +
            escapeHtml(formatReferRecordDate(row && row.createdAt)) +
            "</td>" +
            "<td>" +
            escapeHtml((row && row.referredEmail) || "") +
            "</td>" +
            "<td>" +
            escapeHtml(points) +
            "</td>" +
            '<td class="refer-referrer__status">' +
            getReferRecordStatusBadgeHtml(row && row.status) +
            "</td>" +
            "</tr>"
          );
        })
        .join("");
    }

    if (listEl) {
      listEl.innerHTML = list
        .map(function (row) {
          const points =
            row && row.pointsAmount != null
              ? numberFormatter.format(Number(row.pointsAmount))
              : "";
          return (
            '<article class="refer-referrer-card">' +
            '<div class="refer-referrer-card__top">' +
            '<time class="refer-referrer-card__date">' +
            escapeHtml(formatReferRecordDate(row && row.createdAt)) +
            "</time>" +
            '<span class="refer-referrer-card__status">' +
            getReferRecordStatusBadgeHtml(row && row.status) +
            "</span>" +
            "</div>" +
            '<p class="refer-referrer-card__email">' +
            escapeHtml((row && row.referredEmail) || "") +
            "</p>" +
            '<p class="refer-referrer-card__points">' +
            escapeHtml(points) +
            "</p>" +
            "</article>"
          );
        })
        .join("");
    }
  }

  function loadReferRecords(tabId, force) {
    const pointSource = REFER_POINT_SOURCE[tabId];
    if (!pointSource) return Promise.resolve(null);

    const emptyMessage =
      tabId === "signup" ? t("refer.empty_signup") : t("refer.empty_purchase");
    const elements = getReferRecordsElements(tabId);
    const tbody = elements.tbody;
    const listEl = elements.listEl;

    if (!force && referRecordsCache[pointSource]) {
      const cached = referRecordsCache[pointSource];
      renderReferRecordsTable(tbody, listEl, cached.records, emptyMessage);
      if (getActiveReferReferrerTabId() === tabId) {
        if (cached.totalPoints != null) {
          renderReferReferrerPoints(cached.totalPoints);
        } else {
          loadReferReferrerPoints(false);
        }
      }
      return Promise.resolve(cached);
    }

    const baseUrl = getLoyaltyBaseUrl();
    const shopifyId = getGiftCardShopifyId();
    if (!baseUrl || !shopifyId) {
      renderReferRecordsTable(tbody, listEl, [], emptyMessage);
      return Promise.resolve(null);
    }

    if (force) {
      delete referRecordsCache[pointSource];
      delete referRecordsPromises[pointSource];
    }

    if (!force && referRecordsPromises[pointSource]) {
      return referRecordsPromises[pointSource];
    }

    setReferRecordsMessage(tbody, listEl, t("common.loading"));

    referRecordsPromises[pointSource] = fetch(
      baseUrl.replace(/\/$/, "") + getReferRecordsApiPath(),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          shopifyId: shopifyId,
          pointSource: pointSource,
        }),
      },
    )
      .then(function (res) {
        if (!res.ok) throw new Error("HTTP_" + res.status);
        return res.json();
      })
      .then(function (json) {
        const parsed = parseReferRecordsResponse(json);
        if (!parsed) {
          renderReferRecordsTable(tbody, listEl, [], emptyMessage);
          return null;
        }
        referRecordsCache[pointSource] = parsed;
        renderReferRecordsTable(tbody, listEl, parsed.records, emptyMessage);
        if (getActiveReferReferrerTabId() === tabId) {
          if (parsed.totalPoints != null) {
            renderReferReferrerPoints(parsed.totalPoints);
          } else {
            loadReferReferrerPoints(false);
          }
        }
        return parsed;
      })
      .catch(function () {
        renderReferRecordsTable(tbody, listEl, [], emptyMessage);
        return null;
      })
      .finally(function () {
        delete referRecordsPromises[pointSource];
      });

    return referRecordsPromises[pointSource];
  }

  function fallbackCopyReferShareLink(input) {
    if (!input) return false;
    input.select();
    input.setSelectionRange(0, 99999);
    try {
      return document.execCommand("copy");
    } catch (e) {
      return false;
    }
  }

  function copyReferShareLink(input) {
    const text = (input && input.value ? String(input.value) : "").trim();
    if (!text) return Promise.resolve(false);

    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard
        .writeText(text)
        .then(function () {
          return true;
        })
        .catch(function () {
          return fallbackCopyReferShareLink(input);
        });
    }

    return Promise.resolve(fallbackCopyReferShareLink(input));
  }

  function setReferShareCopyBtnLoading(btn, isLoading) {
    if (!btn) return;
    btn.classList.toggle("is-loading", !!isLoading);
    btn.disabled = !!isLoading;
    if (isLoading) {
      btn.setAttribute("aria-busy", "true");
      return;
    }
    btn.removeAttribute("aria-busy");
  }

  function initReferShareLink() {
    const panel = getReferPanel();
    if (!panel) return;
    const wrap = panel.querySelector("[data-points-center-refer-share]");
    if (!wrap || wrap.dataset.referShareInit === "true") return;

    const input = wrap.querySelector("[data-refer-link]");
    const copyBtn = wrap.querySelector("[data-refer-copy]");
    if (copyBtn && input) {
      copyBtn.addEventListener("click", function () {
        if (!(input.value || "").trim() || copyBtn.classList.contains("is-loading")) {
          return;
        }

        setReferShareCopyBtnLoading(copyBtn, true);
        let referShareCopyOk = false;
        copyReferShareLink(input)
          .then(function (ok) {
            referShareCopyOk = !!ok;
            if (ok) {
              copyBtn.classList.add("is-copied");
              copyBtn.setAttribute(
                "aria-label",
                t("refer.copy_share_link_success"),
              );
              window.setTimeout(function () {
                copyBtn.classList.remove("is-copied");
                copyBtn.setAttribute(
                  "aria-label",
                  t("refer.copy_share_link"),
                );
              }, 1600);
            }
          })
          .finally(function () {
            setReferShareCopyBtnLoading(copyBtn, false);
            if (referShareCopyOk) {
              showCopiedTooltip(copyBtn, t("common.copied"));
            }
          });
      });
    }

    wrap.dataset.referShareInit = "true";
  }

  function loadReferShareLink(force) {
    const panel = getReferPanel();
    const input =
      panel &&
      panel.querySelector("[data-points-center-refer-share] [data-refer-link]");
    if (!input) return Promise.resolve(null);

    if (!force && referShareLinkLoaded && (input.value || "").trim()) {
      return Promise.resolve(input.value.trim());
    }

    const baseUrl = getLoyaltyBaseUrl();
    const shopifyId = getGiftCardShopifyId();

    if (!shopifyId) {
      input.value = "";
      input.placeholder = t("refer.share_link_login");
      return Promise.resolve(null);
    }

    if (!baseUrl) {
      input.value = "";
      input.placeholder = t("refer.share_link_unavailable");
      return Promise.resolve(null);
    }

    if (!force && referShareLinkPromise) {
      return referShareLinkPromise;
    }

    input.value = "";
    input.placeholder = t("refer.share_link_loading");

    referShareLinkPromise = fetch(
      baseUrl.replace(/\/$/, "") + REFERRAL_SHARE_LINK_PATH,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shopifyId: shopifyId }),
      },
    )
      .then(function (res) {
        if (!res.ok) throw new Error("HTTP_" + res.status);
        return res.json();
      })
      .then(function (json) {
        const shareUrl =
          json && json.code === 200 && json.data && json.data.shareUrl
            ? String(json.data.shareUrl).trim()
            : "";
        if (shareUrl) {
          input.value = shareUrl;
          input.placeholder = "";
          referShareLinkLoaded = true;
          return shareUrl;
        }
        input.value = "";
        input.placeholder = t("refer.share_link_failed");
        return null;
      })
      .catch(function () {
        input.value = "";
        input.placeholder = t("refer.share_link_failed");
        return null;
      })
      .finally(function () {
        referShareLinkPromise = null;
      });

    return referShareLinkPromise;
  }

  function loadReferReferrerSection(force) {
    initReferShareLink();
    loadReferShareLink(force);
    const tabId = getActiveReferReferrerTabId();
    return loadReferRecords(tabId, force);
  }

  function parseReferReferrerPoints(json) {
    if (!json || json.code !== 200) return null;
    const data = json.data;
    if (data == null) return null;
    if (typeof data === "number") return data;

    const fields = [
      "totalPoints",
      "referrerPoints",
      "points",
      "availablePoints",
      "earnedPoints",
    ];
    for (let i = 0; i < fields.length; i++) {
      const value = Number(data[fields[i]]);
      if (Number.isFinite(value)) return value;
    }
    return null;
  }

  function renderReferReferrerPoints(points) {
    const el = root.querySelector("[data-refer-referrer-points]");
    if (!el) return;
    el.textContent =
      points == null || !Number.isFinite(points)
        ? "0"
        : numberFormatter.format(points);
  }

  function loadReferReferrerPoints(force) {
    const el = root.querySelector("[data-refer-referrer-points]");
    if (!el) return Promise.resolve(null);

    if (!force && referReferrerPointsCache != null) {
      renderReferReferrerPoints(referReferrerPointsCache);
      return Promise.resolve(referReferrerPointsCache);
    }

    const baseUrl = getLoyaltyBaseUrl();
    const shopifyId = getGiftCardShopifyId();
    if (!baseUrl || !shopifyId) {
      renderReferReferrerPoints(null);
      return Promise.resolve(null);
    }

    if (!force && referReferrerPointsPromise) {
      return referReferrerPointsPromise;
    }

    if (force) {
      referReferrerPointsCache = null;
      referReferrerPointsPromise = null;
    }

    referReferrerPointsPromise = fetch(
      baseUrl.replace(/\/$/, "") + getReferPointsApiPath(),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ shopifyId: shopifyId }),
      },
    )
      .then(function (res) {
        if (!res.ok) throw new Error("HTTP_" + res.status);
        return res.json();
      })
      .then(function (json) {
        const points = parseReferReferrerPoints(json);
        referReferrerPointsCache = points;
        renderReferReferrerPoints(points);
        return points;
      })
      .catch(function () {
        renderReferReferrerPoints(null);
        return null;
      })
      .finally(function () {
        referReferrerPointsPromise = null;
      });

    return referReferrerPointsPromise;
  }

  const tabs = root.querySelectorAll(
    ".points-center-nav [data-points-center-tab]",
  );
  const panels = root.querySelectorAll("[data-points-center-panel]");
  let activateCouponTab = null;

  // ── Tab navigation & view state ────────────────────────────────────
  function isValidMainTab(tabId) {
    return (
      !!tabId &&
      !!root.querySelector('[data-points-center-panel="' + tabId + '"]')
    );
  }

  function loadPointsCenterViewState() {
    try {
      const raw = sessionStorage.getItem(POINTS_CENTER_VIEW_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function savePointsCenterViewState() {
    try {
      const activePanel = root.querySelector(
        "[data-points-center-panel].is-active",
      );
      const mainTab = activePanel
        ? activePanel.getAttribute("data-points-center-panel")
        : DEFAULT_POINTS_CENTER_TAB;
      const couponTabsEl = root.querySelector("[data-coupon-tabs]");
      const activeCoupon = couponTabsEl
        ? couponTabsEl.querySelector("[data-my-coupon-subtab].is-active")
        : null;
      const couponTab = activeCoupon
        ? activeCoupon.getAttribute("data-my-coupon-subtab")
        : null;
      const ordersTabsEl = root.querySelector("[data-orders-tabs]");
      const activeOrders = ordersTabsEl
        ? ordersTabsEl.querySelector("[data-orders-subtab].is-active")
        : null;
      const ordersTab = activeOrders
        ? activeOrders.getAttribute("data-orders-subtab")
        : ordersActiveSubtab;
      sessionStorage.setItem(
        POINTS_CENTER_VIEW_KEY,
        JSON.stringify({
          mainTab: mainTab,
          couponTab: couponTab,
          ordersTab: ordersTab,
        }),
      );
    } catch (e) {
      /* ignore quota / private mode */
    }
  }

  function scrollPointsCenterToTop(options) {
    options = options || {};
    if (options.skip) return;

    const behavior = options.smooth ? "smooth" : "auto";
    window.scrollTo({ top: 0, behavior: behavior });
  }

  function getPointsCenterScrollOffset(extraGap) {
    const gap = typeof extraGap === "number" ? extraGap : 16;
    let offset = gap;

    document
      .querySelectorAll(
        ".shopify-section-group-header-group .shopify-section, .header-policy",
      )
      .forEach(function (el) {
        const rect = el.getBoundingClientRect();
        if (rect.height > 0 && rect.top < 160 && rect.bottom > 0) {
          offset = Math.max(offset, rect.bottom + gap);
        }
      });

    if (isOverviewMobileViewport()) {
      const panelHeader = root.querySelector(
        '[data-points-center-panel="benefits-center"] .points-center-panel__header',
      );
      if (panelHeader) {
        const style = getComputedStyle(panelHeader);
        if (style.display !== "none" && style.visibility !== "hidden") {
          offset = Math.max(
            offset,
            panelHeader.getBoundingClientRect().bottom + gap,
          );
        }
      }
    }

    return offset;
  }

  function activateTab(tabId, options) {
    options = options || {};
    if (tabId === "my-gift-card") {
      tabId = "my-coupon";
      options.couponTab = options.couponTab || "gift-card";
    }
    if (!isValidMainTab(tabId)) tabId = DEFAULT_POINTS_CENTER_TAB;

    tabs.forEach(function (tab) {
      const isActive = tab.getAttribute("data-points-center-tab") === tabId;
      tab.classList.toggle("is-active", isActive);
      if (isActive) {
        tab.setAttribute("aria-current", "page");
      } else {
        tab.removeAttribute("aria-current");
      }
    });

    panels.forEach(function (panel) {
      const isActive = panel.getAttribute("data-points-center-panel") === tabId;
      panel.classList.toggle("is-active", isActive);
      panel.hidden = !isActive;
    });

    if (tabId === "my-rewards") {
      const replayCountUp = !minisCreditsCountUpPlayed;
      if (replayCountUp) minisCreditsCountUpPlayed = true;
      requestAnimationFrame(function () {
        ensureMyRewardsData({ replayCountUp: replayCountUp });
      });
    }

    if (tabId === "my-orders") {
      ensureOrdersData({ ordersTab: options.ordersTab });
    }

    if (tabId === "my-coupon") {
      const couponTabId = resolveCouponSubTab(options.couponTab);
      if (typeof activateCouponTab === "function") {
        activateCouponTab(couponTabId, { skipSave: true });
      }
      const config = getBenefitPanelConfig(couponTabId);
      loadMyBenefits(couponTabId, config.getCurrentPage(), false);
    } else if (tabId === "refer-a-friend") {
      loadReferReferrerSection(false);
    } else if (tabId === "benefits-center") {
      loadPointStore(false);
      const couponEl = root.querySelector("[data-asset-coupon-count]");
      const couponCount = couponEl ? parseCountUpNumber(couponEl.textContent) : 0;
      const replayCountUp = !benefitsHeroCountUpPlayed;
      if (replayCountUp) benefitsHeroCountUpPlayed = true;
      requestAnimationFrame(function () {
        renderBenefitsCenter(
          loyaltySummaryData,
          loyaltyTiersCache || [],
          couponCount,
          { replayCountUp: replayCountUp },
        );
      });
    }

    if (!options.skipSave) savePointsCenterViewState();

    if (!options.skipScroll) {
      requestAnimationFrame(function () {
        scrollPointsCenterToTop();
      });
    }
  }

  const POINTS_CENTER_BIRTHDAY_FOCUS_KEY =
    "miniforum:points-center:focus-birthday";

  function maybeFocusProfileBirthdayFromMembership() {
    try {
      if (sessionStorage.getItem(POINTS_CENTER_BIRTHDAY_FOCUS_KEY) !== "1") {
        return;
      }
      sessionStorage.removeItem(POINTS_CENTER_BIRTHDAY_FOCUS_KEY);
      goToProfileBirthdayField();
    } catch (e) {}
  }

  function goToProfileBirthdayField() {
    activateTab("profile", { skipScroll: true });
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        const profileContent = root.querySelector("[data-profile-content]");
        const editBtn =
          profileContent &&
          profileContent.querySelector("[data-profile-edit]");
        if (editBtn) editBtn.click();
        const birthdayInput = root.querySelector("[data-profile-birthday]");
        const birthdayWrap = root.querySelector("[data-profile-birthday-wrap]");
        const focusTarget = birthdayWrap || birthdayInput;
        if (focusTarget && typeof focusTarget.scrollIntoView === "function") {
          focusTarget.scrollIntoView({ block: "center", behavior: "smooth" });
        }
        if (birthdayInput && typeof birthdayInput.focus === "function") {
          birthdayInput.focus({ preventScroll: true });
        }
      });
    });
  }

  function restorePointsCenterView() {
    const state = loadPointsCenterViewState();
    if (!state || !state.mainTab) return;
    let mainTab = state.mainTab;
    let couponTab = state.couponTab;
    if (mainTab === "my-gift-card") {
      mainTab = "my-coupon";
      couponTab = couponTab || "gift-card";
    }
    if (!isValidMainTab(mainTab)) return;
    if (mainTab === DEFAULT_POINTS_CENTER_TAB) return;
    activateTab(mainTab, {
      couponTab: couponTab,
      ordersTab: state.ordersTab,
      skipSave: true,
      skipScroll: true,
    });
  }

  function isValidCouponTab(tabId) {
    return tabId === "discount" || tabId === "gift-card";
  }

  function resolveCouponSubTab(requestedTabId) {
    if (requestedTabId && isValidCouponTab(requestedTabId))
      return requestedTabId;
    const saved = loadPointsCenterViewState();
    if (saved && saved.couponTab && isValidCouponTab(saved.couponTab)) {
      return saved.couponTab;
    }
    return "discount";
  }

  tabs.forEach(function (tab) {
    tab.addEventListener("click", function () {
      activateTab(tab.getAttribute("data-points-center-tab"));
    });
  });

  root.addEventListener("click", function (e) {
    const backBtn = e.target.closest("[data-points-center-back]");
    if (!backBtn || !root.contains(backBtn)) return;
    // Desktop keeps normal titles; only mobile back chrome navigates.
    if (window.matchMedia && !window.matchMedia("(max-width: 768px)").matches) {
      return;
    }
    e.preventDefault();
    activateTab(
      backBtn.getAttribute("data-points-center-tab") ||
        DEFAULT_POINTS_CENTER_TAB,
    );
  });

  root.addEventListener("keydown", function (e) {
    if (e.key !== "Enter" && e.key !== " ") return;
    const backBtn = e.target.closest("[data-points-center-back]");
    if (!backBtn || !root.contains(backBtn)) return;
    if (window.matchMedia && !window.matchMedia("(max-width: 768px)").matches) {
      return;
    }
    e.preventDefault();
    activateTab(
      backBtn.getAttribute("data-points-center-tab") ||
        DEFAULT_POINTS_CENTER_TAB,
    );
  });

  // Handle "Get" / action buttons that should switch tabs
  root
    .querySelectorAll(
      "[data-scroll-overview-earn], [data-points-center-tab].points-center-asset-card__action, [data-points-center-tab].points-overview-asset-card__earn, [data-points-center-tab].points-benefits-hero__stat-link, [data-points-center-tab].points-my-benefits__more, [data-points-center-tab].points-overview-stat__action, [data-points-center-tab].points-center-overview-profile__edit",
    )
    .forEach(function (actionBtn) {
      actionBtn.addEventListener("click", function (e) {
        e.preventDefault();
        if (actionBtn.hasAttribute("data-scroll-overview-earn")) {
          openOverviewEarnSection();
          return;
        }
        const targetTab = actionBtn.getAttribute("data-points-center-tab");
        const couponTab = actionBtn.getAttribute("data-my-coupon-subtab");
        const ordersTab = actionBtn.getAttribute("data-orders-subtab");
        if (targetTab) {
          const options = {};
          if (couponTab) options.couponTab = couponTab;
          if (ordersTab) options.ordersTab = ordersTab;
          activateTab(
            targetTab,
            Object.keys(options).length ? options : undefined,
          );
        }
      });
    });

  root.addEventListener("click", function (e) {
    const emptyBtn = e.target.closest(".my-coupons__empty-btn");
    if (!emptyBtn || !root.contains(emptyBtn)) return;
    e.preventDefault();
    activateTab(
      emptyBtn.getAttribute("data-points-center-tab") || "benefits-center",
    );
  });

  function getBenefitsRedeemScrollOffset() {
    return getPointsCenterScrollOffset(32);
  }

  let benefitsRedeemScrollToken = 0;

  function scrollToBenefitsRedeemStore() {
    const title = root.querySelector("#benefits-redeem-store-title");
    if (!title) return;

    const token = ++benefitsRedeemScrollToken;
    const scrollingElement = document.scrollingElement || document.documentElement;

    // Cancel any in-progress smooth scroll so the next target is recalculated cleanly.
    scrollingElement.scrollTop = scrollingElement.scrollTop;

    requestAnimationFrame(function () {
      if (token !== benefitsRedeemScrollToken) return;

      const offset = getBenefitsRedeemScrollOffset();
      const targetTop = Math.max(
        0,
        title.getBoundingClientRect().top + scrollingElement.scrollTop - offset,
      );
      const delta = Math.abs(targetTop - scrollingElement.scrollTop);

      scrollingElement.scrollTo({
        top: targetTop,
        behavior: delta > 24 ? "smooth" : "auto",
      });
    });
  }

  root.addEventListener("click", function (e) {
    const redeemLink = e.target.closest("[data-benefits-scroll-redeem]");
    if (!redeemLink || !root.contains(redeemLink)) return;
    e.preventDefault();
    scrollToBenefitsRedeemStore();
  });

  (function initBenefitsHelpModal() {
    const modal = root.querySelector("[data-benefits-help-modal]");
    if (!modal) return;
    const listEl = modal.querySelector("[data-benefits-help-list]");

    function openBenefitsHelpModal() {
      renderBenefitsHelpModalList(listEl);
      modal.hidden = false;
      document.documentElement.style.overflow = "hidden";
    }

    function closeBenefitsHelpModal() {
      closePointsCenterBottomSheet(modal, {
        onClosed: function () {
          const historyModal = root.querySelector(
            "[data-benefits-history-modal]",
          );
          const rulesModal = root.querySelector("[data-benefits-rules-modal]");
          if (
            (!historyModal || historyModal.hidden) &&
            (!rulesModal || rulesModal.hidden)
          ) {
            document.documentElement.style.overflow = "";
          }
        },
      });
    }

    root.querySelectorAll("[data-my-benefits-help]").forEach(function (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        openBenefitsHelpModal();
      });
    });

    modal.querySelectorAll("[data-benefits-help-close]").forEach(function (el) {
      el.addEventListener("click", closeBenefitsHelpModal);
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && !modal.hidden) closeBenefitsHelpModal();
    });
  })();

  const couponTabsContainer = root.querySelector("[data-coupon-tabs]");
  if (couponTabsContainer) {
    const couponTabs = couponTabsContainer.querySelectorAll(
      "[data-my-coupon-subtab]",
    );
    const couponPanes = root.querySelectorAll("[data-coupon-pane]");

    activateCouponTab = function (tabId, options) {
      options = options || {};
      if (!isValidCouponTab(tabId)) tabId = "discount";

      couponTabs.forEach(function (tab) {
        const isActive = tab.getAttribute("data-my-coupon-subtab") === tabId;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
      });

      couponPanes.forEach(function (pane) {
        const isActive = pane.getAttribute("data-coupon-pane") === tabId;
        pane.classList.toggle("is-active", isActive);
        pane.hidden = !isActive;
      });

      scheduleCouponCardBackgroundRefresh(
        root.querySelector('[data-coupon-pane="' + tabId + '"]'),
      );

      if (!options.skipSave) savePointsCenterViewState();
    };

    couponTabs.forEach(function (tab) {
      tab.addEventListener("click", function () {
        const tabId = tab.getAttribute("data-my-coupon-subtab");
        if (!tabId || !isValidCouponTab(tabId)) return;
        if (tabId === getActiveCouponTabId()) return;
        scrollPointsCenterToTop();
        activateCouponTab(tabId);
        loadMyBenefits(tabId, 1, true);
      });
    });

    window.addEventListener("resize", function () {
      const couponPanel = root.querySelector(
        '[data-points-center-panel="my-coupon"]',
      );
      if (couponPanel && !couponPanel.hidden) {
        syncCouponCardScales(couponPanel);
      }
    });
  }

  function openOverviewEarnSection() {
    activateTab("account-overview", { skipScroll: true });
    const earnSection =
      root.querySelector(".points-overview-membership--earn") ||
      root.querySelector("[data-overview-earn-carousel]");
    if (!earnSection) return;

    function scrollToEarnSection() {
      requestAnimationFrame(function () {
        const offset = isOverviewMobileViewport() ? 120 : 180;
        const top =
          earnSection.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      });
    }

    expandOverviewEarnOnMobile(scrollToEarnSection);
  }

  function openPointsRewardsHistory() {
    activateTab("my-rewards", { skipScroll: true });
    const historySection = root.querySelector(".points-history");
    if (historySection) {
      historySection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  (function initOrdersPanel() {
    const ordersTabs = root.querySelectorAll("[data-orders-subtab]");
    if (!ordersTabs.length) return;

    activateOrdersSubtab = function (tabId, options) {
      options = options || {};
      if (!isValidOrdersSubtab(tabId)) tabId = "all";
      const changed = tabId !== ordersActiveSubtab;
      ordersActiveSubtab = tabId;

      ordersTabs.forEach(function (tab) {
        const isActive = tab.getAttribute("data-orders-subtab") === tabId;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
        if (
          isActive &&
          !options.skipTabScroll &&
          typeof tab.scrollIntoView === "function"
        ) {
          try {
            tab.scrollIntoView({
              behavior: "smooth",
              inline: "center",
              block: "nearest",
            });
          } catch (err) {
            tab.scrollIntoView(false);
          }
        }
      });

      if (!options.skipSave) savePointsCenterViewState();
      if (changed || options.forceReload) {
        resetAndLoadOrders(tabId);
      }
    };

    ordersTabs.forEach(function (tab) {
      tab.addEventListener("click", function () {
        const tabId = tab.getAttribute("data-orders-subtab");
        if (!tabId || !isValidOrdersSubtab(tabId)) return;
        activateOrdersSubtab(tabId);
      });
    });

    const prevBtn = root.querySelector("[data-orders-prev]");
    const nextBtn = root.querySelector("[data-orders-next]");
    if (prevBtn) {
      prevBtn.addEventListener("click", function () {
        if (ordersLoading || ordersPageIndex <= 0) return;
        const pageIndex = ordersPageIndex - 1;
        scrollPointsCenterToTop({ smooth: true });
        loadOrdersPage({
          pageIndex: pageIndex,
          cursor: ordersCursorStack[pageIndex] || null,
        });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener("click", function () {
        if (ordersLoading) return;
        const pageCount = getOrdersTotalPages();
        const pageIndex = ordersPageIndex + 1;
        const cursor = ordersCursorStack[pageIndex] || ordersNextCursor || null;
        if (!ordersHasNextPage && pageIndex >= pageCount && !cursor) return;
        if (!cursor && pageIndex > 0) return;
        scrollPointsCenterToTop({ smooth: true });
        loadOrdersPage({ pageIndex: pageIndex, cursor: cursor });
      });
    }

    initOrdersDetailModal();
  })();

  // ── Redeem EcoCredits History modal (Figma 8:5186) ─────────────────
  const BENEFITS_HISTORY_PAGE_SIZE = 12;
  let benefitsHistoryTotal = 0;
  let benefitsHistoryLoadedCount = 0;
  let benefitsHistoryLoading = false;
  let benefitsHistoryPool = [];
  let benefitsHistoryApiPage = 0;

  function createBenefitsHistoryCard(item) {
    return createMyBenefitCard(item, { benefitsHistoryTicket: true });
  }

  function syncBenefitsHistoryCardScales() {
    const cards = root.querySelectorAll(
      ".benefits-history-modal__grid .my-coupon-card--discount",
    );
    if (!cards.length) return;

    if (window.matchMedia("(min-width: 769px)").matches) {
      cards.forEach(function (card) {
        const shell = card.querySelector(".my-coupon-card__shell");
        if (shell) shell.style.transform = "";
        card.style.height = "";
      });
      return;
    }

    cards.forEach(function (card) {
      const shell = card.querySelector(".my-coupon-card__shell");
      if (!shell) return;
      const width = card.getBoundingClientRect().width;
      if (!width) return;
      const scale = width / 258;
      shell.style.transform = "scale(" + scale + ")";
      card.style.height = 282 * scale + "px";
    });
  }

  function refreshBenefitsHistoryCouponCards() {
    const grid = root.querySelector("[data-benefits-history-grid]");
    if (!grid) return;
    scheduleCouponCardBackgroundRefresh(grid);
    syncCouponCardScales(grid);
    syncBenefitsHistoryCardScales();
  }

  function setBenefitsHistoryMoreLoading(isLoading) {
    const moreBtn = root.querySelector("[data-benefits-history-more]");
    if (!moreBtn) return;
    moreBtn.classList.toggle("is-loading", isLoading);
    moreBtn.disabled = !!isLoading;
    if (isLoading) {
      moreBtn.hidden = false;
      moreBtn.setAttribute("aria-busy", "true");
      moreBtn.innerHTML =
        '<span class="benefits-history-modal__more-spinner" aria-hidden="true"></span>' +
        "<span>" +
        escapeHtml(t("common.loading")) +
        "</span>";
      return;
    }
    moreBtn.removeAttribute("aria-busy");
    moreBtn.innerHTML =
      escapeHtml(t("common.view_more")) +
      ' <span aria-hidden="true">&gt;</span>';
  }

  function updateBenefitsHistoryMoreBtn() {
    const moreBtn = root.querySelector("[data-benefits-history-more]");
    if (!moreBtn) return;
    if (benefitsHistoryLoading) return;
    moreBtn.hidden = !(
      benefitsHistoryLoadedCount >= BENEFITS_HISTORY_PAGE_SIZE &&
      benefitsHistoryTotal > benefitsHistoryLoadedCount
    );
  }

  function fetchBenefitsHistoryCategory(
    baseUrl,
    shopifyId,
    benefitCategory,
    pageNum,
  ) {
    return fetch(
      baseUrl.replace(/\/$/, "") + "/front/redeem/redeem/selectPage",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          shopifyId: shopifyId,
          benefitCategory: benefitCategory,
          pageNum: pageNum,
          pageSize: BENEFITS_HISTORY_PAGE_SIZE,
        }),
      },
    )
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        if (!json || Number(json.code) !== 200 || !json.data) {
          return { list: [], total: 0 };
        }
        const data = json.data;
        const list = Array.isArray(data.list)
          ? data.list
          : Array.isArray(data.records)
            ? data.records
            : [];
        return { list: list, total: Number(data.total) || 0 };
      })
      .catch(function () {
        return { list: [], total: 0 };
      });
  }

  function sortHistoryItems(list) {
    return list.slice().sort(function (a, b) {
      return Number((b && b.id) || 0) - Number((a && a.id) || 0);
    });
  }

  function appendHistoryItemsToPool(items) {
    const seen = {};
    benefitsHistoryPool.forEach(function (item) {
      if (item && item.id != null) seen[String(item.id)] = true;
    });
    items.forEach(function (item) {
      const id = item && item.id != null ? String(item.id) : "";
      if (id && seen[id]) return;
      if (id) seen[id] = true;
      benefitsHistoryPool.push(item);
    });
  }

  function renderHistoryChunk(grid, append) {
    const start = append ? benefitsHistoryLoadedCount : 0;
    const chunk = benefitsHistoryPool.slice(
      start,
      start + BENEFITS_HISTORY_PAGE_SIZE,
    );
    if (!append) {
      grid.innerHTML = "";
      benefitsHistoryLoadedCount = 0;
    }
    if (!chunk.length && !append) {
      grid.innerHTML =
        '<p class="benefits-history-modal__empty">' +
        escapeHtml(t("redeem.no_history")) +
        "</p>";
      return;
    }
    const frag = document.createDocumentFragment();
    chunk.forEach(function (item) {
      frag.appendChild(createBenefitsHistoryCard(item));
    });
    grid.appendChild(frag);
    benefitsHistoryLoadedCount += chunk.length;
    refreshBenefitsHistoryCouponCards();
  }

  function fetchHistoryApiPage(baseUrl, shopifyId, pageNum) {
    return Promise.all([
      fetchBenefitsHistoryCategory(baseUrl, shopifyId, "COUPON", pageNum),
      fetchBenefitsHistoryCategory(baseUrl, shopifyId, "GIFT_CARD", pageNum),
    ]).then(function (results) {
      const coupon = results[0] || { list: [], total: 0 };
      const gift = results[1] || { list: [], total: 0 };
      benefitsHistoryTotal = coupon.total + gift.total;
      appendHistoryItemsToPool(sortHistoryItems(coupon.list.concat(gift.list)));
      benefitsHistoryApiPage = pageNum;
    });
  }

  function loadBenefitsRedeemHistory(append) {
    const modal = root.querySelector("[data-benefits-history-modal]");
    const grid = root.querySelector("[data-benefits-history-grid]");
    if (!modal || !grid || benefitsHistoryLoading) return Promise.resolve();

    const baseUrl = getLoyaltyBaseUrl();
    if (!baseUrl) {
      grid.innerHTML =
        '<p class="benefits-history-modal__empty">' +
        escapeHtml(t("common.no_api")) +
        "</p>";
      updateBenefitsHistoryMoreBtn();
      return Promise.resolve();
    }

    const shopifyId = getGiftCardShopifyId();
    if (!shopifyId) {
      grid.innerHTML =
        '<p class="benefits-history-modal__empty">' +
        escapeHtml(t("redeem.please_login_history")) +
        "</p>";
      updateBenefitsHistoryMoreBtn();
      return Promise.resolve();
    }

    benefitsHistoryLoading = true;
    if (append) {
      setBenefitsHistoryMoreLoading(true);
    } else {
      grid.innerHTML =
        '<p class="benefits-history-modal__empty">' +
        escapeHtml(t("common.loading")) +
        "</p>";
      benefitsHistoryPool = [];
      benefitsHistoryApiPage = 0;
      benefitsHistoryLoadedCount = 0;
      benefitsHistoryTotal = 0;
      setBenefitsHistoryMoreLoading(false);
      const moreBtn = root.querySelector("[data-benefits-history-more]");
      if (moreBtn) moreBtn.hidden = true;
    }

    const needCount =
      (append ? benefitsHistoryLoadedCount : 0) + BENEFITS_HISTORY_PAGE_SIZE;

    function ensurePoolAndRender() {
      if (
        benefitsHistoryPool.length >= needCount ||
        (benefitsHistoryTotal > 0 &&
          benefitsHistoryPool.length >= benefitsHistoryTotal)
      ) {
        renderHistoryChunk(grid, append);
        return Promise.resolve();
      }
      const prevLen = benefitsHistoryPool.length;
      return fetchHistoryApiPage(
        baseUrl,
        shopifyId,
        benefitsHistoryApiPage + 1,
      ).then(function () {
        if (benefitsHistoryPool.length === prevLen) {
          renderHistoryChunk(grid, append);
          return;
        }
        return ensurePoolAndRender();
      });
    }

    const startedAt = Date.now();

    function finishLoading() {
      const minMs = append ? 400 : 0;
      const wait = Math.max(0, minMs - (Date.now() - startedAt));
      window.setTimeout(function () {
        benefitsHistoryLoading = false;
        setBenefitsHistoryMoreLoading(false);
        updateBenefitsHistoryMoreBtn();
      }, wait);
    }

    return new Promise(function (resolve) {
      requestAnimationFrame(function () {
        requestAnimationFrame(resolve);
      });
    })
      .then(ensurePoolAndRender)
      .catch(function () {
        if (!append) {
          grid.innerHTML =
            '<p class="benefits-history-modal__empty benefits-history-modal__empty--error">' +
            escapeHtml(t("common.network_error")) +
            "</p>";
          benefitsHistoryLoadedCount = 0;
        }
        return null;
      })
      .finally(finishLoading);
  }

  function openBenefitsRedeemHistoryModal() {
    const modal = root.querySelector("[data-benefits-history-modal]");
    if (!modal) return;
    modal.hidden = false;
    document.documentElement.style.overflow = "hidden";
    benefitsHistoryPool = [];
    benefitsHistoryApiPage = 0;
    benefitsHistoryTotal = 0;
    benefitsHistoryLoadedCount = 0;
    const moreBtn = modal.querySelector("[data-benefits-history-more]");
    if (moreBtn) moreBtn.hidden = true;
    ensurePointStoreItemsLoaded();
    loadBenefitsRedeemHistory(false);
  }

  function closeBenefitsRedeemHistoryModal() {
    const modal = root.querySelector("[data-benefits-history-modal]");
    closePointsCenterBottomSheet(modal, {
      onClosed: function () {
        const rulesModal = root.querySelector("[data-benefits-rules-modal]");
        if (!rulesModal || rulesModal.hidden) {
          document.documentElement.style.overflow = "";
        }
      },
    });
  }

  (function initBenefitsRedeemHistoryModal() {
    const modal = root.querySelector("[data-benefits-history-modal]");
    if (!modal) return;
    modal
      .querySelectorAll("[data-benefits-history-close]")
      .forEach(function (el) {
        el.addEventListener("click", closeBenefitsRedeemHistoryModal);
      });
    const moreBtn = root.querySelector("[data-benefits-history-more]");
    if (moreBtn) {
      moreBtn.addEventListener("click", function () {
        loadBenefitsRedeemHistory(true);
      });
    }
    window.addEventListener("resize", function () {
      const historyModal = root.querySelector("[data-benefits-history-modal]");
      if (historyModal && !historyModal.hidden) {
        refreshBenefitsHistoryCouponCards();
      }
    });
    document.addEventListener("keydown", function (e) {
      if (e.key !== "Escape" || modal.hidden) return;
      const rulesModal = root.querySelector("[data-benefits-rules-modal]");
      if (rulesModal && !rulesModal.hidden) return;
      closeBenefitsRedeemHistoryModal();
    });
  })();

  window.addEventListener("pageshow", function (event) {
    if (!event.persisted || !isMyRewardsPanelActive()) return;
    ensureMyRewardsData();
  });

  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState !== "visible" || !isMyRewardsPanelActive())
      return;
    const tbody = root.querySelector("[data-history-tbody]");
    if (
      tbody &&
      tbody.textContent &&
      tbody.textContent.indexOf(t("common.loading")) !== -1
    ) {
      loadPointsHistory(historyCurrentPage || 1);
    }
  });

  // Fetch loyalty overview on page load (summary + tiers + asset counts)
  if (root.getAttribute("data-design-mode") === "true") {
    bootstrapTierProfileBackgrounds();
  } else {
    setProfileBgPending(true);
  }
  refreshLoyaltyOverview(false);
  initOverviewRecommendedCarousel();

  const benefitsRedeemHistoryBtn = root.querySelector(
    "[data-benefits-redeem-history]",
  );
  if (benefitsRedeemHistoryBtn) {
    benefitsRedeemHistoryBtn.addEventListener(
      "click",
      openBenefitsRedeemHistoryModal,
    );
  }

  const benefitsPointStoreMoreBtn = root.querySelector(
    "[data-benefits-point-store-more]",
  );
  if (benefitsPointStoreMoreBtn) {
    benefitsPointStoreMoreBtn.addEventListener("click", function () {
      benefitsPointStoreVisibleCount += getBenefitsPointStorePageSize();
      const grid = root.querySelector("[data-benefits-point-store-grid]");
      const pagination = root.querySelector(
        "[data-benefits-point-store-pagination]",
      );
      if (grid) {
        renderBenefitsPointStore(
          grid,
          pagination,
          benefitsPointStoreFilteredItems,
        );
      }
    });
  }

  root
    .querySelectorAll("[data-benefits-redeem-filter]")
    .forEach(function (filterBtn) {
      filterBtn.addEventListener("click", function () {
        const filterKey = filterBtn.getAttribute("data-benefits-redeem-filter");
        if (!filterKey || filterKey === pointStoreFilter) return;
        pointStoreFilter = filterKey;
        benefitsPointStoreVisibleCount = getBenefitsPointStorePageSize();
        setPointStoreFilterUi(filterKey);
        if (pointStoreAllItems !== null) {
          const grid = root.querySelector("[data-benefits-point-store-grid]");
          const pagination = root.querySelector(
            "[data-benefits-point-store-pagination]",
          );
          if (grid) {
            renderBenefitsPointStore(
              grid,
              pagination,
              filterPointStoreByRange(pointStoreAllItems, pointStoreFilter),
            );
          }
        } else {
          loadPointStore(false);
        }
      });
    });

  let benefitsPointStoreResizeTimer = null;
  window.addEventListener("resize", function () {
    if (benefitsPointStoreResizeTimer) {
      clearTimeout(benefitsPointStoreResizeTimer);
    }
    benefitsPointStoreResizeTimer = setTimeout(function () {
      benefitsPointStoreResizeTimer = null;
      const pageSize = getBenefitsPointStorePageSize();
      // Keep at least one full row when layout columns change
      if (benefitsPointStoreVisibleCount < pageSize) {
        benefitsPointStoreVisibleCount = pageSize;
      } else {
        const remainder = benefitsPointStoreVisibleCount % pageSize;
        if (remainder !== 0) {
          benefitsPointStoreVisibleCount += pageSize - remainder;
        }
      }
      const grid = root.querySelector("[data-benefits-point-store-grid]");
      const pagination = root.querySelector(
        "[data-benefits-point-store-pagination]",
      );
      if (
        grid &&
        benefitsPointStoreFilteredItems &&
        benefitsPointStoreFilteredItems.length
      ) {
        renderBenefitsPointStore(
          grid,
          pagination,
          benefitsPointStoreFilteredItems,
        );
      }
    }, 150);
  });

  // Refer a Friend — As a Referrer sub-tabs (upper section only)
  root
    .querySelectorAll("[data-refer-referrer-tabs]")
    .forEach(function (referReferrerTabsContainer) {
      const block = referReferrerTabsContainer.closest(".refer-referrer");
      if (!block) return;

      const referReferrerTabs = referReferrerTabsContainer.querySelectorAll(
        "[data-refer-referrer-tab]",
      );
      const referReferrerPanes = block.querySelectorAll(
        "[data-refer-referrer-pane]",
      );

      function activateReferReferrerTab(tabId) {
        referReferrerTabs.forEach(function (tab) {
          const isActive =
            tab.getAttribute("data-refer-referrer-tab") === tabId;
          tab.classList.toggle("is-active", isActive);
          tab.setAttribute("aria-selected", isActive ? "true" : "false");
        });

        referReferrerPanes.forEach(function (pane) {
          const isActive =
            pane.getAttribute("data-refer-referrer-pane") === tabId;
          pane.classList.toggle("is-active", isActive);
          pane.hidden = !isActive;
        });
      }

      referReferrerTabs.forEach(function (tab) {
        tab.addEventListener("click", function () {
          const tabId = tab.getAttribute("data-refer-referrer-tab");
          scrollPointsCenterToTop();
          activateReferReferrerTab(tabId);
          loadReferRecords(tabId, false);
        });
      });
    });

  const referPanelNowBtn = root.querySelector("[data-refer-panel-now]");
  if (referPanelNowBtn) {
    referPanelNowBtn.addEventListener("click", function () {
      goToMembershipRefer();
    });
  }

  // Bind history filter listeners once (globally)
  const historyTypeSelect = root.querySelector("[data-history-type]");
  const historyPeriodSelect = root.querySelector("[data-history-period]");
  const historyPeriodCustom = root.querySelector("[data-history-period-custom]");
  const historyDateStart = root.querySelector("[data-history-date-start]");
  const historyDateEnd = root.querySelector("[data-history-date-end]");

  function formatLocalDateYmd(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return y + "-" + m + "-" + d;
  }

  function getDefaultHistoryDateRange(months) {
    const end = new Date();
    const start = new Date(
      end.getTime() - months * 30 * 24 * 3600 * 1000,
    );
    return {
      start: formatLocalDateYmd(start),
      end: formatLocalDateYmd(end),
    };
  }

  function setHistoryPeriodCustomVisible(visible) {
    if (!historyPeriodCustom) return;
    if (visible) historyPeriodCustom.removeAttribute("hidden");
    else historyPeriodCustom.setAttribute("hidden", "");
    if (!visible) closeAllHistoryDatePickers();
  }

  const historyDatePickerApis = { start: null, end: null };

  function isHistoryDateYmd(value) {
    const Picker = window.PointsCenterBirthdayPicker;
    if (Picker) {
      return Picker.isValidIso(value, { disableAfterToday: true });
    }
    if (!value || typeof value !== "string") return false;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const parsed = new Date(value + "T00:00:00");
    if (Number.isNaN(parsed.getTime())) return false;
    return formatLocalDateYmd(parsed) === value;
  }

  function getHistoryDateIsoFromInput(input) {
    if (!input) return "";
    if (input === historyDateStart && historyDatePickerApis.start) {
      return historyDatePickerApis.start.getIso();
    }
    if (input === historyDateEnd && historyDatePickerApis.end) {
      return historyDatePickerApis.end.getIso();
    }
    const Picker = window.PointsCenterBirthdayPicker;
    if (!Picker) return "";
    const iso = Picker.parseIso(input.getAttribute("data-birthday-iso") || "");
    return Picker.isValidIso(iso, { disableAfterToday: true }) ? iso : "";
  }

  function setHistoryDateInputIso(input, iso) {
    if (!input) return;
    if (input === historyDateStart && historyDatePickerApis.start) {
      historyDatePickerApis.start.setIso(iso);
      return;
    }
    if (input === historyDateEnd && historyDatePickerApis.end) {
      historyDatePickerApis.end.setIso(iso);
    }
  }

  function closeAllHistoryDatePickers() {
    if (historyDatePickerApis.start) historyDatePickerApis.start.close();
    if (historyDatePickerApis.end) historyDatePickerApis.end.close();
  }

  function bindHistoryCalendarOutsideClose() {
    if (root.dataset.historyCalendarOutsideBound === "true") return;
    root.dataset.historyCalendarOutsideBound = "true";
    root.addEventListener(
      "pointerdown",
      function (event) {
        const openWrap = event.target.closest(
          "[data-profile-birthday-wrap].is-calendar-open",
        );
        if (openWrap) return;
        const anyOpen =
          historyDatePickerApis.start &&
          historyDatePickerApis.start.isOpen &&
          historyDatePickerApis.start.isOpen();
        const anyOpenEnd =
          historyDatePickerApis.end &&
          historyDatePickerApis.end.isOpen &&
          historyDatePickerApis.end.isOpen();
        if (!anyOpen && !anyOpenEnd) return;
        closeAllHistoryDatePickers();
      },
      true,
    );
  }

  function initHistoryDatePickers() {
    const Picker = window.PointsCenterBirthdayPicker;
    if (!Picker) return;

    bindHistoryCalendarOutsideClose();

    const startWrap = historyDateStart
      ? historyDateStart.closest("[data-profile-birthday-wrap]")
      : null;
    const endWrap = historyDateEnd
      ? historyDateEnd.closest("[data-profile-birthday-wrap]")
      : null;

    if (startWrap && !historyDatePickerApis.start) {
      historyDatePickerApis.start = Picker.attach(startWrap, {
        minYearOffset: 15,
        disableAfterToday: true,
        onSelect: function () {
          onHistoryCustomDateChange();
        },
      });
    }

    if (endWrap && !historyDatePickerApis.end) {
      historyDatePickerApis.end = Picker.attach(endWrap, {
        minYearOffset: 15,
        disableAfterToday: true,
        onSelect: function () {
          onHistoryCustomDateChange();
        },
      });
    }
  }

  function ensureHistoryCustomDateDefaults() {
    if (!historyDateStart || !historyDateEnd) return;
    initHistoryDatePickers();
    const hasStart = getHistoryDateIsoFromInput(historyDateStart);
    const hasEnd = getHistoryDateIsoFromInput(historyDateEnd);
    if (hasStart && hasEnd) return;
    const range = getDefaultHistoryDateRange(3);
    if (!hasStart) setHistoryDateInputIso(historyDateStart, range.start);
    if (!hasEnd) setHistoryDateInputIso(historyDateEnd, range.end);
  }

  function onHistoryCustomDateChange() {
    if (!historyPeriodSelect || historyPeriodSelect.value !== "custom") return;
    const start = getHistoryDateIsoFromInput(historyDateStart);
    const end = getHistoryDateIsoFromInput(historyDateEnd);
    if (
      !isHistoryDateYmd(start) ||
      !isHistoryDateYmd(end) ||
      start > end
    ) {
      return;
    }
    if (isMyRewardsPanelActive()) {
      loadPointsHistory(1);
    }
  }

  if (historyTypeSelect) {
    historyTypeSelect.onchange = () => {
      // Only trigger if we are currently on My Point pane
      if (isMyRewardsPanelActive()) {
        loadPointsHistory(1);
      }
    };
  }

  if (historyPeriodSelect) {
    historyPeriodSelect.onchange = () => {
      const isCustom = historyPeriodSelect.value === "custom";
      setHistoryPeriodCustomVisible(isCustom);
      if (isCustom) ensureHistoryCustomDateDefaults();
      if (isMyRewardsPanelActive()) {
        loadPointsHistory(1);
      }
    };
  }

  initHistoryDatePickers();

  const POINT_STORE_FETCH_SIZE = 60;
  const POINT_STORE_FILTER_KEYS = [
    "lt3000",
    "3000-6000",
    "6000-10000",
    "gt10000",
  ];
  const POINT_STORE_FILTER_RANGES = {
    lt3000: { min: 0, max: 2999 },
    "3000-6000": { min: 3000, max: 5999 },
    "6000-10000": { min: 6000, max: 10000 },
    gt10000: { min: 10001, max: null },
  };
  let benefitsTicketBgUid = 0;
  const BENEFITS_TICKET_BG_TEMPLATE_IDS = {
    "free product": "benefits-redeem-ticket-bg-free-product",
    "product discount": "benefits-redeem-ticket-bg-product-discount",
    "order discount": "benefits-redeem-ticket-bg-order-discount",
    "free shipping": "benefits-redeem-ticket-bg-free-shipping",
    "gift card": "benefits-redeem-ticket-bg-gift-card",
  };

  // ── Point store (benefits center redeem grid) ──────────────────────
  function parseRedeemSnapshot(item) {
    if (!item || item.snapshot == null) return {};
    let raw = item.snapshot;
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
      } catch (e) {
        return {};
      }
    }
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
    return raw;
  }

  function getStoreItemSnap(item) {
    const nested = parseRedeemSnapshot(item);
    const merged = Object.assign({}, nested, item || {});
    const preferNestedKeys = [
      "description",
      "customerFacingLabel",
      "redeemLabel",
      "minPurchaseAmount",
      "minItemQuantity",
      "discountAmount",
      "discountType",
      "shippingThreshold",
      "ruleType",
      "ruleName",
      "ruleId",
      "benefitRuleId",
      "freeProductImage",
      "freeProductId",
      "freeProductVariantId",
      "variantId",
      "costToRedeem",
      "giftCardExpiresOn",
      "giftCardEnabled",
      "giftCardBalance",
      "giftCardInitialValue",
    ];
    preferNestedKeys.forEach(function (key) {
      if (
        (merged[key] == null || merged[key] === "") &&
        nested[key] != null &&
        nested[key] !== ""
      ) {
        merged[key] = nested[key];
      }
    });
    // Redeem records use item.id as redeem-id; keep snapshot rule id as ruleId
    if (
      nested.id != null &&
      item &&
      item.id != null &&
      Number(nested.id) !== Number(item.id) &&
      (merged.ruleId == null || merged.ruleId === "")
    ) {
      merged.ruleId = nested.id;
    }
    return merged;
  }

  function getRedeemRuleId(item) {
    const nested = parseRedeemSnapshot(item);
    const snap = getStoreItemSnap(item);
    return (
      Number(
        snap.ruleId ||
          snap.benefitRuleId ||
          snap.rewardRuleId ||
          snap.sourceRuleId ||
          nested.id ||
          0,
      ) || 0
    );
  }

  function findPointStoreItemForRedeem(item) {
    if (!Array.isArray(pointStoreAllItems) || !pointStoreAllItems.length) {
      return null;
    }
    const ruleId = getRedeemRuleId(item);
    if (ruleId) {
      for (let i = 0; i < pointStoreAllItems.length; i++) {
        const storeSnap = getStoreItemSnap(pointStoreAllItems[i]);
        if (Number(storeSnap.id) === ruleId) return pointStoreAllItems[i];
      }
    }
    const label = String(formatBenefitLabel(getStoreItemSnap(item)) || "")
      .trim()
      .toLowerCase();
    if (!label || label === "reward") return null;
    for (let j = 0; j < pointStoreAllItems.length; j++) {
      const storeLabel = String(
        formatBenefitLabel(getStoreItemSnap(pointStoreAllItems[j])) || "",
      )
        .trim()
        .toLowerCase();
      if (storeLabel && storeLabel === label) return pointStoreAllItems[j];
    }
    return null;
  }

  function ensurePointStoreItemsLoaded() {
    if (pointStoreAllItems !== null) {
      return Promise.resolve(pointStoreAllItems);
    }
    if (pointStoreLoadPromise) {
      return pointStoreLoadPromise.then(function () {
        return pointStoreAllItems || [];
      });
    }
    const baseUrl = getLoyaltyBaseUrl();
    if (!baseUrl || !isLoggedIn()) return Promise.resolve([]);
    pointStoreLoadPromise = fetchPointStoreAllPages(baseUrl)
      .then(function (items) {
        pointStoreAllItems = items;
        return items;
      })
      .catch(function () {
        return [];
      })
      .finally(function () {
        pointStoreLoadPromise = null;
      });
    return pointStoreLoadPromise;
  }

  function getStoreItemPoints(item) {
    const snap = getStoreItemSnap(item);
    const points = snap.costToRedeem != null ? snap.costToRedeem : 0;
    return Number(points) || 0;
  }

  function filterPointStoreByRange(items, filterKey) {
    const range = POINT_STORE_FILTER_RANGES[filterKey];
    if (!range || !items) return items || [];
    return items.filter(function (item) {
      const pts = getStoreItemPoints(item);
      if (range.min != null && pts < range.min) return false;
      if (range.max != null && pts > range.max) return false;
      return true;
    });
  }

  function setPointStoreFilterUi(filterKey) {
    root
      .querySelectorAll("[data-benefits-redeem-filter]")
      .forEach(function (btn) {
        const isActive =
          btn.getAttribute("data-benefits-redeem-filter") === filterKey;
        btn.classList.toggle("is-active", isActive);
        btn.setAttribute("aria-selected", isActive ? "true" : "false");
      });
  }

  function ensurePointStoreFilterHasItems(items) {
    if (
      !items ||
      !items.length ||
      filterPointStoreByRange(items, pointStoreFilter).length
    ) {
      return pointStoreFilter;
    }
    for (let i = 0; i < POINT_STORE_FILTER_KEYS.length; i++) {
      const key = POINT_STORE_FILTER_KEYS[i];
      if (filterPointStoreByRange(items, key).length) {
        pointStoreFilter = key;
        setPointStoreFilterUi(key);
        return key;
      }
    }
    return pointStoreFilter;
  }

  function getBenefitsTicketBgTemplateId(ruleType) {
    const key = String(ruleType || "")
      .trim()
      .toLowerCase();
    return (
      BENEFITS_TICKET_BG_TEMPLATE_IDS[key] ||
      BENEFITS_TICKET_BG_TEMPLATE_IDS["free product"]
    );
  }

  function getBenefitsTicketBgHtml(disabled, ruleType) {
    const tpl = document.getElementById(
      getBenefitsTicketBgTemplateId(ruleType),
    );
    if (!tpl || !tpl.content) return "";
    const uid = String(++benefitsTicketBgUid);
    const wrap = document.createElement("div");
    wrap.appendChild(tpl.content.cloneNode(true));
    const svg = wrap.querySelector("svg");
    if (svg) {
      svg.classList.add("benefits-redeem-ticket__bg-svg");
      if (disabled) {
        svg.classList.add("benefits-redeem-ticket__bg-svg--disabled");
      }
      svg.removeAttribute("width");
      svg.removeAttribute("height");
      svg.setAttribute("preserveAspectRatio", "none");
      svg.setAttribute("overflow", "visible");
      svg.style.maxWidth = "none";
      svg.style.maxHeight = "none";
    }
    let html = wrap.innerHTML;
    if (svg) {
      html = html.replace(/paint(\d+)_linear[^"]*/g, "paint$1_linear_" + uid);
    }
    return (
      '<div class="benefits-redeem-ticket__bg" aria-hidden="true">' +
      html +
      "</div>"
    );
  }

  let ecocreditsIconInnerHtml = null;

  const ECOCREDITS_ICON_20_INNER_HTML =
    '<svg class="points-icon-ecocredits-20" width="20" height="20" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M12.0639 10.8629C14.6504 10.8629 17.3387 10.2752 18.9526 9.15606V11.1958H20.4481V6.7278C20.4481 4.04186 16.1283 2.59253 12.0639 2.59253C7.99947 2.59253 3.68 4.04186 3.68 6.72796V18.917C3.68 21.6029 7.99967 23.0522 12.0641 23.0522V21.507C7.79597 21.507 5.17544 19.9984 5.17544 18.917V15.2509C6.78955 16.3699 9.4777 16.9578 12.0641 16.9578V15.4125C7.79597 15.4125 5.17544 13.9039 5.17544 12.8223V9.15622C6.78928 10.2752 9.47732 10.8629 12.0639 10.8629ZM12.0639 4.13766C16.3321 4.13766 18.9526 5.64637 18.9526 6.7278C18.9526 7.8093 16.3321 9.31786 12.0639 9.31786C7.79593 9.31786 5.17529 7.8093 5.17529 6.7278C5.17533 5.64633 7.79597 4.13766 12.0639 4.13766ZM18.7659 11.7075C15.0534 11.7075 13.1109 13.1835 13.1109 14.6411V22.4739C13.1109 23.9316 15.0534 25.4073 18.7659 25.4073C22.4505 25.4073 24.3905 23.9535 24.4193 22.5064H24.4207V14.641C24.4206 13.1833 22.4784 11.7075 18.7659 11.7075ZM18.7659 13.2528C21.5102 13.2528 22.9253 14.2258 22.9253 14.6411C22.9253 15.0565 21.5102 16.0296 18.7659 16.0296C16.0214 16.0296 14.6062 15.0565 14.6062 14.6411C14.6062 14.2258 16.0216 13.2528 18.7659 13.2528ZM18.7659 23.8618C16.0214 23.8618 14.6062 22.8887 14.6062 22.4735V20.6037C15.5552 21.1361 16.9477 21.4906 18.7659 21.4906C20.584 21.4906 21.9765 21.136 22.9253 20.6037V22.4735C22.9253 22.8887 21.5102 23.8618 18.7659 23.8618ZM18.7659 19.9455C16.0214 19.9455 14.6062 18.9725 14.6062 18.5573V16.6874C15.5552 17.2199 16.9477 17.5745 18.7659 17.5745C20.584 17.5745 21.9765 17.2201 22.9253 16.6877V18.5576C22.9253 18.9725 21.5102 19.9456 18.7659 19.9456V19.9455Z" fill="url(#points-icon-ecocredits-20)"/>' +
    '<defs><linearGradient id="points-icon-ecocredits-20" x1="14.0504" y1="2.59253" x2="14.0504" y2="25.4073" gradientUnits="userSpaceOnUse"><stop stop-color="#6197FF"/><stop offset="1" stop-color="#1E2172"/></linearGradient></defs>' +
    "</svg>";

  function getEcocreditsIconHtml(wrapperClass) {
    if (!ecocreditsIconInnerHtml) {
      const tpl = document.getElementById("points-icon-ecocredits-20");
      if (tpl && tpl.content && tpl.content.firstChild) {
        const iconWrap = document.createElement("span");
        iconWrap.appendChild(tpl.content.cloneNode(true));
        ecocreditsIconInnerHtml = iconWrap.innerHTML;
      } else {
        ecocreditsIconInnerHtml = ECOCREDITS_ICON_20_INNER_HTML;
      }
    }
    if (!wrapperClass) return ecocreditsIconInnerHtml;
    return (
      '<span class="' +
      escapeHtml(wrapperClass) +
      '">' +
      ecocreditsIconInnerHtml +
      "</span>"
    );
  }

  function formatRedeemCostHtml(pointsLabel) {
    return (
      '<p class="benefits-redeem-ticket__cost">' +
      escapeHtml(pointsLabel) +
      "</p>"
    );
  }

  let redeemModalTicketBgUid = 0;

  const REDEEM_MODAL_TICKET_BG_TEMPLATE_IDS = {
    "free product": "points-redeem-modal-ticket-bg-free-product",
    "product discount": "points-redeem-modal-ticket-bg-product-discount",
    "order discount": "points-redeem-modal-ticket-bg-order-discount",
    "free shipping": "points-redeem-modal-ticket-bg-free-shipping",
    "gift card": "points-redeem-modal-ticket-bg-gift-card",
  };

  function getRedeemModalTicketBgTemplateId(ruleType) {
    const key = String(ruleType || "")
      .trim()
      .toLowerCase();
    return (
      REDEEM_MODAL_TICKET_BG_TEMPLATE_IDS[key] ||
      REDEEM_MODAL_TICKET_BG_TEMPLATE_IDS["free product"]
    );
  }

  function cloneRedeemModalTicketBgTemplate(templateId) {
    const tpl = document.getElementById(templateId);
    if (!tpl || !tpl.content) return "";
    const uid = String(++redeemModalTicketBgUid);
    const wrap = document.createElement("div");
    wrap.appendChild(tpl.content.cloneNode(true));
    const svg = wrap.querySelector("svg");
    if (svg) {
      svg.classList.add("points-redeem-modal__ticket-bg-svg");
      svg.removeAttribute("width");
      svg.removeAttribute("height");
      svg.setAttribute("preserveAspectRatio", "none");
      svg.setAttribute("overflow", "visible");
      svg.style.maxWidth = "none";
      svg.style.maxHeight = "none";
    }
    let html = wrap.innerHTML;
    html = html.replace(/paint(\d+)_linear(_\d+_\d+)?/g, (_, n) => {
      return "rm_ticket_paint" + n + "_linear_" + uid;
    });
    html = html.replace(/paint(\d+)_radial(_\d+_\d+)?/g, (_, n) => {
      return "rm_ticket_paint" + n + "_radial_" + uid;
    });
    return (
      '<div class="points-redeem-modal__ticket-bg" aria-hidden="true">' +
      html +
      "</div>"
    );
  }

  function getRedeemModalTicketBgHtml(ruleType) {
    return cloneRedeemModalTicketBgTemplate(
      getRedeemModalTicketBgTemplateId(ruleType),
    );
  }

  function getRedeemSuccessTicketBgHtml() {
    return cloneRedeemModalTicketBgTemplate(
      "points-redeem-modal-ticket-bg-success",
    );
  }

  function formatStoreTicketValueHtml(snap, item) {
    return formatTicketValueHtmlByPrefix(snap, "benefits-redeem-ticket", item);
  }

  function formatStoreMinPurchase(snap, item) {
    const nested = parseRedeemSnapshot(item);
    const amountRaw =
      snap && snap.minPurchaseAmount != null && snap.minPurchaseAmount !== ""
        ? snap.minPurchaseAmount
        : nested.minPurchaseAmount != null && nested.minPurchaseAmount !== ""
          ? nested.minPurchaseAmount
          : null;
    const amountNum =
      amountRaw == null || amountRaw === "" ? 0 : Number(amountRaw);
    if (!Number.isNaN(amountNum) && amountNum > 0) {
      const display =
        amountNum % 1 === 0
          ? numberFormatter.format(amountNum)
          : amountNum.toFixed(2);
      return t("redeem.min_amount_html", { amount: display });
    }

    const qtyRaw =
      snap && snap.minItemQuantity != null && snap.minItemQuantity !== ""
        ? snap.minItemQuantity
        : nested.minItemQuantity != null && nested.minItemQuantity !== ""
          ? nested.minItemQuantity
          : null;
    const qtyNum = qtyRaw == null || qtyRaw === "" ? 0 : Number(qtyRaw);
    if (Number.isNaN(qtyNum) || qtyNum <= 0) return "";
    const qtyDisplay =
      qtyNum % 1 === 0 ? numberFormatter.format(qtyNum) : String(qtyNum);
    return t("redeem.min_qty_html", { quantity: qtyDisplay });
  }

  function getStoreBenefitTypeLabel(ruleType) {
    const label = String(ruleType || "").trim();
    return label || t("common.dash");
  }

  function bindBenefitsRedeemTicketActions(card, item) {
    const redeemBtn = card.querySelector("[data-benefits-redeem-now]");
    if (redeemBtn) {
      redeemBtn.addEventListener("click", function () {
        if (redeemBtn.disabled) return;
        if (isStoreItemRedeemBlocked(item)) return;
        if (!isLoggedIn()) {
          redirectToLogin();
          return;
        }
        openRedeemModal(item);
      });
    }
    const rulesBtn = card.querySelector("[data-benefits-redeem-rules]");
    if (rulesBtn) {
      rulesBtn.addEventListener("click", function () {
        openBenefitsRedeemRulesModal(item);
      });
    }
  }

  function getBenefitsRedeemRulesLines(item) {
    const snap = getStoreItemSnap(item);
    let desc = String(snap.description || "").trim();

    // History redeem records may omit description; fall back to store catalog
    if (!desc) {
      const matched = findPointStoreItemForRedeem(item);
      if (matched) {
        desc = String(getStoreItemSnap(matched).description || "").trim();
      }
    }

    if (!desc) return [];
    var lines = desc
      .split(/\r?\n+/)
      .map(function (line) {
        return line.trim();
      })
      .filter(Boolean);
    if (lines.length === 1) {
      var parts = lines[0].split(/\.\s+/);
      if (parts.length > 1) {
        lines = parts
          .map(function (part, index) {
            part = part.trim();
            if (!part) return "";
            if (index < parts.length - 1 && !/[.!?。]$/.test(part)) {
              part += ".";
            }
            return part;
          })
          .filter(Boolean);
      }
    }
    return lines;
  }

  function openBenefitsRedeemRulesModal(item) {
    const modal = root.querySelector("[data-benefits-rules-modal]");
    if (!modal) return;
    const body = modal.querySelector("[data-benefits-rules-body]");

    function fillBody() {
      if (!body) return;
      const lines = getBenefitsRedeemRulesLines(item);
      body.innerHTML = lines.length
        ? lines
            .map(function (line) {
              return "<p>" + escapeHtml(line) + "</p>";
            })
            .join("")
        : "";
    }

    fillBody();
    modal.hidden = false;
    document.documentElement.style.overflow = "hidden";

    // Warm store cache then refresh if History records lack description
    if (!getBenefitsRedeemRulesLines(item).length) {
      ensurePointStoreItemsLoaded().then(function () {
        if (!modal.hidden) fillBody();
      });
    }
  }

  function closeBenefitsRedeemRulesModal() {
    const modal = root.querySelector("[data-benefits-rules-modal]");
    closePointsCenterBottomSheet(modal, {
      dialogSelector: ".benefits-rules-modal__dialog",
      onClosed: function () {
        const historyModal = root.querySelector(
          "[data-benefits-history-modal]",
        );
        if (!historyModal || historyModal.hidden) {
          document.documentElement.style.overflow = "";
        }
      },
    });
  }

  (function initBenefitsRedeemRulesModal() {
    const modal = root.querySelector("[data-benefits-rules-modal]");
    if (!modal) return;
    modal
      .querySelectorAll("[data-benefits-rules-close]")
      .forEach(function (el) {
        el.addEventListener("click", closeBenefitsRedeemRulesModal);
      });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && !modal.hidden) {
        closeBenefitsRedeemRulesModal();
      }
    });
  })();

  function createBenefitsRedeemTicket(item) {
    const snap = getStoreItemSnap(item);
    const points = getStoreItemPoints(item);
    const ruleType = normalizeRuleType(item);
    const valueHtml = formatStoreTicketValueHtml(snap, item);
    const minPurchase = formatStoreMinPurchase(snap, item);
    const typeLabel = getStoreBenefitTypeLabel(ruleType);
    const pointsLabel = t("redeem.cost_label", {
      count: numberFormatter.format(points),
    });
    const canAfford = isLoggedIn() && canAffordRedeem(points);
    const redeemable = isRedeemableItem(item);
    const redeemBlocked = isStoreItemRedeemBlocked(item);
    let redeemDisabled =
      !redeemable || redeemBlocked || (isLoggedIn() && !canAfford);
    let redeemTitle = "";
    if (!redeemable) redeemTitle = getUnsupportedRuleTypeReason(item);
    else if (redeemBlocked)
      redeemTitle = getStoreItemRedeemBlockedReason(item);
    else if (isLoggedIn() && !canAfford)
      redeemTitle = getInsufficientPointsReason(points);

    const card = document.createElement("article");
    card.className =
      "benefits-redeem-ticket" + (redeemDisabled ? " is-disabled" : "");
    card.innerHTML =
      '<div class="benefits-redeem-ticket__shell">' +
      getBenefitsTicketBgHtml(redeemDisabled, ruleType) +
      '<div class="benefits-redeem-ticket__body">' +
      '<div class="benefits-redeem-ticket__top">' +
      valueHtml +
      (minPurchase
        ? '<p class="benefits-redeem-ticket__min">' + minPurchase + "</p>"
        : "") +
      formatGiftCardExpiryHtml(item, "benefits-redeem-ticket__expiry") +
      "</div>" +
      '<div class="benefits-redeem-ticket__mid">' +
      formatRedeemCostHtml(pointsLabel) +
      '<p class="benefits-redeem-ticket__type">' +
      escapeHtml(typeLabel) +
      "</p>" +
      "</div>" +
      "</div>" +
      '<div class="benefits-redeem-ticket__foot">' +
      '<button type="button" class="benefits-redeem-ticket__redeem"' +
      " data-benefits-redeem-now" +
      (redeemDisabled ? " disabled" : "") +
      (redeemTitle ? ' title="' + escapeHtml(redeemTitle) + '"' : "") +
      ">" +
      escapeHtml(t("benefits.redeem_now")) +
      "</button>" +
      '<button type="button" class="benefits-redeem-ticket__rules" data-benefits-redeem-rules>' +
      escapeHtml(t("benefits.rules")) +
      "</button>" +
      "</div>" +
      "</div>";

    bindBenefitsRedeemTicketActions(card, item);
    return card;
  }

  function renderBenefitsPointStore(grid, pagination, items) {
    const moreBtn = root.querySelector("[data-benefits-point-store-more]");
    benefitsPointStoreFilteredItems = Array.isArray(items) ? items.slice() : [];
    grid.innerHTML = "";
    if (!benefitsPointStoreFilteredItems.length) {
      grid.innerHTML =
        '<p class="benefits-redeem-store__empty">' +
        escapeHtml(t("benefits.no_rewards_range")) +
        "</p>";
      if (moreBtn) moreBtn.hidden = true;
    } else {
      const visible = benefitsPointStoreFilteredItems.slice(
        0,
        benefitsPointStoreVisibleCount,
      );
      visible.forEach(function (item) {
        try {
          grid.appendChild(createBenefitsRedeemTicket(item));
        } catch (err) {
          console.error("[points-center] failed to render redeem ticket", err);
        }
      });
      if (moreBtn) {
        moreBtn.hidden =
          benefitsPointStoreFilteredItems.length <=
          benefitsPointStoreVisibleCount;
      }
    }
    if (pagination) {
      pagination.innerHTML = "";
      pagination.hidden = true;
    }
  }

  function fetchPointStoreAllPages(baseUrl) {
    function fetchPage(pageNum) {
      return fetch(
        baseUrl.replace(/\/$/, "") + "/front/redeem/store/selectPage",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            pageNum: pageNum,
            pageSize: POINT_STORE_FETCH_SIZE,
          }),
        },
      )
        .then(function (res) {
          return res.json();
        })
        .then(function (json) {
          if (!json || json.code !== 200 || !json.data) {
            throw new Error(
              (json && json.msg) || t("benefits.failed_load_store"),
            );
          }
          return json.data;
        });
    }

    return fetchPage(1).then(function (firstPage) {
      const all = (firstPage.list || []).slice();
      const totalPages = firstPage.pages || 1;
      if (totalPages <= 1) return all;
      const rest = [];
      for (let p = 2; p <= totalPages; p++) rest.push(p);
      return rest.reduce(function (chain, pageNum) {
        return chain.then(function (acc) {
          return fetchPage(pageNum).then(function (data) {
            return acc.concat(data.list || []);
          });
        });
      }, Promise.resolve(all));
    });
  }

  function loadPointStore(forceReload) {
    const baseUrl = getLoyaltyBaseUrl();
    const grid = root.querySelector("[data-benefits-point-store-grid]");
    const pagination = root.querySelector(
      "[data-benefits-point-store-pagination]",
    );
    if (!grid) return;

    if (!baseUrl) {
      grid.innerHTML =
        '<p class="benefits-redeem-store__empty">' +
        escapeHtml(t("benefits.store_unavailable")) +
        "</p>";
      const moreBtn = root.querySelector("[data-benefits-point-store-more]");
      if (moreBtn) moreBtn.hidden = true;
      return;
    }

    if (!isLoggedIn()) {
      grid.innerHTML =
        '<p class="benefits-redeem-store__empty">' +
        escapeHtml(t("benefits.please_login_redeem")) +
        "</p>";
      if (pagination) pagination.innerHTML = "";
      const moreBtn = root.querySelector("[data-benefits-point-store-more]");
      if (moreBtn) moreBtn.hidden = true;
      return;
    }

    function renderFromCache() {
      ensurePointStoreFilterHasItems(pointStoreAllItems);
      renderBenefitsPointStore(
        grid,
        pagination,
        filterPointStoreByRange(pointStoreAllItems, pointStoreFilter),
      );
    }

    if (pointStoreAllItems !== null && !forceReload) {
      ensurePointsLoaded().then(renderFromCache).catch(renderFromCache);
      return;
    }

    grid.innerHTML =
      '<p class="benefits-redeem-store__empty">' +
      escapeHtml(t("common.loading")) +
      "</p>";

    if (pointStoreLoadPromise && !forceReload) {
      pointStoreLoadPromise.then(renderFromCache).catch(function () {
        grid.innerHTML =
          '<p class="benefits-redeem-store__empty">' +
          escapeHtml(t("benefits.failed_store")) +
          "</p>";
      });
      return;
    }

    pointStoreLoadPromise = ensurePointsLoaded()
      .then(function () {
        return fetchPointStoreAllPages(baseUrl);
      })
      .then(function (items) {
        pointStoreAllItems = items;
        renderFromCache();
      })
      .catch(function () {
        grid.innerHTML =
          '<p class="benefits-redeem-store__empty">' +
          escapeHtml(t("benefits.failed_store")) +
          "</p>";
        if (pagination) pagination.innerHTML = "";
      })
      .finally(function () {
        pointStoreLoadPromise = null;
      });
  }

  // ── My rewards & points history ──────────────────────────────────
  function loadMyPointSummary(force, options) {
    options = options || {};
    const grid = root.querySelector("[data-points-summary-grid]");
    if (!grid) return Promise.resolve();

    const baseUrl = getLoyaltyBaseUrl();
    if (!baseUrl) {
      grid.innerHTML =
        '<p style="grid-column: 1 / -1; color:#64748b;">' +
        escapeHtml(t("rewards.points_unavailable")) +
        "</p>";
      return Promise.resolve();
    }

    if (!getGiftCardShopifyId()) {
      grid.innerHTML =
        '<p style="grid-column: 1 / -1; color:#64748b;">' +
        escapeHtml(t("rewards.please_login")) +
        "</p>";
      return Promise.resolve();
    }

    if (!force && loyaltySummaryData) {
      renderMyPointSummary(grid, loyaltySummaryData, options);
      return Promise.resolve(loyaltySummaryData);
    }

    grid.innerHTML =
      '<p style="grid-column: 1 / -1; color:#64748b;">' +
      escapeHtml(t("common.loading")) +
      "</p>";

    return fetchLoyaltySummary(force).then(function (data) {
      if (!data) {
        grid.innerHTML =
          '<p style="grid-column: 1 / -1; color:#ef4444;">' +
          escapeHtml(t("rewards.failed_summary")) +
          "</p>";
        return null;
      }
      renderMyPointSummary(grid, data, options);
      return data;
    });
  }

  function renderMyPointSummary(container, data, options) {
    options = options || {};
    const replayCountUp = !!options.replayCountUp;
    const countUpOpts = replayCountUp ? { start: 0, force: true } : null;
    container.innerHTML = "";

    const stats = [
      {
        value: getSummaryAvailablePoints(data),
        label: t("rewards.current_points"),
        kind: "integer",
      },
      {
        value: getSummaryPendingPoints(data),
        label: t("rewards.pending_points"),
        kind: "integer",
      },
      {
        value: data.spentAmount || 0,
        label: t("rewards.total_spending"),
        kind: "currency",
      },
      {
        value: data.totalPoints || 0,
        label: t("rewards.total_earned"),
        kind: "integer",
      },
    ];

    stats.forEach(function (stat) {
      const card = document.createElement("div");
      card.className = "points-summary-card";
      const valueEl = document.createElement("div");
      valueEl.className = "points-summary-value";
      valueEl.textContent = "0";
      const labelEl = document.createElement("div");
      labelEl.className = "points-summary-label";
      labelEl.textContent = stat.label;
      card.appendChild(valueEl);
      card.appendChild(labelEl);
      container.appendChild(card);

      if (countUpOpts) {
        if (stat.kind === "currency") {
          animateCountUpCurrency(valueEl, stat.value, countUpOpts);
        } else {
          animateCountUp(valueEl, stat.value, countUpOpts);
        }
      } else {
        setPointSummaryValue(valueEl, stat);
      }
    });
  }

  // Points History
  function normalizePointsHistoryStatusKey(status) {
    return String(status || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "_");
  }

  function getPointsHistoryStatusMod(status) {
    const normalized = normalizePointsHistoryStatusKey(status);
    const modMap = {
      COMPLETED: "completed",
      CANCELLED: "cancelled",
      CANCELED: "cancelled",
      PENDING: "pending",
      FAILED: "failed",
      PROCESSING: "processing",
      REFUNDED: "refunded",
    };
    return modMap[normalized] || "default";
  }

  function formatPointsHistoryStatusTitleCase(status) {
    const raw = String(status || "").trim();
    if (!raw) return "";
    return raw
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, function (ch) {
        return ch.toUpperCase();
      });
  }

  function getPointsHistoryStatusLabel(status) {
    const normalized = normalizePointsHistoryStatusKey(status);
    const i18nMap = {
      COMPLETED: "rewards.history_status_completed",
      CANCELLED: "rewards.history_status_cancelled",
      CANCELED: "rewards.history_status_cancelled",
      PENDING: "rewards.history_status_pending",
      FAILED: "status.failed",
      PROCESSING: "status.processing",
      REFUNDED: "status.refunded",
    };
    if (i18nMap[normalized]) return t(i18nMap[normalized]);
    if (!normalized) return t("common.dash");
    return formatPointsHistoryStatusTitleCase(status);
  }

  function getPointsHistoryStatusBadgeHtml(statusMod, statusLabel) {
    const mod = statusMod || "default";
    return (
      '<span class="points-history-status points-history-status--' +
      escapeHtml(mod) +
      '">' +
      escapeHtml(statusLabel || t("common.dash")) +
      "</span>"
    );
  }

  function formatPointsHistoryItem(item) {
    const positiveTypes = ["EARN", "ADMIN_MANUAL_ADD"];
    const negativeTypes = ["SPEND", "REFUND", "ADMIN_MANUAL_SUBTRACT"];
    const isPositive = positiveTypes.includes(item.transaction_type);
    const pointClass = isPositive ? "point-positive" : "point-negative";
    const pointSign = negativeTypes.includes(item.transaction_type) ? "-" : "+";
    const statusMod = getPointsHistoryStatusMod(item.status);
    const statusLabel = getPointsHistoryStatusLabel(item.status);

    return {
      date: new Date(item.created_at).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      }),
      details: item.note || "-",
      pointClass: pointClass,
      pointText: pointSign + item.points_amount,
      statusMod: statusMod,
      statusLabel: statusLabel,
    };
  }

  function setPointsHistoryMessage(tbody, listEl, message, isError) {
    const color = isError ? "#ef4444" : "#64748b";
    if (tbody) {
      tbody.innerHTML =
        '<tr><td colspan="4" style="color:' +
        color +
        '; text-align:center;">' +
        escapeHtml(message) +
        "</td></tr>";
    }
    if (listEl) {
      listEl.innerHTML =
        '<p class="points-history__list-msg' +
        (isError ? " points-history__list-msg--error" : "") +
        '">' +
        escapeHtml(message) +
        "</p>";
    }
  }

  function buildPointsHistoryPayload(pageNum, shopifyId) {
    const typeSelect = root.querySelector("[data-history-type]");
    const periodSelect = root.querySelector("[data-history-period]");
    const payload = {
      shopifyId: shopifyId,
      pageNum: pageNum,
      pageSize: HISTORY_PAGE_SIZE,
    };

    if (typeSelect && typeSelect.value)
      payload.transactionType = typeSelect.value;
    if (periodSelect && periodSelect.value) {
      if (periodSelect.value === "custom") {
        const startInput = root.querySelector("[data-history-date-start]");
        const endInput = root.querySelector("[data-history-date-end]");
        const start = getHistoryDateIsoFromInput(startInput);
        const end = getHistoryDateIsoFromInput(endInput);
        if (
          !isHistoryDateYmd(start) ||
          !isHistoryDateYmd(end) ||
          start > end
        ) {
          throw new Error(t("rewards.invalid_date_range"));
        }
        payload.createdAtStart = start;
        payload.createdAtEnd = end;
      } else {
        const months = parseInt(periodSelect.value, 10);
        if (!Number.isFinite(months) || months <= 0) {
          throw new Error(t("rewards.invalid_period"));
        }
        const end = new Date();
        const start = new Date(
          end.getTime() - months * 30 * 24 * 3600 * 1000,
        );
        payload.createdAtStart = formatLocalDateYmd(start);
        payload.createdAtEnd = formatLocalDateYmd(end);
      }
    }

    return payload;
  }

  function loadPointsHistory(pageNum) {
    const tbody = root.querySelector("[data-history-tbody]");
    const listEl = root.querySelector("[data-history-list]");
    const pagination = root.querySelector("[data-history-pagination]");
    if (!tbody || !pagination) return;

    const baseUrl = getLoyaltyBaseUrl();
    if (!baseUrl) {
      setPointsHistoryMessage(tbody, listEl, t("common.no_api"), false);
      pagination.innerHTML = "";
      return;
    }

    const shopifyId = getGiftCardShopifyId();
    if (!shopifyId) {
      setPointsHistoryMessage(
        tbody,
        listEl,
        t("redeem.please_login_history"),
        false,
      );
      pagination.innerHTML = "";
      return;
    }

    let payload;
    try {
      payload = buildPointsHistoryPayload(pageNum, shopifyId);
    } catch (e) {
      setPointsHistoryMessage(
        tbody,
        listEl,
        e && e.message ? e.message : t("rewards.failed_history"),
        true,
      );
      pagination.innerHTML = "";
      return;
    }

    historyCurrentPage = pageNum;
    const requestId = ++historyRequestId;
    setPointsHistoryMessage(tbody, listEl, t("common.loading"), false);
    pagination.innerHTML = "";

    fetch(baseUrl.replace(/\/$/, "") + "/front/points/history/page", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        if (requestId !== historyRequestId) return;
        if (!json || json.code !== 200 || !json.data) {
          setPointsHistoryMessage(
            tbody,
            listEl,
            t("rewards.failed_history"),
            true,
          );
          pagination.innerHTML = "";
          return;
        }
        renderPointsHistory(tbody, listEl, pagination, json.data);
      })
      .catch(function () {
        if (requestId !== historyRequestId) return;
        setPointsHistoryMessage(tbody, listEl, t("common.network_error"), true);
        pagination.innerHTML = "";
      });
  }

  function renderPointsHistory(tbody, listEl, paginationContainer, data) {
    tbody.innerHTML = "";
    if (listEl) listEl.innerHTML = "";

    if (!data.list || data.list.length === 0) {
      setPointsHistoryMessage(tbody, listEl, t("rewards.no_records"), false);
      paginationContainer.innerHTML = "";
      return;
    }

    data.list.forEach((item) => {
      const row = formatPointsHistoryItem(item);
      const tr = document.createElement("tr");

      tr.innerHTML =
        '<td class="points-history__date">' +
        escapeHtml(row.date) +
        "</td>" +
        '<td class="points-history__details">' +
        escapeHtml(row.details) +
        "</td>" +
        '<td class="' +
        row.pointClass +
        '">' +
        escapeHtml(row.pointText) +
        "</td>" +
        '<td class="points-history__status">' +
        getPointsHistoryStatusBadgeHtml(row.statusMod, row.statusLabel) +
        "</td>";
      tbody.appendChild(tr);

      if (listEl) {
        const card = document.createElement("article");
        card.className = "points-history-card";
        card.innerHTML =
          '<div class="points-history-card__top">' +
          '<time class="points-history-card__date" datetime="' +
          escapeHtml(item.created_at || "") +
          '">' +
          escapeHtml(row.date) +
          "</time>" +
          '<span class="points-history-card__status">' +
          getPointsHistoryStatusBadgeHtml(row.statusMod, row.statusLabel) +
          "</span>" +
          "</div>" +
          '<p class="points-history-card__details">' +
          escapeHtml(row.details) +
          "</p>" +
          '<p class="points-history-card__point ' +
          row.pointClass +
          '">' +
          escapeHtml(row.pointText) +
          "</p>";
        listEl.appendChild(card);
      }
    });

    // Pagination
    renderHistoryPagination(paginationContainer, data);
  }

  function renderHistoryPagination(container, data) {
    container.innerHTML = "";
    const totalPages = data.pages || 1;
    const current = data.pageNum || 1;

    const prev = document.createElement("button");
    prev.textContent = "<";
    prev.disabled = current <= 1;
    prev.onclick = () => {
      scrollPointsCenterToTop();
      loadPointsHistory(current - 1);
    };
    container.appendChild(prev);

    for (
      let i = Math.max(1, current - 2);
      i <= Math.min(totalPages, current + 2);
      i++
    ) {
      const btn = document.createElement("button");
      btn.textContent = i;
      if (i === current) btn.classList.add("is-active");
      btn.onclick = () => {
        scrollPointsCenterToTop();
        loadPointsHistory(i);
      };
      container.appendChild(btn);
    }

    const next = document.createElement("button");
    next.textContent = ">";
    next.disabled = current >= totalPages;
    next.onclick = () => {
      scrollPointsCenterToTop();
      loadPointsHistory(current + 1);
    };
    container.appendChild(next);
  }

  function normalizeSocialLink(link) {
    const url = String(link || "").trim();
    if (!url) return "";
    if (/^https?:\/\//i.test(url)) return url;
    if (url.charAt(0) === "/") return url;
    if (url.indexOf("//") === 0) return "https:" + url;
    return "https://" + url.replace(/^\/+/, "");
  }

  function getCategoryName(rule) {
    if (!rule) return "";
    if (rule.categoryName != null && rule.categoryName !== "")
      return String(rule.categoryName);
    if (rule.category_name != null && rule.category_name !== "")
      return String(rule.category_name);
    return "";
  }

  function goToMembershipRefer() {
    const referScroll = window.MembershipReferScroll;
    if (referScroll && referScroll.scrollTo({ behavior: "smooth" })) {
      if (location.hash !== "#membership-refer") {
        history.replaceState(
          null,
          "",
          location.pathname + location.search + "#membership-refer",
        );
      }
      referScroll.scrollToWhenReady();
      return;
    }
    if (MEMBERSHIP_REFER_URL) {
      window.location.href = MEMBERSHIP_REFER_URL;
    }
  }

  function pickEarnRuleLink(rule) {
    if (!rule) return "";
    const keys = [
      "socialLink",
      "social_link",
      "redirectUrl",
      "redirect_url",
      "linkUrl",
      "link_url",
      "actionUrl",
      "action_url",
      "customLink",
      "custom_link",
      "url",
    ];
    for (let i = 0; i < keys.length; i++) {
      const value = rule[keys[i]];
      if (value != null && String(value).trim() !== "") {
        return String(value).trim();
      }
    }
    return "";
  }

  function pickEarnRuleLink(rule) {
    if (!rule) return "";
    const keys = [
      "socialLink",
      "social_link",
      "redirectUrl",
      "redirect_url",
      "linkUrl",
      "link_url",
      "actionUrl",
      "action_url",
      "customLink",
      "custom_link",
      "url",
    ];
    for (let i = 0; i < keys.length; i++) {
      const value = rule[keys[i]];
      if (value != null && String(value).trim() !== "") {
        return String(value).trim();
      }
    }
    return "";
  }

  // ── Earn points carousel (account overview) ───────────────────────
  function toEarnRuleItem(rule) {
    const ruleId = rule && rule.id != null ? Number(rule.id) : 0;
    const title = rule && rule.ruleName ? String(rule.ruleName) : "";
    const rewardType =
      rule && rule.rewardType ? String(rule.rewardType).toUpperCase() : "";
    let pointsText = "";
    if (rule && rule.pointsValue != null) {
      pointsText =
        rewardType === "RATIO"
          ? t("rewards.points_ratio", { count: rule.pointsValue })
          : t("rewards.points_amount", { count: rule.pointsValue });
    }
    const rawLink = pickEarnRuleLink(rule);
    const customerFacingLabel =
      rule &&
      rule.customerFacingLabel != null &&
      rule.customerFacingLabel !== ""
        ? String(rule.customerFacingLabel)
        : "";
    return {
      id: ruleId,
      title: title,
      pointsText: pointsText,
      pointsValue:
        rule && rule.pointsValue != null && rule.pointsValue !== ""
          ? Number(rule.pointsValue)
          : null,
      icon: rule && rule.sceneIcon ? String(rule.sceneIcon) : "",
      completed: !!(rule && rule.interactionCompleted === true),
      socialLink: normalizeSocialLink(rawLink),
      ruleDescription:
        rule && rule.ruleDescription ? String(rule.ruleDescription) : "",
      metaPreview:
        rule && rule.ruleDescription
          ? String(rule.ruleDescription).substring(0, 80)
          : "",
      categoryName: getCategoryName(rule),
      ruleType: rule && rule.ruleType ? String(rule.ruleType) : "",
      rewardType: rewardType,
      buttonLabel: customerFacingLabel || title,
    };
  }

  function getOverviewEarnCheckSvg(uid) {
    const gradId = "overviewEarnCheckGrad-" + uid;
    return (
      '<svg class="membership-earn-card__check-icon" width="80" height="80" viewBox="0 0 80 80" fill="none" aria-hidden="true">' +
      '<path fill="url(#' +
      gradId +
      ')" d="M61.5 9C64.5376 9 67 11.4624 67 14.5V31.5C67 32.3284 66.3284 33 65.5 33C64.6716 33 64 32.3284 64 31.5V14.5C64 13.1193 62.8807 12 61.5 12H14.5C13.1193 12 12 13.1193 12 14.5V66C12 67.3807 13.1193 68.5 14.5 68.5H61.5C62.8807 68.5 64 67.3807 64 66V58C64 57.1716 64.6716 56.5 65.5 56.5C66.3284 56.5 67 57.1716 67 58V66C67 69.0376 64.5376 71.5 61.5 71.5H14.5C11.4624 71.5 9 69.0376 9 66V14.5C9 11.4624 11.4624 9 14.5 9H61.5ZM68.4258 39.4531C69.0039 38.8599 69.9536 38.8477 70.5469 39.4258C71.1401 40.0039 71.1523 40.9536 70.5742 41.5469L51.5742 61.0469C51.2813 61.3474 50.8756 61.5113 50.4561 61.499C50.0366 61.4867 49.6411 61.2996 49.3662 60.9824L42.8662 53.4824C42.3238 52.8564 42.3917 51.9087 43.0176 51.3662C43.6436 50.8238 44.5913 50.8916 45.1338 51.5176L50.5645 57.7832L68.4258 39.4531ZM43 42C43.8284 42 44.5 42.6716 44.5 43.5C44.5 44.3284 43.8284 45 43 45H24C23.1716 45 22.5 44.3284 22.5 43.5C22.5 42.6716 23.1716 42 24 42H43ZM52 25C52.8284 25 53.5 25.6716 53.5 26.5C53.5 27.3284 52.8284 28 52 28H24C23.1716 28 22.5 27.3284 22.5 26.5C22.5 25.6716 23.1716 25 24 25H52Z"/>' +
      '<defs><linearGradient id="' +
      gradId +
      '" x1="9" y1="40.25" x2="70.9999" y2="40.25" gradientUnits="userSpaceOnUse">' +
      '<stop stop-color="#6197FF"/><stop offset="1" stop-color="#1E2172"/>' +
      "</linearGradient></defs></svg>"
    );
  }

  function cleanSocialRuleTitleFragment(fragment) {
    return String(fragment || "")
      .replace(/\s+rule\s*$/i, "")
      .replace(/\s+channel\s*$/i, "")
      .trim();
  }

  function getSocialEarnThanksAction(ruleTitle) {
    const raw = String(ruleTitle || "").trim();
    if (!raw) return "";

    const followMatch = raw.match(/^follow\s+us\s+on\s+(.+)$/i);
    if (followMatch) {
      return t("rewards.social_follow_on", {
        platform: cleanSocialRuleTitleFragment(followMatch[1]),
      });
    }

    const subscribeMatch = raw.match(/^subscribe\s+(?:to\s+)?(.+)$/i);
    if (subscribeMatch) {
      return t("rewards.social_subscribe_to", {
        target: cleanSocialRuleTitleFragment(subscribeMatch[1]),
      });
    }

    const joinMatch = raw.match(/^join\s+(.+)$/i);
    if (joinMatch) {
      return t("rewards.social_join", {
        target: cleanSocialRuleTitleFragment(joinMatch[1]),
      });
    }

    return raw.charAt(0).toLowerCase() + raw.slice(1);
  }

  function formatSocialEarnSuccessMessage(item) {
    if (!item || item.categoryName !== CATEGORY_SOCIAL) return "";
    const action = getSocialEarnThanksAction(item.title);
    let count =
      item.pointsValue != null && !Number.isNaN(Number(item.pointsValue))
        ? Number(item.pointsValue)
        : null;
    if (count == null && item.pointsText) {
      const parsed = Number(
        String(item.pointsText).replace(/[^\d.]/g, "").split(".")[0],
      );
      if (Number.isFinite(parsed) && parsed > 0) count = parsed;
    }
    if (!action || count == null) return "";
    return t("rewards.social_success", {
      action: action,
      count: numberFormatter.format(count),
    });
  }

  function getOverviewEarnCompletedDesc(item) {
    if (isBirthdayEarnRule(item) && hasProfileBirthday()) {
      let count =
        item.pointsValue != null && !Number.isNaN(Number(item.pointsValue))
          ? Number(item.pointsValue)
          : null;
      if (count == null && item.pointsText) {
        const parsed = Number(
          String(item.pointsText).replace(/[^\d.]/g, "").split(".")[0],
        );
        if (Number.isFinite(parsed) && parsed > 0) count = parsed;
      }
      if (count != null) {
        return t("rewards.birthday_success", {
          count: numberFormatter.format(count),
        });
      }
    }
    if (item.categoryName === CATEGORY_SOCIAL) {
      const socialMsg = formatSocialEarnSuccessMessage(item);
      if (socialMsg) return socialMsg;
    }
    if (item.ruleDescription) return item.ruleDescription;
    const pointsPart = item.pointsText
      ? item.pointsText.replace(/\s*Points.*$/i, "").trim()
      : "";
    if (pointsPart && item.title) {
      return t("rewards.got_by", { points: pointsPart, title: item.title });
    }
    return item.title || "";
  }

  function buildOverviewEarnStaticButton(label, item) {
    if (!item.socialLink) return "";
    const labelHtml =
      '<span class="membership-earn-card__btn-label">' + label + "</span>";
    return (
      '<a class="membership-earn-card__btn membership-earn-card__btn--outline" href="' +
      escapeHtml(item.socialLink) +
      '" target="_blank" rel="noopener noreferrer">' +
      labelHtml +
      "</a>"
    );
  }

  function getProductReviewButtonLabel(item) {
    const title = String((item && item.title) || "").trim();
    const description = String((item && item.ruleDescription) || "").trim();
    const facing = String((item && item.buttonLabel) || "").trim();
    if (title && title.length <= 48) return title;
    if (facing && facing !== description && facing.length <= 48) return facing;
    return t("rewards.review_cta");
  }

  function buildOverviewEarnReviewButton(label, link) {
    const labelHtml =
      '<span class="membership-earn-card__btn-label">' + label + "</span>";
    const normalized = link ? escapeHtml(link) : "";
    if (normalized) {
      return (
        '<a class="membership-earn-card__btn membership-earn-card__btn--outline" href="' +
        normalized +
        '" target="_blank" rel="noopener noreferrer">' +
        labelHtml +
        "</a>"
      );
    }
    return (
      '<button type="button" class="membership-earn-card__btn membership-earn-card__btn--outline" data-earn-point-review>' +
      labelHtml +
      "</button>"
    );
  }

  function buildOverviewEarnShopButton(label, link, dataAttr) {
    const attr = dataAttr || "data-earn-point-purchase";
    const labelHtml =
      '<span class="membership-earn-card__btn-label">' + label + "</span>";
    const normalized = link ? escapeHtml(link) : "";
    if (normalized) {
      return (
        '<a class="membership-earn-card__btn membership-earn-card__btn--outline" href="' +
        normalized +
        '" target="_blank" rel="noopener noreferrer">' +
        labelHtml +
        "</a>"
      );
    }
    return (
      '<button type="button" class="membership-earn-card__btn membership-earn-card__btn--outline" ' +
      attr +
      ">" +
      labelHtml +
      "</button>"
    );
  }

  function buildOverviewEarnActionButton(item) {
    const label = escapeHtml(item.buttonLabel || item.title);
    if (shouldShowEarnRuleAsCompleted(item)) return "";

    if (
      item.categoryName === CATEGORY_SOCIAL ||
      item.categoryName === CATEGORY_LINK_SHARING
    ) {
      let claimAttrs =
        'type="button" class="membership-earn-card__btn membership-earn-card__btn--outline" data-earn-point-claim="' +
        item.id +
        '" data-earn-point-category="' +
        escapeHtml(item.categoryName) +
        '"';
      if (item.categoryName === CATEGORY_SOCIAL && item.socialLink) {
        claimAttrs +=
          ' data-earn-point-social-link="' + escapeHtml(item.socialLink) + '"';
      }
      return (
        "<button " +
        claimAttrs +
        '><span class="membership-earn-card__btn-label">' +
        label +
        "</span></button>"
      );
    }

    if (isProductReviewRule(item)) {
      const reviewLink = getProductReviewLink(item);
      const reviewLabel = escapeHtml(getProductReviewButtonLabel(item));
      return buildOverviewEarnReviewButton(reviewLabel, reviewLink);
    }

    if (isBirthdayEarnRule(item)) {
      const birthdayLabel = escapeHtml(
        item.title || item.buttonLabel || t("profile.birthday"),
      );
      return (
        '<button type="button" class="membership-earn-card__btn membership-earn-card__btn--outline" data-earn-point-profile-birthday>' +
        '<span class="membership-earn-card__btn-label">' +
        birthdayLabel +
        "</span></button>"
      );
    }

    if (isPurchaseEarnRule(item)) {
      const shopLink = getPurchaseEarnLink(item);
      const shopLabel = escapeHtml(getPurchaseEarnButtonLabel(item));
      return buildOverviewEarnShopButton(shopLabel, shopLink);
    }

    return "";
  }

  const OVERVIEW_EARN_PAGE_SIZE = 6;

  function renderOverviewEarnCard(item) {
    const title = escapeHtml(item.title);
    const iconUrl = escapeHttpUrl(item.icon);
    if (shouldShowEarnRuleAsCompleted(item)) {
      const desc = escapeHtml(getOverviewEarnCompletedDesc(item));
      const uid = String(item.id || title || "done").replace(
        /[^a-zA-Z0-9_-]/g,
        "",
      );
      return (
        '<article class="membership-earn-card membership-earn-card--completed">' +
        '<div class="membership-earn-card__check">' +
        getOverviewEarnCheckSvg(uid) +
        "</div>" +
        '<div class="membership-earn-card__content">' +
        '<h4 class="membership-earn-card__title">' +
        title +
        "</h4>" +
        '<p class="membership-earn-card__desc">' +
        desc +
        "</p>" +
        "</div>" +
        "</article>"
      );
    }

    const bodyText = escapeHtml(item.ruleDescription || item.title);
    const iconHtml = iconUrl
      ? '<img class="membership-earn-card__icon" src="' +
        iconUrl +
        '" alt="" loading="lazy" decoding="async">'
      : "";
    return (
      '<article class="membership-earn-card">' +
      '<div class="membership-earn-card__top">' +
      iconHtml +
      '<p class="membership-earn-card__text">' +
      bodyText +
      "</p>" +
      "</div>" +
      buildOverviewEarnActionButton(item) +
      "</article>"
    );
  }

  function renderOverviewEarnPoints(container, rules) {
    destroyOverviewEarnCarousel();

    // Keep API order — do not move completed items
    const items = (Array.isArray(rules) ? rules : [])
      .map(toEarnRuleItem)
      .filter(function (item) {
        return item.title || item.pointsText;
      });

    if (!items.length) {
      container.innerHTML = "";
      initOverviewEarnCarousel();
      return;
    }

    if (isOverviewMobile()) {
      container.innerHTML = items
        .map(function (item) {
          return (
            '<div class="swiper-slide points-overview-earn__card-slide">' +
            renderOverviewEarnCard(item) +
            "</div>"
          );
        })
        .join("");
    } else {
      const pages = [];
      for (let i = 0; i < items.length; i += OVERVIEW_EARN_PAGE_SIZE) {
        pages.push(items.slice(i, i + OVERVIEW_EARN_PAGE_SIZE));
      }

      container.innerHTML = pages
        .map(function (pageItems) {
          return (
            '<div class="swiper-slide points-overview-earn__page">' +
            '<div class="points-overview-earn__page-grid">' +
            pageItems.map(renderOverviewEarnCard).join("") +
            "</div>" +
            "</div>"
          );
        })
        .join("");
    }

    initOverviewEarnCarousel();
  }

  function destroyOverviewEarnCarousel() {
    if (overviewEarnSwiper) {
      try {
        overviewEarnSwiper.destroy(true, true);
      } catch (e) {
        /* ignore */
      }
      overviewEarnSwiper = null;
    }
  }

  function getOverviewEarnNavButtons() {
    const carousel = root.querySelector("[data-overview-earn-carousel]");
    if (!carousel) return { prev: null, next: null };
    const mobile = isOverviewMobile();
    const scope = mobile
      ? ".points-overview-earn__controls"
      : ".points-overview-earn__viewport";
    return {
      prev: carousel.querySelector(scope + " [data-overview-earn-prev]"),
      next: carousel.querySelector(scope + " [data-overview-earn-next]"),
    };
  }

  function initOverviewEarnCarousel() {
    const swiperEl = root.querySelector("[data-overview-earn-swiper]");
    const navButtons = getOverviewEarnNavButtons();
    const prev = navButtons.prev;
    const next = navButtons.next;
    const paginationEl = root.querySelector("[data-overview-earn-pagination]");
    if (!swiperEl || !prev || !next || typeof Swiper === "undefined") return;

    destroyOverviewEarnCarousel();

    const slideCount = swiperEl.querySelectorAll(".swiper-slide").length;
    if (!slideCount) {
      if (paginationEl) paginationEl.innerHTML = "";
      prev.disabled = true;
      next.disabled = true;
      return;
    }

    const mobile = isOverviewMobile();
    const earnOptions = {
      slidesPerView: mobile ? "auto" : 1,
      spaceBetween: mobile ? 12 : 16,
      observer: true,
      observeParents: true,
      watchOverflow: true,
      navigation: {
        nextEl: next,
        prevEl: prev,
      },
    };

    if (paginationEl) {
      earnOptions.pagination = {
        el: paginationEl,
        clickable: true,
        bulletClass: "points-overview-earn__dot",
        bulletActiveClass: "is-active",
      };
    }

    overviewEarnSwiper = new Swiper(swiperEl, earnOptions);
  }

  function destroyOverviewRecommendedCarousel() {
    if (overviewRecommendedSwiper) {
      try {
        overviewRecommendedSwiper.destroy(true, true);
      } catch (e) {
        /* ignore */
      }
      overviewRecommendedSwiper = null;
    }
  }

  function setOverviewRecommendedLoading(isLoading) {
    const section = root.querySelector("[data-overview-recommended]");
    if (!section) return;
    section.classList.toggle("is-loading", !!isLoading);
    const loadingEl = section.querySelector(
      "[data-overview-recommended-loading]",
    );
    if (loadingEl) loadingEl.hidden = !isLoading;
  }

  function finishOverviewRecommendedCarousel() {
    setOverviewRecommendedLoading(false);
  }

  function initOverviewRecommendedCarousel(retryCount) {
    const section = root.querySelector("[data-overview-recommended]");
    const swiperEl = root.querySelector("[data-overview-recommended-swiper]");
    const prev = root.querySelector("[data-overview-recommended-prev]");
    const next = root.querySelector("[data-overview-recommended-next]");
    const paginationEl = root.querySelector(
      "[data-overview-recommended-pagination]",
    );
    const controls = root.querySelector("[data-overview-recommended-controls]");
    if (!section || !swiperEl) return;

    setOverviewRecommendedLoading(true);

    if (typeof Swiper === "undefined") {
      const attempt = typeof retryCount === "number" ? retryCount : 0;
      if (attempt < 40) {
        window.setTimeout(function () {
          initOverviewRecommendedCarousel(attempt + 1);
        }, 50);
      } else {
        finishOverviewRecommendedCarousel();
      }
      return;
    }

    destroyOverviewRecommendedCarousel();

    const slideCount = swiperEl.querySelectorAll(".swiper-slide").length;
    if (!slideCount) {
      if (controls) controls.hidden = true;
      finishOverviewRecommendedCarousel();
      return;
    }

    const mobile = isOverviewMobile();
    const needsCarousel = mobile ? slideCount > 2 : slideCount > 4;
    if (controls) controls.hidden = !needsCarousel;

    const recommendedOptions = {
      slidesPerView: Math.min(4, slideCount),
      spaceBetween: 12,
      observer: true,
      observeParents: true,
      watchOverflow: true,
      allowTouchMove: needsCarousel,
      breakpoints: {
        0: {
          slidesPerView: "auto",
          spaceBetween: 12,
        },
        769: {
          slidesPerView: Math.min(2, slideCount),
          spaceBetween: 12,
        },
        980: {
          slidesPerView: Math.min(4, slideCount),
          spaceBetween: 12,
        },
      },
      on: {
        init: function () {
          finishOverviewRecommendedCarousel();
        },
      },
    };

    if (needsCarousel && paginationEl) {
      recommendedOptions.pagination = {
        el: paginationEl,
        clickable: true,
        bulletClass: "points-center-recommended__dot",
        bulletActiveClass: "is-active",
      };
    }

    if (needsCarousel && prev && next) {
      recommendedOptions.navigation = {
        nextEl: next,
        prevEl: prev,
      };
    }

    overviewRecommendedSwiper = new Swiper(swiperEl, recommendedOptions);

    // Fallback if init event already fired synchronously
    if (overviewRecommendedSwiper && overviewRecommendedSwiper.initialized) {
      finishOverviewRecommendedCarousel();
    }
  }

  function handleOverviewMobileChange() {
    if (cachedEarnRules) {
      getEarnPointsContainers().forEach(function (container) {
        renderOverviewEarnPoints(container, cachedEarnRules);
      });
    }
    initOverviewRecommendedCarousel();
  }

  if (OVERVIEW_MOBILE_MQ) {
    if (OVERVIEW_MOBILE_MQ.addEventListener) {
      OVERVIEW_MOBILE_MQ.addEventListener("change", handleOverviewMobileChange);
    } else if (OVERVIEW_MOBILE_MQ.addListener) {
      OVERVIEW_MOBILE_MQ.addListener(handleOverviewMobileChange);
    }
  }

  function refreshOverviewEarnFromProfile() {
    if (!cachedEarnRules || !cachedEarnRules.length) return;
    getEarnPointsContainers().forEach(function (container) {
      renderOverviewEarnPoints(container, cachedEarnRules);
    });
  }

  window.addEventListener("points-center:profile-updated", function (event) {
    const detail = event && event.detail ? event.detail : null;
    if (detail && detail.birthday) {
      rememberProfileBirthdayIso(detail.birthday);
    } else {
      const fromDom = getProfileBirthdayIsoFromDom();
      if (fromDom) rememberProfileBirthdayIso(fromDom);
    }
    refreshOverviewEarnFromProfile();
  });

  function handleEarnPointsClick(event) {
    const birthdayBtn =
      event.target && event.target.closest("[data-earn-point-profile-birthday]");
    if (birthdayBtn) {
      if (!isLoggedIn()) {
        redirectToLogin();
        return;
      }
      goToProfileBirthdayField();
      return;
    }

    const reviewBtn =
      event.target && event.target.closest("[data-earn-point-review]");
    if (reviewBtn) {
      const reviewUrl =
        normalizeSocialLink(EARN_PRODUCT_REVIEW_URL) || DEFAULT_EARN_CTA_PATH;
      window.open(reviewUrl, "_blank", "noopener,noreferrer");
      return;
    }

    const purchaseBtn =
      event.target && event.target.closest("[data-earn-point-purchase]");
    if (purchaseBtn) {
      const shopUrl =
        normalizeSocialLink(EARN_PURCHASE_SHOP_URL) || DEFAULT_EARN_CTA_PATH;
      window.location.href = shopUrl;
      return;
    }

    const rulesBtn =
      event.target && event.target.closest("[data-earn-point-rule]");
    if (rulesBtn) {
      const text = rulesBtn.getAttribute("data-earn-point-rule") || "";
      if (!text) return;
      alert(text);
      return;
    }

    const claimBtn =
      event.target && event.target.closest("[data-earn-point-claim]");
    if (!claimBtn || claimBtn.disabled) return;

    if (!isLoggedIn()) {
      redirectToLogin();
      return;
    }

    const categoryName =
      claimBtn.getAttribute("data-earn-point-category") || "";
    if (categoryName === CATEGORY_LINK_SHARING) {
      goToMembershipRefer();
      return;
    }
    if (categoryName !== CATEGORY_SOCIAL) return;

    const baseUrl = getLoyaltyBaseUrl();
    const shopifyId = getGiftCardShopifyId();
    const ruleId = Number(claimBtn.getAttribute("data-earn-point-claim")) || 0;
    if (!baseUrl || !shopifyId || !ruleId) return;

    if (cachedEarnRules && cachedEarnRules.length) {
      const matchedRule = cachedEarnRules.find(function (rule) {
        return Number(rule && rule.id) === ruleId;
      });
      if (matchedRule && isProductReviewRule(toEarnRuleItem(matchedRule))) {
        return;
      }
    }

    const socialLink =
      claimBtn.getAttribute("data-earn-point-social-link") || "";
    if (socialLink) {
      window.open(socialLink, "_blank", "noopener,noreferrer");
    }

    claimBtn.disabled = true;
    fetch(baseUrl.replace(/\/$/, "") + "/front/points/social/claim", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ shopifyId: shopifyId, ruleId: ruleId }),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        if (!json || Number(json.code) !== 200) {
          claimBtn.disabled = false;
          return;
        }
        earnPointsLoaded = false;
        cachedEarnRules = null;
        loadEarnPoints(true);
        window.dispatchEvent(
          new CustomEvent("membership:points-updated", {
            detail: { source: "points-center-earn", ruleId: ruleId },
          }),
        );
      })
      .catch(function () {
        claimBtn.disabled = false;
      });
  }

  if (!root.dataset.earnPointsActionsInit) {
    root.addEventListener("click", handleEarnPointsClick);
    root.dataset.earnPointsActionsInit = "true";
  }

  function getEarnPointsContainers() {
    return Array.prototype.slice.call(
      root.querySelectorAll("[data-overview-earn-points-grid]"),
    );
  }

  // Earn Points Rules
  function loadEarnPoints(force) {
    const containers = getEarnPointsContainers();
    if (!containers.length) return;

    function setEarnMessage(message, isError) {
      destroyOverviewEarnCarousel();
      containers.forEach(function (container) {
        container.innerHTML =
          '<div class="swiper-slide points-overview-earn__page">' +
          '<p style="margin:0;color:' +
          (isError ? "#ef4444" : "#64748b") +
          ';">' +
          message +
          "</p>" +
          "</div>";
      });
    }

    const baseUrl = (
      root
        .querySelector('[data-points-center-panel="account-overview"]')
        ?.getAttribute("data-loyalty-base-url") || ""
    ).trim();
    if (!baseUrl) {
      setEarnMessage(t("common.no_api"), false);
      return;
    }

    fetchProfileBirthdayForEarn().then(function () {
      if (!force && earnPointsLoaded && cachedEarnRules) {
        containers.forEach(function (container) {
          renderOverviewEarnPoints(container, cachedEarnRules);
        });
        return;
      }

      setEarnMessage(t("common.loading"), false);

      const shopifyId = getGiftCardShopifyId();

      fetch(baseUrl.replace(/\/$/, "") + "/front/points/rules/list", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shopifyId: shopifyId }),
      })
        .then((res) => res.json())
        .then(function (json) {
          if (!json || json.code !== 200 || !json.data) {
            setEarnMessage(t("rewards.failed_earn"), true);
            return;
          }
          cachedEarnRules = json.data;
          earnPointsLoaded = true;
          containers.forEach(function (container) {
            renderOverviewEarnPoints(container, json.data);
          });
        })
        .catch(function () {
          setEarnMessage(t("common.network_error"), true);
        });
    });
  }

  function renderEarnPoints(container, rules) {
    const pointsIconSvg = getEcocreditsIconHtml("earn-point-card__points-icon");

    const items = (Array.isArray(rules) ? rules : [])
      .map(toEarnRuleItem)
      .filter(function (item) {
        return item.title || item.pointsText;
      })
      .sort(function (a, b) {
        return (
          Number(shouldShowEarnRuleAsCompleted(a)) -
          Number(shouldShowEarnRuleAsCompleted(b))
        );
      });

    if (!items.length) {
      container.innerHTML = "";
      return;
    }

    container.innerHTML = items
      .map(function (item) {
        const title = escapeHtml(item.title);
        const pointsText = escapeHtml(item.pointsText);
        const actionLabel = escapeHtml(item.buttonLabel || item.title);
        const icon = escapeHtml(item.icon);
        const socialLink = escapeHtml(item.socialLink);
        const categoryName = escapeHtml(item.categoryName);
        const ruleDescription = escapeHtml(item.ruleDescription);
        const metaPreview = escapeHtml(item.metaPreview);
        const rulesHtml =
          '<button type="button" class="earn-point-card__rules" data-earn-point-rule="' +
          ruleDescription +
          '">' +
          escapeHtml(t("rewards.rules_link")) +
          "</button>";
        const iconUrl = escapeHttpUrl(item.icon);
        const iconHtml = iconUrl
          ? '<img class="earn-point-card__icon" src="' +
            iconUrl +
            '" alt="" loading="lazy" decoding="async">'
          : "";
        let buttonHtml = "";
        if (shouldShowEarnRuleAsCompleted(item)) {
          buttonHtml =
            '<button type="button" class="earn-point-card__btn earn-point-card__btn--done" disabled>' +
            escapeHtml(t("rewards.got_the_points")) +
            "</button>";
        } else if (isProductReviewRule(item)) {
          const reviewLink = getProductReviewLink(item);
          const reviewLabel = escapeHtml(getProductReviewButtonLabel(item));
          buttonHtml = buildOverviewEarnReviewButton(reviewLabel, reviewLink).replace(
            /membership-earn-card__btn/g,
            "earn-point-card__btn earn-point-card__btn--action",
          );
        } else if (isBirthdayEarnRule(item)) {
          buttonHtml =
            '<button type="button" class="earn-point-card__btn earn-point-card__btn--action" data-earn-point-profile-birthday>' +
            actionLabel +
            "</button>";
        } else if (isPurchaseEarnRule(item)) {
          const shopLink = getPurchaseEarnLink(item);
          const shopLabel = escapeHtml(getPurchaseEarnButtonLabel(item));
          buttonHtml = buildOverviewEarnShopButton(
            shopLabel,
            shopLink,
          ).replace(
            /membership-earn-card__btn/g,
            "earn-point-card__btn earn-point-card__btn--action",
          );
        } else if (
          item.categoryName === CATEGORY_SOCIAL ||
          item.categoryName === CATEGORY_LINK_SHARING
        ) {
          let claimAttrs =
            'type="button" class="earn-point-card__btn earn-point-card__btn--action" data-earn-point-claim="' +
            item.id +
            '" data-earn-point-category="' +
            categoryName +
            '"';
          if (item.categoryName === CATEGORY_SOCIAL && item.socialLink) {
            claimAttrs += ' data-earn-point-social-link="' + socialLink + '"';
          }
          buttonHtml =
            "<button " + claimAttrs + ">" + actionLabel + "</button>";
        }
        const cardClass = shouldShowEarnRuleAsCompleted(item)
          ? "earn-point-card earn-point-card--completed"
          : "earn-point-card";
        return (
          '<div class="' +
          cardClass +
          '">' +
          '<div class="earn-point-card__header">' +
          '<h4 class="earn-point-card__title">' +
          title +
          "</h4>" +
          iconHtml +
          "</div>" +
          '<div class="earn-point-card__points">' +
          pointsIconSvg +
          '<span class="earn-point-card__points-text">' +
          pointsText +
          "</span>" +
          rulesHtml +
          "</div>" +
          buttonHtml +
          (metaPreview
            ? '<div class="earn-point-card__meta">' + metaPreview + "</div>"
            : "") +
          "</div>"
        );
      })
      .join("");
  }

  // ── Redeem modal & free-product flow ───────────────────────────────
  // Store list (https://uuqdy6sfce.apifox.cn/459920784e0) returns ruleType per item;
  // each type redeems via its own endpoint (shopifyId + redeemRuleId).
  function normalizeRuleType(item) {
    const snap = getStoreItemSnap(item);
    return String(
      (snap && snap.ruleType) || (item && item.ruleType) || "",
    ).trim();
  }

  const FREE_PRODUCT_RULE_TYPE = "Free Product";
  const FREE_PRODUCT_POINTS_ADJUST_PATH =
    "/front/redeem/free-product/points/adjust";
  const FREE_PRODUCT_INTERVAL_CHECK_PATH = "/front/redeem/interval/check";

  // Apifox: 兑换礼品卡 / 订单折扣 / 产品折扣 / 免运费
  const REDEEM_ENDPOINTS = {
    "Gift Card": "/front/redeem/gift-card/redeem",
    "Order Discount": "/front/redeem/order-discount/redeem",
    "Product Discount": "/front/redeem/product-discount/redeem",
    "Free Shipping": "/front/redeem/free-shipping/redeem",
  };

  function isFreeProductRuleType(ruleType) {
    return (
      String(ruleType || "")
        .trim()
        .toLowerCase() === "free product"
    );
  }

  function parseShopifyResourceId(value) {
    if (value == null || value === "") return { variantId: 0, productId: 0 };
    const raw = String(value).trim();
    const variantMatch = raw.match(/ProductVariant\/(\d+)/i);
    if (variantMatch) return { variantId: Number(variantMatch[1]), productId: 0 };
    const productMatch = raw.match(/Product\/(\d+)/i);
    if (productMatch) return { variantId: 0, productId: Number(productMatch[1]) };
    const asNumber = Number(raw);
    if (!Number.isNaN(asNumber) && asNumber > 0) {
      return { variantId: asNumber, productId: 0 };
    }
    const tail = raw.match(/(\d+)\s*$/);
    return tail
      ? { variantId: Number(tail[1]), productId: 0 }
      : { variantId: 0, productId: 0 };
  }

  function parseFreeProductId(value) {
    return parseShopifyResourceId(value).variantId;
  }

  function getFreeProductVariantId(item) {
    if (!item) return 0;
    if (item._resolvedFreeProductVariantId) {
      return Number(item._resolvedFreeProductVariantId) || 0;
    }
    const snap = getStoreItemSnap(item);
    const parsed = parseShopifyResourceId(snap.freeProductId);
    if (parsed.variantId) return parsed.variantId;
    if (snap.freeProductVariantId != null) {
      return parseFreeProductId(snap.freeProductVariantId);
    }
    if (snap.variantId != null) return parseFreeProductId(snap.variantId);
    if (item.freeProductId != null) {
      return parseFreeProductId(item.freeProductId);
    }
    return 0;
  }

  function getStoreItemRedeemCost(item) {
    const snap = getStoreItemSnap(item);
    if (snap.costToRedeem != null && snap.costToRedeem !== "") {
      return Number(snap.costToRedeem) || 0;
    }
    if (item && item.costToRedeem != null) {
      return Number(item.costToRedeem) || 0;
    }
    return 0;
  }

  function getRedeemEndpoint(ruleType) {
    const key = String(ruleType || "").trim();
    if (!key) return null;
    if (REDEEM_ENDPOINTS[key]) return REDEEM_ENDPOINTS[key];
    const lower = key.toLowerCase();
    for (const name in REDEEM_ENDPOINTS) {
      if (
        Object.prototype.hasOwnProperty.call(REDEEM_ENDPOINTS, name) &&
        name.toLowerCase() === lower
      ) {
        return REDEEM_ENDPOINTS[name];
      }
    }
    return null;
  }

  function isRedeemableItem(item) {
    const ruleType = normalizeRuleType(item);
    if (isFreeProductRuleType(ruleType)) {
      return !!getFreeProductVariantId(item);
    }
    return !!getRedeemEndpoint(ruleType);
  }

  function getInsufficientPointsReason(cost) {
    const available = Number(userAvailablePoints) || 0;
    const required = Number(cost) || 0;
    return t("redeem.insufficient", {
      available: numberFormatter.format(available),
      required: numberFormatter.format(required),
    });
  }

  function getUnsupportedRuleTypeReason(item) {
    const ruleType = normalizeRuleType(item);
    if (isFreeProductRuleType(ruleType) && !getFreeProductVariantId(item)) {
      return "";
    }
    if (!ruleType) return t("redeem.unavailable");
    return t("redeem.unavailable_type", { type: ruleType });
  }

  function getApiResponseMessage(json) {
    if (!json || json.msg == null) return "";
    return String(json.msg).trim();
  }

  function alertApiErrorMessage(err) {
    const message = err && err.message ? String(err.message).trim() : "";
    if (message) alert(message);
  }

  function reportFreeProductBlockedByApi(item) {
    const baseUrl = getLoyaltyBaseUrl();
    const shopifyId = getGiftCardShopifyId();
    const snap = getStoreItemSnap(item);
    const redeemRuleId = Number(snap.id || (item && item.id)) || 0;
    if (!baseUrl || !shopifyId || !redeemRuleId) return Promise.resolve();
    return checkFreeProductRedeemInterval(baseUrl, shopifyId, redeemRuleId).catch(
      function (err) {
        alertApiErrorMessage(err);
      },
    );
  }

  function checkFreeProductRedeemInterval(baseUrl, shopifyId, redeemRuleId) {
    return fetch(
      baseUrl.replace(/\/$/, "") + FREE_PRODUCT_INTERVAL_CHECK_PATH,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          shopifyId: shopifyId,
          redeemRuleId: redeemRuleId,
        }),
      },
    )
      .then(function (res) {
        if (!res.ok) throw new Error("HTTP_" + res.status);
        return res.json();
      })
      .then(function (json) {
        if (!json || Number(json.code) !== 200) {
          throw new Error(getApiResponseMessage(json));
        }
        return json;
      });
  }

  function adjustFreeProductPoints(
    baseUrl,
    shopifyId,
    points,
    operationType,
    ruleId,
  ) {
    return fetch(baseUrl.replace(/\/$/, "") + FREE_PRODUCT_POINTS_ADJUST_PATH, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        shopifyId: shopifyId,
        points: points,
        operationType: operationType,
        ruleId: ruleId,
      }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error("HTTP_" + res.status);
        return res.json();
      })
      .then(function (json) {
        if (!json || Number(json.code) !== 200) {
          throw new Error(getApiResponseMessage(json));
        }
        return json;
      });
  }

  function getCartAddErrorMessage(response, httpStatus) {
    if (response && typeof response === "object") {
      const statusCode = Number(response.status);
      const message = String(
        response.description || response.message || response.msg || "",
      ).trim();
      if (statusCode >= 400) {
        return message || t("redeem.failed_cart");
      }
      if (message && /sold\s*out/i.test(message)) {
        return message;
      }
      // Shopify error payload without a successful line item id
      if (
        message &&
        response.id == null &&
        response.variant_id == null &&
        !response.items
      ) {
        return message;
      }
    }
    if (httpStatus && httpStatus >= 400) {
      return t("redeem.failed_cart");
    }
    return "";
  }

  function findExistingFreeRedeemInCart(cart) {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];

    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      const props = item && item.properties ? item.properties : {};
      const encoded = String(props._free_redeem || props.free_redeem || "");
      const encodedRule = Number(encoded.split("|")[0]) || 0;
      const isFree =
        (props._free != null && String(props._free) !== "") ||
        (props.free != null && String(props.free) !== "") ||
        encodedRule > 0;
      if (isFree) return item;
    }
    return null;
  }

  function assertNoDuplicateFreeRedeem() {
    const cartJsUrl =
      (window.Theme && Theme.routes && Theme.routes.cart_js_url) || "/cart.js";
    return fetch(cartJsUrl, { headers: { Accept: "application/json" } })
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .then(function (cart) {
        if (findExistingFreeRedeemInCart(cart)) {
          throw new Error(t("redeem.free_already_in_cart"));
        }
      });
  }

  function addFreeProductToCart(variantId, ruleId, points) {
    const formData = new FormData();
    formData.append("id", String(variantId));
    formData.append("quantity", "1");
    formData.append("properties[_free]", "true");
    formData.append(
      "properties[_free_redeem]",
      String(ruleId) + "|" + String(points),
    );

    const sectionIds = [];
    document
      .querySelectorAll("cart-items-component")
      .forEach(function (component) {
        if (component instanceof HTMLElement && component.dataset.sectionId) {
          sectionIds.push(component.dataset.sectionId);
        }
      });
    if (sectionIds.length) {
      formData.append("sections", sectionIds.join(","));
    }

    const cartAddUrl =
      (window.Theme && Theme.routes && Theme.routes.cart_add_url) ||
      "/cart/add.js";

    return fetch(cartAddUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: formData,
    }).then(function (res) {
      return res.text().then(function (text) {
        let data = null;
        try {
          data = text ? JSON.parse(text) : null;
        } catch (e) {
          const parseErr = new Error(t("redeem.failed_cart"));
          parseErr.cartFailed = true;
          throw parseErr;
        }
        const errMsg = getCartAddErrorMessage(data, res.status);
        if (errMsg || !res.ok) {
          const cartErr = new Error(errMsg || t("redeem.failed_cart"));
          cartErr.cartFailed = true;
          throw cartErr;
        }
        return data;
      });
    });
  }

  function dispatchCartAdd(variantId, response) {
    document.dispatchEvent(
      new CustomEvent("cart:update", {
        bubbles: true,
        detail: {
          resource: {},
          sourceId: String(variantId),
          data: {
            source: "points-center-free-product",
            itemCount: 1,
            variantId: String(variantId),
            sections:
              response && response.sections ? response.sections : undefined,
          },
        },
      }),
    );
  }

  function openCartDrawer() {
    const drawer = document.querySelector("cart-drawer-component");
    if (drawer && typeof drawer.open === "function") {
      drawer.open();
    }
  }

  function setRedeemSubmitTooltip(submitBtn, reason) {
    if (!submitBtn) return;
    const wrap =
      submitBtn.closest("[data-redeem-submit-wrap]") || submitBtn.parentElement;
    const text = (reason || "").trim();
    if (!text) {
      submitBtn.removeAttribute("title");
      submitBtn.removeAttribute("aria-label");
      if (wrap) wrap.removeAttribute("title");
      return;
    }
    submitBtn.setAttribute("aria-label", text);
    if (wrap) {
      wrap.setAttribute("title", text);
    } else {
      submitBtn.setAttribute("title", text);
    }
  }

  function buildRedeemConfirmVisualHtml(item) {
    const snap = getStoreItemSnap(item);
    const points = getStoreItemPoints(item);
    const ruleType = normalizeRuleType(item);
    const minPurchase = formatStoreMinPurchase(snap, item);
    const typeLabel = getStoreBenefitTypeLabel(ruleType);
    const pointsLabel = t("redeem.cost_label", {
      count: numberFormatter.format(points),
    });
    return (
      '<div class="points-redeem-modal__ticket">' +
      getRedeemModalTicketBgHtml(ruleType) +
      '<div class="points-redeem-modal__ticket-body">' +
      '<div class="points-redeem-modal__ticket-top">' +
      formatStoreTicketValueHtml(snap, item) +
      (minPurchase
        ? '<p class="benefits-redeem-ticket__min">' + minPurchase + "</p>"
        : "") +
      formatGiftCardExpiryHtml(item, "benefits-redeem-ticket__expiry") +
      "</div>" +
      '<div class="points-redeem-modal__ticket-mid">' +
      formatRedeemCostHtml(pointsLabel) +
      '<p class="benefits-redeem-ticket__type">' +
      escapeHtml(typeLabel) +
      "</p>" +
      "</div></div></div>"
    );
  }

  function copyTextSilent(value) {
    if (!value) return Promise.resolve(false);

    const fallbackCopy = function () {
      try {
        const ta = document.createElement("textarea");
        ta.value = String(value);
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        ta.style.top = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return !!ok;
      } catch (e) {
        return false;
      }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(String(value)).then(
        function () {
          return true;
        },
        function () {
          return fallbackCopy();
        },
      );
    }

    return Promise.resolve(fallbackCopy());
  }

  function markRedeemCopyBtnCopied(copyBtn) {
    if (!copyBtn) return;
    copyBtn.classList.add("is-copied");
    copyBtn.setAttribute("aria-label", t("common.copied"));
    setTimeout(function () {
      copyBtn.classList.remove("is-copied");
      copyBtn.setAttribute("aria-label", t("common.copy_code"));
    }, 1500);
  }

  function setCopyBtnLoading(copyBtn, isLoading) {
    if (!copyBtn) return;
    copyBtn.classList.toggle("is-loading", !!isLoading);
    if (isLoading) {
      copyBtn.disabled = true;
      copyBtn.setAttribute("aria-busy", "true");
      return;
    }
    copyBtn.removeAttribute("aria-busy");
    copyBtn.disabled =
      copyBtn.getAttribute("data-copy-default-disabled") === "true";
  }

  function isGiftCardRuleType(ruleType) {
    const t = String(ruleType || "")
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, " ");
    return t === "gift card" || t === "giftcard";
  }

  function isMaskedRedeemCode(value) {
    const raw = String(value || "").trim();
    if (!raw) return false;
    return /^\*+/.test(raw) || raw.indexOf("•") !== -1;
  }

  function extractRedeemPlainCode(data, isGiftCard) {
    if (!data || isGiftCard) return "";
    const candidates = [
      data.discountCode,
      data.giftCardCode,
      data.code,
      data.couponCode,
    ];
    for (let i = 0; i < candidates.length; i++) {
      const value = String(candidates[i] || "").trim();
      if (value && !isMaskedRedeemCode(value)) return value;
    }
    return "";
  }

  function buildRedeemSuccessVisualHtml(item, code, meta) {
    const info = meta || {};
    const snap = getStoreItemSnap(item);
    const minPurchase = formatStoreMinPurchase(snap, item);
    const isGift = !!info.isGiftCard;
    const redeemId = Number(info.redeemId) > 0 ? Number(info.redeemId) : 0;
    const hasPlainCode = !!info.hasPlainCode;
    const codeText = code || "••••••••";
    // Gift card / missing plaintext: copy via /front/redeem/redeem/code { redeemId }
    const useRedeemIdCopy = isGift || (!hasPlainCode && redeemId > 0);
    const canCopy = hasPlainCode || redeemId > 0;
    const eyebrow = isGift
      ? t("coupons.gift_card_eyebrow")
      : t("coupons.discount_coupon");
    return (
      '<div class="points-redeem-modal__ticket points-redeem-modal__ticket--success">' +
      getRedeemSuccessTicketBgHtml() +
      '<div class="points-redeem-modal__ticket-body points-redeem-modal__ticket-body--success">' +
      '<p class="points-redeem-modal__ticket-eyebrow">' +
      escapeHtml(eyebrow) +
      "</p>" +
      '<div class="points-redeem-modal__ticket-top points-redeem-modal__ticket-top--success">' +
      formatStoreTicketValueHtml(snap, item) +
      (minPurchase
        ? '<p class="benefits-redeem-ticket__min">' + minPurchase + "</p>"
        : "") +
      "</div>" +
      '<div class="points-redeem-modal__ticket-code">' +
      '<button type="button" class="points-redeem-success-card__code" data-redeem-success-copy' +
      (useRedeemIdCopy
        ? ' data-redeem-id="' + escapeHtml(String(redeemId)) + '"'
        : ' data-coupon-code="' + escapeHtml(codeText) + '"') +
      (canCopy ? "" : ' data-copy-default-disabled="true" disabled') +
      ' aria-label="' +
      escapeHtml(t("common.copy_code")) +
      '">' +
      '<span class="points-redeem-success-card__code-text">' +
      escapeHtml(codeText) +
      "</span>" +
      COUPON_COPY_ICON_SVG +
      "</button></div></div></div>"
    );
  }

  function extractRedeemCode(data) {
    if (!data) return "";
    return (
      data.discountCode ||
      data.giftCardCode ||
      data.code ||
      data.discountCodeMasked ||
      data.giftCardCodeMasked ||
      ""
    );
  }

  // Gift-card redeem returns { code: "****xxxx", redeemId } — prefer redeemId.
  function extractRedeemRecordId(data) {
    if (!data || typeof data !== "object") return 0;
    const candidates = [
      data.redeemId,
      data.redeemRecordId,
      data.redeem_id,
      data.redeemRecord && data.redeemRecord.id,
      data.id,
    ];
    for (let i = 0; i < candidates.length; i++) {
      const n = Number(candidates[i]);
      if (!Number.isNaN(n) && n > 0) return n;
    }
    return 0;
  }

  function normalizeRedeemResponseData(json) {
    const raw =
      json &&
      typeof json.data === "object" &&
      json.data &&
      !Array.isArray(json.data)
        ? json.data
        : {};
    const data = Object.assign({}, raw);
    if (data.redeemId == null && json && json.redeemId != null) {
      data.redeemId = json.redeemId;
    }
    if (data.redeemRecordId == null && json && json.redeemRecordId != null) {
      data.redeemRecordId = json.redeemRecordId;
    }
    return data;
  }

  function extractDisplayRedeemCode(data, isGiftCard) {
    if (!data) return "";
    if (isGiftCard) {
      return (
        data.giftCardCodeMasked || data.discountCodeMasked || data.code || ""
      );
    }
    return (
      data.discountCode ||
      data.giftCardCode ||
      data.code ||
      data.discountCodeMasked ||
      data.giftCardCodeMasked ||
      ""
    );
  }

  function copyGiftCardRedeemCode(copyBtn, redeemId) {
    const id = Number(redeemId);
    const baseUrl = getLoyaltyBaseUrl();
    const fetchCode =
      typeof window.fetchRedeemCodePlaintext === "function"
        ? window.fetchRedeemCodePlaintext
        : null;
    if (!copyBtn || !id || !baseUrl || !fetchCode) {
      console.warn(
        "[redeem] copy skipped: missing redeemId / baseUrl / fetchCode",
        {
          redeemId: id,
          baseUrl: !!baseUrl,
          fetchCode: !!fetchCode,
        },
      );
      return Promise.resolve(false);
    }
    if (copyBtn.classList.contains("is-loading")) return Promise.resolve(false);
    setCopyBtnLoading(copyBtn, true);
    // POST /front/redeem/redeem/code  body: { redeemId }
    return fetchCode(baseUrl, id)
      .then(function (value) {
        if (!value) {
          console.warn("[redeem] empty code from /front/redeem/redeem/code", {
            redeemId: id,
          });
          return false;
        }
        return copyTextWithTip(value, t("common.copied"), copyBtn).then(
          function (ok) {
            if (ok) {
              markRedeemCopyBtnCopied(copyBtn);
            } else {
              console.warn("[redeem] copy to clipboard failed");
            }
            return ok;
          },
        );
      })
      .catch(function (err) {
        console.error("[redeem] fetch/decrypt code failed", err);
        alert(t("redeem.failed_code"));
        return false;
      })
      .finally(function () {
        setCopyBtnLoading(copyBtn, false);
      });
  }

  function setRedeemModalPane(modal, pane) {
    modal.querySelectorAll("[data-redeem-pane]").forEach(function (el) {
      const isActive = el.getAttribute("data-redeem-pane") === pane;
      el.hidden = !isActive;
    });
  }

  function getRedeemModalEl() {
    if (root) {
      const inRoot = root.querySelector("[data-redeem-modal]");
      if (inRoot) return inRoot;
    }
    return document.querySelector("[data-redeem-modal]");
  }

  function closeRedeemModal(modal) {
    if (!modal) modal = getRedeemModalEl();
    closePointsCenterBottomSheet(modal, {
      dialogSelector: ".points-redeem-modal__dialog",
      onClosed: function () {
        document.documentElement.style.overflow = "";
      },
    });
  }

  function showRedeemSuccessModal(modal, item, data, options) {
    const opts = options || {};
    const ruleType = normalizeRuleType(item);
    const redeemId = extractRedeemRecordId(data);
    // Only treat real gift-card rule types as gift cards (avoid misclassifying
    // Product Discount responses that return { id, code }).
    const isGiftCard =
      isGiftCardRuleType(ruleType) || !!opts.forceGiftCard;
    let plainCode = extractRedeemPlainCode(data, isGiftCard);
    let displayCode =
      plainCode || extractDisplayRedeemCode(data, isGiftCard) || "";

    function renderSuccessVisual(code, hasPlain) {
      const visual = modal.querySelector("[data-redeem-success-visual]");
      if (!visual) return;
      visual.removeAttribute("aria-hidden");
      visual.innerHTML = buildRedeemSuccessVisualHtml(item, code, {
        isGiftCard: isGiftCard,
        redeemId: redeemId,
        hasPlainCode: !!hasPlain,
      });
      bindSuccessCopyBtn(hasPlain ? code : "");
    }

    function bindSuccessCopyBtn(resolvedPlainCode) {
      const copyBtn = modal.querySelector("[data-redeem-success-copy]");
      if (!copyBtn) return;
      copyBtn.onclick = function () {
        if (copyBtn.disabled) return;

        if (isGiftCard || copyBtn.hasAttribute("data-redeem-id")) {
          const id =
            copyBtn.getAttribute("data-redeem-id") ||
            modal.getAttribute("data-last-redeem-id") ||
            String(redeemId || "");
          copyGiftCardRedeemCode(copyBtn, id);
          return;
        }

        const value =
          copyBtn.getAttribute("data-coupon-code") ||
          resolvedPlainCode ||
          plainCode ||
          "";
        if (!value || isMaskedRedeemCode(value)) {
          console.warn("[redeem] copy skipped: empty coupon code");
          return;
        }
        copyTextWithTip(value, t("common.copied"), copyBtn).then(function (ok) {
          if (ok) {
            markRedeemCopyBtnCopied(copyBtn);
          } else {
            console.warn("[redeem] copy to clipboard failed");
          }
        });
      };
    }

    const successTextEl = modal.querySelector("[data-redeem-success-text]");
    if (successTextEl) {
      const snap = getStoreItemSnap(item);
      const redeemLabel = String(
        (data && (data.redeemLabel || data.redeem_label)) ||
          (snap && (snap.redeemLabel || snap.redeem_label)) ||
          (item && (item.redeemLabel || item.redeem_label)) ||
          "",
      ).trim();
      successTextEl.textContent = redeemLabel || t("redeem.success_text");
    }

    if (redeemId > 0) {
      modal.setAttribute("data-last-redeem-id", String(redeemId));
    } else {
      modal.removeAttribute("data-last-redeem-id");
    }
    setRedeemModalPane(modal, "success");
    modal.hidden = false;
    document.documentElement.style.overflow = "hidden";

    renderSuccessVisual(displayCode, !!plainCode);

    // Product/Order discount may only return redeemId; fetch plaintext for display.
    if (!isGiftCard && !plainCode && redeemId > 0) {
      const baseUrl = getLoyaltyBaseUrl();
      const fetchCode =
        typeof window.fetchRedeemCodePlaintext === "function"
          ? window.fetchRedeemCodePlaintext
          : null;
      if (baseUrl && fetchCode) {
        fetchCode(baseUrl, redeemId)
          .then(function (value) {
            const next = String(value || "").trim();
            if (!next || modal.hidden) return;
            plainCode = next;
            displayCode = next;
            renderSuccessVisual(next, true);
          })
          .catch(function (err) {
            console.warn("[redeem] fetch success code failed", err);
          });
      }
    }
  }

  function formatRedeemSuccessLines(json, data) {
    const lines = [json.msg || t("redeem.redeemed_ok")];
    if (data.discountCodeMasked)
      lines.push(t("common.code_value", { code: data.discountCodeMasked }));
    else if (data.discountCode)
      lines.push(t("common.code_value", { code: data.discountCode }));
    if (data.giftCardCodeMasked)
      lines.push(t("common.code_value", { code: data.giftCardCodeMasked }));
    else if (data.giftCardCode)
      lines.push(t("common.code_value", { code: data.giftCardCode }));
    if (data.amount != null) lines.push("$" + data.amount);
    if (data.pointsSpent != null)
      lines.push(t("redeem.points_spent", { count: data.pointsSpent }));
    if (data.availablePointsAfter != null)
      lines.push(
        t("redeem.points_remaining", { count: data.availablePointsAfter }),
      );
    return lines;
  }

  function refreshPointsAfterRedeem(availablePointsAfter) {
    return fetchLoyaltySummary(true)
      .then(function (data) {
        if (!data && availablePointsAfter != null) {
          applyPointsUpdate(availablePointsAfter);
          return;
        }
        const grid = root.querySelector("[data-points-summary-grid]");
        if (grid && data) renderMyPointSummary(grid, data);
        return Promise.all([
          fetchLoyaltyTiers(false),
          fetchBonusRulesSimple(false),
        ]).then(function (results) {
          const tiers = results[0];
          const couponEl = root.querySelector("[data-asset-coupon-count]");
          const couponCount = couponEl ? parseCountUpNumber(couponEl.textContent) : 0;
          if (data) {
            renderOverviewLevel(data, tiers);
            renderBenefitsCenter(data, tiers, couponCount);
          }
        });
      })
      .catch(function () {
        if (availablePointsAfter != null) {
          applyPointsUpdate(availablePointsAfter);
        }
      })
      .finally(function () {
        invalidatePointStoreCache();
        invalidateBenefitCaches();
        refreshBenefitPanelsIfVisible();
        skipNextPointsUpdatedFetch = true;
        window.dispatchEvent(new CustomEvent("membership:points-updated"));
      });
  }

  window.addEventListener("membership:points-updated", function () {
    if (skipNextPointsUpdatedFetch) {
      skipNextPointsUpdatedFetch = false;
      return;
    }
    refreshLoyaltyOverview(true);
    invalidatePointStoreCache();
    invalidateBenefitCaches();
    earnPointsLoaded = false;
    cachedEarnRules = null;
    loadEarnPoints(true);
    const grid = root.querySelector("[data-points-summary-grid]");
    if (grid && loyaltySummaryData) {
      renderMyPointSummary(grid, loyaltySummaryData);
    }
    if (isMyRewardsPanelActive()) {
      loadPointsHistory(historyCurrentPage || 1);
    }
    refreshBenefitPanelsIfVisible();
  });

  function redeemFreeProduct(item, submitBtn, modal) {
    const baseUrl = getLoyaltyBaseUrl();
    const shopifyId = getGiftCardShopifyId();
    const ruleId = Number(item && item.id) || 0;
    const cost = getStoreItemRedeemCost(item);
    const variantId = getFreeProductVariantId(item);

    if (!baseUrl || !shopifyId || !ruleId || !variantId) {
      alert(t("redeem.unable_product"));
      return;
    }

    if (!canAffordRedeem(cost)) {
      submitBtn.disabled = true;
      setRedeemSubmitTooltip(submitBtn, getInsufficientPointsReason(cost));
      return;
    }

    submitBtn.disabled = true;
    submitBtn.textContent = t("redeem.redeeming");

    let pointsSubtracted = false;

    assertNoDuplicateFreeRedeem()
      .then(function () {
        return checkFreeProductRedeemInterval(baseUrl, shopifyId, ruleId);
      })
      .then(function () {
        return adjustFreeProductPoints(
          baseUrl,
          shopifyId,
          cost,
          "SUBTRACT",
          ruleId,
        );
      })
      .then(function () {
        pointsSubtracted = true;
        return addFreeProductToCart(variantId, ruleId, cost);
      })
      .then(function (response) {
        dispatchCartAdd(variantId, response);
        openCartDrawer();
        closeRedeemModal(modal);
        invalidateBenefitCaches();
        return refreshPointsAfterRedeem(null);
      })
      .catch(function (err) {
        let message = err && err.message ? String(err.message).trim() : "";

        let refundPromise = Promise.resolve();
        if (pointsSubtracted) {
          // Sold out / cart failure after deduct: always refund points.
          refundPromise = adjustFreeProductPoints(
            baseUrl,
            shopifyId,
            cost,
            "ADD",
            ruleId,
          )
            .then(function () {
              return refreshPointsAfterRedeem(null);
            })
            .catch(function () {
              message =
                message +
                "\n" +
                t("redeem.points_refund_failed");
            });
        }

        return refundPromise.finally(function () {
          if (message) alert(message);
        });
      })
      .finally(function () {
        applyModalRedeemState(item, submitBtn);
      });
  }

  function redeemReward(item, submitBtn, modal) {
    const baseUrl = getLoyaltyBaseUrl();
    const shopifyId = getGiftCardShopifyId();
    const snap = getStoreItemSnap(item);
    const redeemRuleId = Number(snap.id || (item && item.id)) || 0;
    const cost =
      snap.costToRedeem != null
        ? snap.costToRedeem
        : item.costToRedeem != null
          ? item.costToRedeem
          : 0;
    const ruleType = normalizeRuleType(item);

    if (isFreeProductRuleType(ruleType)) {
      redeemFreeProduct(item, submitBtn, modal);
      return;
    }

    const endpoint = getRedeemEndpoint(ruleType);

    if (!baseUrl || !shopifyId || !redeemRuleId || !endpoint) {
      alert(getUnsupportedRuleTypeReason(item) || t("redeem.unable_login"));
      return;
    }

    if (!canAffordRedeem(cost)) {
      submitBtn.disabled = true;
      setRedeemSubmitTooltip(submitBtn, getInsufficientPointsReason(cost));
      return;
    }

    submitBtn.disabled = true;
    submitBtn.textContent = t("redeem.redeeming");

    fetch(baseUrl.replace(/\/$/, "") + endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        shopifyId: shopifyId,
        redeemRuleId: redeemRuleId,
      }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error("HTTP_" + res.status);
        return res.json();
      })
      .then(function (json) {
        if (!json || Number(json.code) !== 200) {
          throw new Error(getApiResponseMessage(json));
        }
        const data = normalizeRedeemResponseData(json);
        // Gift card: data = { code: "****xxxx", redeemId } — copy via /redeem/code
        showRedeemSuccessModal(modal, item, data, {
          forceGiftCard: isGiftCardRuleType(normalizeRuleType(item)),
        });
        invalidateBenefitCaches();
        refreshPointsAfterRedeem(data.availablePointsAfter);
      })
      .catch(function (err) {
        alertApiErrorMessage(err);
      })
      .finally(function () {
        applyModalRedeemState(item, submitBtn);
      });
  }

  function applyModalRedeemState(item, submitBtn) {
    const points = getStoreItemRedeemCost(item);
    const canAfford = canAffordRedeem(points);

    submitBtn.textContent = t("redeem.confirm");

    if (!isRedeemableItem(item)) {
      submitBtn.disabled = true;
      submitBtn.removeAttribute("data-insufficient-points");
      setRedeemSubmitTooltip(submitBtn, getUnsupportedRuleTypeReason(item));
      return;
    }

    if (isStoreItemRedeemBlocked(item)) {
      submitBtn.disabled = true;
      submitBtn.removeAttribute("data-insufficient-points");
      setRedeemSubmitTooltip(
        submitBtn,
        getStoreItemRedeemBlockedReason(item),
      );
      return;
    }

    if (!canAfford) {
      submitBtn.disabled = true;
      submitBtn.setAttribute("data-insufficient-points", "true");
      setRedeemSubmitTooltip(submitBtn, getInsufficientPointsReason(points));
      return;
    }

    submitBtn.disabled = false;
    submitBtn.removeAttribute("data-insufficient-points");
    setRedeemSubmitTooltip(submitBtn, "");
  }

  // Redeem Modal logic — Confirm (8:4899) then Success (8:4939)
  function openRedeemModal(item) {
    if (!isLoggedIn()) {
      redirectToLogin();
      return;
    }
    if (isStoreItemRedeemBlocked(item)) {
      const reason = getStoreItemRedeemBlockedReason(item);
      if (reason) alert(reason);
      return;
    }

    const modal = getRedeemModalEl();
    if (!modal) return;

    const visual = modal.querySelector("[data-redeem-confirm-visual]");
    const rulesEl = modal.querySelector("[data-redeem-rules]");
    const submitBtn = modal.querySelector("[data-redeem-submit]");
    const shopBtn = modal.querySelector("[data-redeem-shop-now]");
    const snap = getStoreItemSnap(item);
    const points =
      snap.costToRedeem != null
        ? snap.costToRedeem
        : item.costToRedeem != null
          ? item.costToRedeem
          : 0;

    if (visual) visual.innerHTML = buildRedeemConfirmVisualHtml(item);
    if (rulesEl) {
      const lines = getBenefitsRedeemRulesLines(item);
      rulesEl.innerHTML = lines.length
        ? lines
            .map(function (line) {
              return "<p>" + escapeHtml(line) + "</p>";
            })
            .join("")
        : "<p>—</p>";
    }
    if (shopBtn) {
      const shopUrl =
        (modal.getAttribute("data-shop-url") || "").trim() ||
        (window.Shopify &&
          window.Shopify.routes &&
          window.Shopify.routes.root) ||
        "/";
      shopBtn.href = shopUrl;
      shopBtn.setAttribute("target", "_blank");
      shopBtn.setAttribute("rel", "noopener noreferrer");
      // Stay on success modal until user closes; Shop Now opens in a new tab
      shopBtn.onclick = null;
    }

    setRedeemModalPane(modal, "confirm");

    function showModal() {
      applyModalRedeemState(item, submitBtn);
      modal.hidden = false;
      document.documentElement.style.overflow = "hidden";
    }

    ensurePointsLoaded().then(showModal).catch(showModal);

    if (!getBenefitsRedeemRulesLines(item).length) {
      ensurePointStoreItemsLoaded().then(function () {
        if (modal.hidden || !rulesEl) return;
        const lines = getBenefitsRedeemRulesLines(item);
        if (!lines.length) return;
        rulesEl.innerHTML = lines
          .map(function (line) {
            return "<p>" + escapeHtml(line) + "</p>";
          })
          .join("");
      });
    }

    modal.querySelectorAll("[data-redeem-close]").forEach(function (btn) {
      btn.onclick = function () {
        closeRedeemModal(modal);
      };
    });

    const overlay = modal.querySelector(".points-redeem-modal__overlay");
    if (overlay) {
      overlay.onclick = function () {
        closeRedeemModal(modal);
      };
    }

    submitBtn.onclick = function () {
      if (!isLoggedIn()) {
        redirectToLogin();
        return;
      }
      if (!isRedeemableItem(item)) {
        if (isFreeProductRuleType(normalizeRuleType(item))) {
          reportFreeProductBlockedByApi(item);
          return;
        }
        const reason = getUnsupportedRuleTypeReason(item);
        if (reason) alert(reason);
        return;
      }
      if (isStoreItemRedeemBlocked(item)) {
        alert(getStoreItemRedeemBlockedReason(item));
        return;
      }
      if (
        submitBtn.disabled ||
        submitBtn.hasAttribute("data-insufficient-points")
      )
        return;
      if (!canAffordRedeem(points)) {
        submitBtn.disabled = true;
        submitBtn.setAttribute("data-insufficient-points", "true");
        setRedeemSubmitTooltip(submitBtn, getInsufficientPointsReason(points));
        return;
      }
      redeemReward(item, submitBtn, modal);
    };
  }

  (function initRedeemModalEsc() {
    const modal = getRedeemModalEl();
    if (!modal) return;
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && !modal.hidden) closeRedeemModal(modal);
    });
  })();

  // TEMP: force-open redeem success modal for style tweaking — set false when done
  (function previewRedeemSuccessModal() {
    const PREVIEW = false;
    if (!PREVIEW) return;
    const modal = getRedeemModalEl();
    if (!modal) return;
    const sampleItem = {
      id: 0,
      ruleType: "Order Discount",
      customerFacingLabel: "$20 off order",
      ruleName: "$20 off order",
      discountAmount: 20,
      discountType: "fixed",
      minPurchaseAmount: 100,
      costToRedeem: 1500,
      description: "Preview sample reward",
    };
    showRedeemSuccessModal(modal, sampleItem, {
      discountCode: "CDSEW25WJG5MFYW7ZBJ4",
    });
    modal.querySelectorAll("[data-redeem-close]").forEach(function (btn) {
      btn.onclick = function () {
        closeRedeemModal(modal);
      };
    });
    const overlay = modal.querySelector(".points-redeem-modal__overlay");
    if (overlay) {
      overlay.onclick = function () {
        closeRedeemModal(modal);
      };
    }
  })();

  window.PointsCenterRedeemModal = {
    open: openRedeemModal,
    ensurePoints: ensurePointsLoaded,
  };

  // ── Init ───────────────────────────────────────────────────────────
  if (typeof window.initPointsCenterSupportForm === "function") {
    window.initPointsCenterSupportForm(root);
  }
  restorePointsCenterView();
  maybeFocusProfileBirthdayFromMembership();
  rememberProfileBirthdayIso(getProfileBirthdayIsoFromDom());
})();
